/**
 * Server-side mirror of the IX3 Designer's authoring invariants. The client
 * schema (parseIX3DataStrict) is structurally permissive, so these pure guards
 * reject the well-shaped-but-unauthorable states the panel can't produce
 * ("nothing more, nothing less" — Best Practices #8).
 *
 * Each guard cites the Designer source it mirrors, takes client tuples, and
 * returns the first violation as a string (null when clean). Existence checks
 * need host state and live in the responder (host/ix3.ts), not here.
 */

import {
  IX3_WF_EXTENSION_KEYS,
  COMPONENT_TIMELINE_ROLES,
  HOVER_TIMELINE_ROLES,
  MOUSE_MOVE_TIMELINE_ROLES,
  TIMELINE_ROLE_NAMES,
  TargetScope,
  DEFAULT_DURATION_TRIGGER_MODE,
  DURATION_TRIGGER_MODES,
  DURATION_TRIGGER_SECONDS_RANGE,
  minimumDurationSeconds,
  isComponentInstanceTriggerKey,
  isMouseMoveIntervalRole,
  type ClickTriggerConfig,
  type DurationTriggerMode,
  type HoverTriggerConfig,
  type MouseMoveTriggerConfig,
  type VariableAction,
  type RiveAddedProperty,
  type AnimateRivePropertyType,
} from '@packages/systems/ix3-wf-plugins/shared';
import {
  COMPONENT_MANIFEST,
  componentPartKeys,
  componentRoles,
  type ComponentManifestEntry,
  type ComponentPart,
} from '@packages/systems/ix3-wf-plugins/components';
import {instPathKey, readInstPath} from '@packages/systems/core-constants';
import {
  PERCENT_CANVAS_DURATION_S,
  RELATIONSHIP_TYPES,
  STANDARD_TRIGGER_ALLOWED_CONTROLS,
  TimelineControlType,
} from '@packages/systems/ix3/core';
import type {Interaction} from './interaction';
import {hasResolvableScrollUnit} from './interaction/scrollUnits';
import {MAX_CANVAS_DURATION_S} from './timeline';
import type {Timeline, TimelineAction} from './timeline';
import type {StandardPlaybackControl} from './interaction/control';
import type {ConditionalOutcome} from './interaction/condition';
import type {FilterContext, TargetTuple} from './target';
import {createIx3ValueBudget, secondsOrMsSchema} from './shared';
import {
  STYLE_ACTION_PROPERTIES,
  TRANSFORM_ACTION_PROPERTIES,
} from './actionPropertyAllowlist';
import {
  isRandomValue,
  isAdditiveValue,
  isRandomArrayValue,
} from './timeline/animation';
import {
  describeCustomPropertyRejection,
  customPropertySupportsMode,
} from './customProperties';

import {
  analyzeTweenValueSlots,
  inferTweenType,
  type TweenValueSlots,
} from './tweenSlots';

export {
  analyzeTweenValueSlots,
  inferTweenType,
  type TweenValueSlots,
} from './tweenSlots';

type Triggers = Interaction['triggers'];

/**
 * Triggers the Designer marks `exclusivity: 'standalone'` — they can't coexist
 * with any other trigger (useIX3Registry.tsx `getAvailableTriggers`).
 */
export const STANDALONE_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.SCROLL,
  IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB,
  IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS,
  IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION,
  IX3_WF_EXTENSION_KEYS.MOUSE_MOVE,
  IX3_WF_EXTENSION_KEYS.NAVBAR,
  IX3_WF_EXTENSION_KEYS.DROPDOWN,
]);

/**
 * Controls for which the Designer clears the trigger's `jump`
 * (IX3PlaybackSettings.tsx `jumpShouldDisable`). The panel tests the *base*
 * control, so the flip-ease variants follow whatever they fold onto:
 * `togglePlayReverseFlipEase` is in, `reverseFlipEase` is out.
 */
export const JUMP_CLEARING_CONTROLS: ReadonlySet<StandardPlaybackControl> =
  new Set([
    'none',
    'restart',
    'resume',
    'togglePlayReverse',
    'togglePlayReverseFlipEase',
  ]);

/**
 * Controls for which the Designer clears the trigger's `speed`
 * (IX3PlaybackSettings.tsx `speedShouldDisable`). Not mergeable with
 * JUMP_CLEARING_CONTROLS: `pause`/`stop` clear speed but keep jump, and
 * `restart`/`resume` do the reverse.
 */
export const SPEED_CLEARING_CONTROLS: ReadonlySet<StandardPlaybackControl> =
  new Set(['pause', 'stop', 'none']);

/**
 * Control types whose editor renders playback settings at all (IX3TriggerEditor
 * renders them for LOAD and STANDARD, null otherwise). Where the dropdown is
 * hidden the panel stops clearing `jump`/`speed` and a saved trigger can keep a
 * control the user can no longer see, so the playback guards skip those.
 */
const PLAYBACK_EDITABLE_CONTROL_TYPES: ReadonlySet<string> = new Set([
  'standard',
  'load',
]);

/**
 * Standard-routed triggers whose playback panel is hidden because the runtime
 * routes by timeline role rather than a single trigger control
 * (IX3TriggerPlaybackControls returns null for these; speed/jump/control
 * are never surfaced or cleared by the panel). Drift-tested against
 * `isMultiTimeline: true` + `controlType: standard` registrations in
 * designerInvariantsDrift.test.ts.
 */
export const ROLE_ROUTED_NO_PLAYBACK_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.NAVBAR,
  IX3_WF_EXTENSION_KEYS.DROPDOWN,
  // Every entry, authorable or not: this says how a saved trigger routes, and
  // the runtime routes it the same way either way.
  ...COMPONENT_MANIFEST.filter(({trigger}) => trigger.emitsRolesAlways).map(
    ({trigger}) => trigger.key
  ),
]);

/**
 * Whether this trigger picks its timeline from a `timeline-role` callback it
 * EMITS at runtime, rather than from the static `assignedGroupId` /
 * `assignedTimelineRole` fields. Two shapes qualify:
 *
 *  - a trigger whose roles are fixed by its schema (Navbar/Dropdown, which
 *    always emit the component state as the role) — that is exactly
 *    {@link ROLE_ROUTED_NO_PLAYBACK_KEYS}, already drift-tested against the
 *    `isMultiTimeline: true` registrations;
 *  - HOVER flipped into role mode per instance (`pluginConfig.multiTimeline`).
 *    The key is required: `pluginConfig` is open JSON, so a DE/imported Click
 *    can legally carry the field, but only `buildHover` reads it and emits
 *    per-role callbacks — anywhere else it is inert.
 *
 * This matters wherever code reasons about which timelines a trigger reaches:
 * `StandardTriggerStrategy` resolves the emitted callback role FIRST and only
 * falls back to the static fields, so for these triggers a co-existing
 * `assignedGroupId` is an artifact the runtime never consults. The published
 * runtime keeps its own copy of this rule (`sharedTimelineAnalysis`, added in
 * #121904) because it must not import this package's Zod surface into its size
 * budget; a drift test there pins the two together.
 */
export function triggerRoutesByCallbackRole(
  key: string,
  config: {pluginConfig?: unknown} | undefined
): boolean {
  return (
    ROLE_ROUTED_NO_PLAYBACK_KEYS.has(key) ||
    (key === IX3_WF_EXTENSION_KEYS.HOVER &&
      (config?.pluginConfig as {multiTimeline?: unknown} | undefined)
        ?.multiTimeline === true)
  );
}

/**
 * Whether this trigger picks its timeline from a CONTINUOUS channel role it
 * resolves at runtime rather than from the static assignment fields. Mouse Move
 * is the schema-fixed case: `ContinuousChannelManager.registerChannel` resolves
 * each channel through `getTimelineIdsForRole`, matching `triggerMetadata.role`
 * exactly, and its axis/interval roles are its contract — not a mode it can
 * leave the way a role-mode Hover can. So on a timeline carrying BOTH a user
 * `groupId` and one of these roles (mixed/imported data), the role is load-
 * bearing: clear it and the channel can no longer resolve the timeline, and it
 * never plays. The parallel of {@link triggerRoutesByCallbackRole} for the
 * continuous strategy; both feed the collapse's preserve-role decision.
 */
export function triggerRoutesByContinuousRole(
  key: string,
  config: {controlType?: string} | undefined
): boolean {
  return (
    config?.controlType === TimelineControlType.CONTINUOUS &&
    timelineRolesForTriggerKey(key) !== undefined
  );
}

/**
 * Whether a callback-routed trigger (see {@link triggerRoutesByCallbackRole})
 * can actually emit `role`. Routing by callback does not mean every role is
 * reachable: role-mode HOVER narrowed by `pluginConfig.eventMode` binds only
 * one of the two mouse events (`buildHover` — `bindEnter`/`bindLeave`), so the
 * opposite role's timeline can never run. Callers that report "nothing drives
 * this group" must consult this, or a `eventMode: 'enter'` Hover silently
 * claims the hover-out group it will never fire.
 *
 * Navbar and Dropdown narrow the same way through a different field. Their
 * shared runtime handler drops every state but the configured one
 * (`buildComponentTrigger`: `if (eventType && state !== eventType) return;`)
 * before emitting, so a trigger persisted with `pluginConfig.event: 'open'`
 * emits ONLY `open`. Without this branch the Designer offers the `close`
 * timeline as a conditional target, validation accepts the pin, and the
 * driven-state UI reports a group nothing can reach as driven.
 *
 * The key's own role CONTRACT narrows first, before any event filter. A
 * callback-routed trigger emits only the roles its plugin builds callbacks for
 * — `buildHover` emits `mouseEnter`/`mouseLeave` and nothing else — so a Hover
 * sitting beside an imported/mixed `close` row can never reach it, whatever
 * `eventMode` says. Reporting that foreign row as emitted lets the condition
 * editor offer a target whose pin intersects every real callback to an empty
 * set, and lets the orphan-repair UI protect a row nothing drives. Only narrow
 * when the contract is KNOWN, exactly as `triggerDrivesGroup`'s CONTINUOUS
 * branch does: an unrecognized KEY (a plugin whose roles this package doesn't
 * enumerate) still reads as emitting, since reporting those as undriven would
 * surface a warning about a shape we can't adjudicate.
 *
 * Past the contract, only the event filter narrows, mirroring the component
 * handler: only the two literal directions (`open`, `close`) count as a filter,
 * and any other `event` (`"opened"`, `true`, `7`, a falsy value) is unfiltered,
 * because the handler degrades an unknown value to "no filter" and emits both
 * roles. Reading it as a filter here instead would report both component groups
 * as undriven for a trigger that executes, and `flipEase` would then omit a
 * control that fires.
 */
export function triggerEmitsCallbackRole(
  key: string,
  config: {pluginConfig?: unknown} | undefined,
  role: string
): boolean {
  if (ROLE_ROUTED_NO_PLAYBACK_KEYS.has(key)) {
    if (timelineRolesForTriggerKey(key)?.has(role) === false) return false;
    const event = (config?.pluginConfig as {event?: unknown} | undefined)
      ?.event;
    return event === TIMELINE_ROLE_NAMES.OPEN ||
      event === TIMELINE_ROLE_NAMES.CLOSE
      ? role === event
      : true;
  }
  if (key !== IX3_WF_EXTENSION_KEYS.HOVER) return true;
  if (timelineRolesForTriggerKey(key)?.has(role) === false) return false;
  const eventMode = (config?.pluginConfig as {eventMode?: unknown} | undefined)
    ?.eventMode;
  if (eventMode === 'enter')
    return role !== HOVER_TIMELINE_ROLES.MOUSE_LEAVE.role;
  if (eventMode === 'leave')
    return role !== HOVER_TIMELINE_ROLES.MOUSE_ENTER.role;
  return true;
}

/** Whether the panel shows this trigger's Control dropdown. Absent means standard. */
function hasEditablePlayback(
  key: string,
  config: Triggers[number][1]
): boolean {
  return (
    PLAYBACK_EDITABLE_CONTROL_TYPES.has(config.controlType ?? 'standard') &&
    !ROLE_ROUTED_NO_PLAYBACK_KEYS.has(key)
  );
}

/**
 * Per-trigger overrides for the Control dropdown. The Designer registration and
 * the invariant below read this one copy so they can't drift. Not in
 * `ix3-wf-plugins/shared` — the published runtime imports that barrel and would
 * carry this authoring-only data in its size budget.
 */

/** Page load only ever plays or does nothing; there is no timeline to resume. */
export const PAGE_LOAD_ALLOWED_CONTROLS: readonly StandardPlaybackControl[] = [
  'play',
  'none',
];

/**
 * Exit intent and Duration fire once and never again, so the same set Page load
 * gets: reverse, resume, pause, stop and the toggles all describe a second
 * activation that never comes — "toggle play/reverse" would play and then never
 * reverse.
 */
export const ONE_SHOT_ALLOWED_CONTROLS: readonly StandardPlaybackControl[] = [
  'play',
  'none',
];

/** The triggers `ONE_SHOT_ALLOWED_CONTROLS` applies to. */
export const ONE_SHOT_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.EXIT_INTENT,
  IX3_WF_EXTENSION_KEYS.DURATION,
]);

/**
 * Whether the "whichever comes first" option applies to `triggers`: more than
 * one one-shot, as the runtime races every one of them, two Durations included.
 * The Designer shows the checkbox only then, and drops `firstWins` once a
 * delete leaves fewer, so both read it from here.
 */
export function isOneShotFirstWinsShown(
  triggers: ReadonlyArray<readonly [string, ...unknown[]]>
): boolean {
  let oneShots = 0;
  for (const [key] of triggers) {
    if (ONE_SHOT_TRIGGER_KEYS.has(key) && ++oneShots > 1) return true;
  }
  return false;
}

/** Click always does something, so "none" is not offered. */
export const CLICK_ALLOWED_CONTROLS: readonly StandardPlaybackControl[] =
  STANDARD_TRIGGER_ALLOWED_CONTROLS.filter((control) => control !== 'none');

/**
 * A click restricted to an occurrence (first, odd, even, a count) fires at most
 * once per state, so the toggle controls have nothing to alternate between.
 */
export const CLICK_NO_TOGGLE_ALLOWED_CONTROLS: readonly StandardPlaybackControl[] =
  STANDARD_TRIGGER_ALLOWED_CONTROLS.filter(
    (control) =>
      control !== 'togglePlayReverse' &&
      control !== 'togglePlayReverseFlipEase' &&
      control !== 'none'
  );

export function getClickAllowedPlaybackControls(
  pluginConfig: ClickTriggerConfig | undefined
): readonly StandardPlaybackControl[] {
  const click = pluginConfig?.click;
  if (click && click !== 'each') {
    return CLICK_NO_TOGGLE_ALLOWED_CONTROLS;
  }
  return CLICK_ALLOWED_CONTROLS;
}

export function getPageLoadAllowedPlaybackControls(): readonly StandardPlaybackControl[] {
  return PAGE_LOAD_ALLOWED_CONTROLS;
}

export function getOneShotAllowedPlaybackControls(): readonly StandardPlaybackControl[] {
  return ONE_SHOT_ALLOWED_CONTROLS;
}

/**
 * The controls a trigger offers, or `undefined` when it doesn't narrow the
 * standard set. Mirrors the Designer's dropdown composition,
 * `schemaAllowedControls ?? STANDARD_TRIGGER_ALLOWED_CONTROLS`
 * (IX3TriggerEditor.tsx).
 */
export function getAllowedPlaybackControlsForTrigger(
  triggerKey: string,
  pluginConfig: unknown
): readonly StandardPlaybackControl[] | undefined {
  switch (triggerKey) {
    case IX3_WF_EXTENSION_KEYS.LOAD:
      return getPageLoadAllowedPlaybackControls();
    case IX3_WF_EXTENSION_KEYS.CLICK:
      return getClickAllowedPlaybackControls(
        pluginConfig as ClickTriggerConfig | undefined
      );
    case IX3_WF_EXTENSION_KEYS.EXIT_INTENT:
    case IX3_WF_EXTENSION_KEYS.DURATION:
      return ONE_SHOT_ALLOWED_CONTROLS;
    default:
      return undefined;
  }
}

/**
 * Controls the conditions editor offers for an outcome — no flip-ease variants
 * (conditions-editor/utils/validation.ts `ConditionalOutcomeSchema`).
 */
export const CONDITIONAL_OUTCOME_CONTROLS: ReadonlySet<StandardPlaybackControl> =
  new Set([
    'play',
    'pause',
    'resume',
    'reverse',
    'restart',
    'stop',
    'none',
    'togglePlayReverse',
  ]);

/** Targets the Designer exposes only for actions (targets/index.ts `shouldShow`). */
export const ACTION_ONLY_TARGET_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY,
  IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY_PARENT,
  IX3_WF_EXTENSION_KEYS.ANY_ELEMENT,
]);

/** Triggers whose registry control type is intrinsically continuous. */
export const CONTINUOUS_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.MOUSE_MOVE,
]);

export type TriggerControlType = 'standard' | 'scroll' | 'load' | 'continuous';

/**
 * Trigger keys whose registry controlType differs from the runtime "standard"
 * default (mirrors the Designer trigger registrations). A missing controlType on
 * these binds the wrong strategy and never fires, so the responder stamps it
 * (stampTriggerControlType) and mismatches are rejected. Canonical copy —
 * code-views normalization imports this rather than keeping its own.
 */
export const NON_STANDARD_TRIGGER_CONTROL_TYPES: ReadonlyMap<
  string,
  TriggerControlType
> = new Map<string, TriggerControlType>([
  [IX3_WF_EXTENSION_KEYS.LOAD, 'load'],
  [IX3_WF_EXTENSION_KEYS.SCROLL, 'scroll'],
  [IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB, 'scroll'],
  [IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS, 'scroll'],
  [IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION, 'scroll'],
  [IX3_WF_EXTENSION_KEYS.MOUSE_MOVE, 'continuous'],
]);

/** The canonical controlType for a trigger extension key. */
export function expectedControlType(triggerKey: string): TriggerControlType {
  return NON_STANDARD_TRIGGER_CONTROL_TYPES.get(triggerKey) ?? 'standard';
}

/**
 * Standalone-trigger exclusivity: a standalone trigger must be the only
 * trigger on the interaction.
 */
export function findStandaloneTriggerError(triggers: Triggers): string | null {
  if (triggers.length <= 1) return null;
  const standalone = triggers.find(([key]) => STANDALONE_TRIGGER_KEYS.has(key));
  return standalone
    ? `Trigger "${standalone[0]}" cannot be combined with other triggers; ` +
        `the Designer only allows it as the sole trigger on an interaction.`
    : null;
}

/** At most one Page load (wf:load) trigger per interaction. */
export function findMultipleLoadTriggerError(
  triggers: Triggers
): string | null {
  const loadCount = triggers.filter(
    ([key]) => key === IX3_WF_EXTENSION_KEYS.LOAD
  ).length;
  return loadCount > 1
    ? `An interaction may have at most one "${IX3_WF_EXTENSION_KEYS.LOAD}" ` +
        `(Page load) trigger.`
    : null;
}

/**
 * Page load is the one trigger the panel gives no configuration surface at all:
 * it registers `component: null`, no `defaultTarget`, and `defaultValue: {}`
 * (triggers/index.ts), and it is excluded from the element-targetable set the
 * create path picks a target for (useInteractionHandlers.ts). So a native load
 * trigger is always a bare 2-tuple with an empty `pluginConfig`.
 *
 * Both extras are load-bearing rather than cosmetic. A target renders a Target
 * editor row on a trigger that has no element semantics. DE writes stamp
 * `wf:load` with `controlType: 'load'`, so IX3Engine routes through
 * LoadTriggerStrategy which does NOT call the plugin load handler that reads
 * `pluginConfig.triggerPoint` — making `triggerPoint` dormant on the normal
 * Page load path, not a lever for different publish timing. Rejecting still
 * makes sense because the panel cannot show, edit, or clear it.
 */
export function findLoadTriggerPurityError(triggers: Triggers): string | null {
  for (const [key, config, target] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.LOAD) continue;
    if (target !== undefined) {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.LOAD}" must not carry a target; ` +
        `page load fires once for the page and the Designer never attaches ` +
        `an element to it. Target elements on the timeline actions instead.`
      );
    }
    const pluginConfigKeys = Object.keys(config.pluginConfig ?? {});
    if (pluginConfigKeys.length > 0) {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.LOAD}" must not set ` +
        `pluginConfig (got ${pluginConfigKeys.join(', ')}); the Designer ` +
        `offers no page load settings and writes an empty config.`
      );
    }
  }
  return null;
}

/**
 * A conditional outcome needs a `targetTimelineId` iff its control isn't "none",
 * and its control must be one the Designer offers. Mirrors
 * conditions-editor/utils/validation.ts. (Target membership: responder.)
 *
 * `allowsRoleRelativeTarget`: the trigger routes by role callbacks, so an
 * ABSENT target means "the timeline of whichever role fired" rather than an
 * incomplete outcome. Pinning one role's timeline makes every other role's
 * event a no-op, so the Designer deliberately clears the target when an
 * interaction gains a second role group (#121905) — this exception is what lets
 * the DE/MCP API create and update the same state the Designer authors.
 */
function findOutcomeError(
  outcome: ConditionalOutcome | undefined,
  label: string,
  allowsRoleRelativeTarget = false
): string | null {
  if (!outcome) return null;
  const {control, targetTimelineId} = outcome;
  if (!CONDITIONAL_OUTCOME_CONTROLS.has(control)) {
    return (
      `Conditional outcome ${label} control "${control}" is not ` +
      `available in the Designer.`
    );
  }
  if (control === 'none') {
    if (targetTimelineId !== undefined) {
      return (
        `Conditional outcome ${label} must not set a targetTimelineId ` +
        `when control is "none".`
      );
    }
  } else if (!targetTimelineId && !allowsRoleRelativeTarget) {
    return (
      `Conditional outcome ${label} requires a targetTimelineId unless ` +
      `control is "none".`
    );
  }
  // The outcome editor runs the same jump/speed clearing as the trigger's
  // playback settings, so the same combinations are unrepresentable here.
  if (JUMP_CLEARING_CONTROLS.has(control) && outcome.jump !== undefined) {
    return (
      `Conditional outcome ${label} must not set a jump when control is ` +
      `"${control}".`
    );
  }
  if (SPEED_CLEARING_CONTROLS.has(control) && outcome.speed !== undefined) {
    return (
      `Conditional outcome ${label} must not set a speed when control is ` +
      `"${control}".`
    );
  }
  return null;
}

export function findConditionalOutcomeError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    const conditionalLogic = config.conditionalLogic;
    if (!conditionalLogic) continue;
    // Role routing, in any of the three forms the runtime honors: an explicit
    // assignment, a plugin opting in per instance (`pluginConfig.multiTimeline`,
    // Hover enter/leave), or a trigger whose roles are fixed by its SCHEMA
    // (Navbar/Dropdown open/close, which keep an empty pluginConfig). The
    // schema-fixed case has to be listed by key — this package can't read the
    // Designer registry — and `ROLE_ROUTED_NO_PLAYBACK_KEYS` is already that
    // list, drift-tested against the `isMultiTimeline` + `controlType: standard`
    // registrations. Omitting it rejects trigger payloads the Designer's own
    // editor authors once it stops auto-pinning a target (#121905), so DE/MCP
    // clients could not create or update conditional Navbar/Dropdown
    // interactions the UI produces.
    //
    // Deliberately decided from the trigger alone, not from the interaction's
    // timelines: this invariant only receives triggers, and being permissive
    // here costs nothing — an absent target on a trigger with no role timelines
    // resolves to every timeline the trigger drives, which is the pre-existing
    // meaning.
    // Two ways a targetless outcome can mean "the matching role", and they have
    // different preconditions:
    //  - the trigger EMITS the role (Hover in role mode, Navbar/Dropdown). The
    //    callback precedes the static fields at runtime, so the exemption holds
    //    whatever `assignedGroupId` says.
    //  - the trigger is statically pinned to one role (`assignedTimelineRole`).
    //    Static routing checks the GROUP first (resolveAssignedTimelineIds), so
    //    the role is only consulted when no group is assigned. With a concrete
    //    group the outcome runs that group instead of the advertised role, and
    //    with an explicit `null` it runs nothing — neither is what a targetless
    //    role-relative outcome promises, so require an absent assignment.
    const allowsRoleRelativeTarget =
      triggerRoutesByCallbackRole(key, config) ||
      (Boolean(config.assignedTimelineRole) && !routesByAssignedGroup(config));
    const error =
      findOutcomeError(
        conditionalLogic.ifTrue,
        'ifTrue',
        allowsRoleRelativeTarget
      ) ??
      findOutcomeError(
        conditionalLogic.ifFalse,
        'ifFalse',
        allowsRoleRelativeTarget
      );
    if (error) return error;
  }
  return null;
}

/**
 * Every condition must carry a value — z.unknown() admits an absent one the
 * Designer never emits, plus a non-empty list. Mirrors the tools' `requireValue`.
 */
export function findConditionValuePresenceError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    const conditions = config.conditionalLogic?.conditions;
    if (!conditions) continue;
    if (conditions.length === 0) {
      return `Trigger "${key}" conditionalLogic requires at least one condition.`;
    }
    for (const condition of conditions) {
      if (condition[1] === undefined) {
        return `Trigger "${key}" condition value is required.`;
      }
    }
  }
  return null;
}

/**
 * Continuous triggers (mouse-move) can't carry conditional logic — the Designer
 * hides the conditions section (IX3TriggerEditor.tsx `!isContinuous`).
 */
export function findContinuousConditionsError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    if (
      (config.controlType === 'continuous' ||
        CONTINUOUS_TRIGGER_KEYS.has(key)) &&
      config.conditionalLogic !== undefined
    ) {
      return `Continuous trigger "${key}" cannot use conditional logic.`;
    }
  }
  return null;
}

/**
 * The panel clears `jump` for restart/resume/togglePlayReverse/none
 * (IX3PlaybackSettings.tsx); a jump alongside those is unrepresentable.
 */
export function findTriggerJumpError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    if (
      hasEditablePlayback(key, config) &&
      config.control !== undefined &&
      JUMP_CLEARING_CONTROLS.has(config.control) &&
      config.jump !== undefined
    ) {
      return (
        `Trigger "${key}" must not set a jump when control is ` +
        `"${config.control}".`
      );
    }
  }
  return null;
}

/**
 * Persist bounds for trigger playback speed. Matches IX3PlaybackSettings
 * `SPEED_SLIDER_RANGES` / `SPEED_UNIT_RANGES` (0–10). Step 0.25 is not
 * pinned: the unit input clamps min/max and can persist other in-range
 * floats. Not in `ix3-wf-plugins/shared` — the published runtime imports
 * that barrel.
 */
export const TRIGGER_SPEED_RANGE = {min: 0, max: 10} as const;

/**
 * The panel clears `speed` for pause/stop/none (IX3PlaybackSettings.tsx); a
 * speed alongside those is unrepresentable. Mirrors findTriggerJumpError.
 *
 * A present speed must also sit in the playback slider's 0–10 persist
 * bound. Clearing-control wins when both fail so pause+speed:250 still
 * reports the control conflict, not the range.
 */
export function findTriggerSpeedError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    if (
      hasEditablePlayback(key, config) &&
      config.control !== undefined &&
      SPEED_CLEARING_CONTROLS.has(config.control) &&
      config.speed !== undefined
    ) {
      return (
        `Trigger "${key}" must not set a speed when control is ` +
        `"${config.control}".`
      );
    }
    if (config.speed === undefined) continue;
    // Range is a playback-slider persist bound. Triggers without that
    // slider stay on the existing uneditable-playback path.
    if (!hasEditablePlayback(key, config)) continue;
    const speed = config.speed as unknown;
    if (
      typeof speed !== 'number' ||
      !Number.isFinite(speed) ||
      speed < TRIGGER_SPEED_RANGE.min ||
      speed > TRIGGER_SPEED_RANGE.max
    ) {
      return (
        `Trigger "${key}" speed must be a finite number between ` +
        `${TRIGGER_SPEED_RANGE.min} and ${TRIGGER_SPEED_RANGE.max}; the ` +
        `Designer's playback slider only persists that range.`
      );
    }
  }
  return null;
}

/** Legacy hover fields, only written by HoverTriggerInputLegacy. */
const LEGACY_HOVER_FIELDS = ['type', 'hover', 'custom'] as const;

/** How many legacy mouseover hovers the stored interaction already carried. */
const countStoredMouseoverHovers = (triggers: Triggers | undefined): number =>
  (triggers ?? []).filter(
    ([key, config]) =>
      key === IX3_WF_EXTENSION_KEYS.HOVER &&
      (config.pluginConfig as HoverTriggerConfig | undefined)?.type ===
        'mouseover'
  ).length;

/**
 * Hover has two mutually exclusive editors and `pluginConfig.multiTimeline` is
 * the discriminator between them. `HoverTriggerInput` branches on
 * `typeof config?.multiTimeline === 'boolean'` — deliberately strict, so that a
 * JSON-safe non-boolean reads as legacy the same way the runtime handler does.
 *
 * The new editor only ever writes `eventMode`; the legacy editor only ever
 * writes `type` / `hover` / `custom` and never sets `multiTimeline`. So the two
 * field sets cannot coexist in authored data, and `eventMode` cannot appear
 * without the boolean discriminator.
 *
 * Mixing them is not inert. With a boolean `multiTimeline` the panel shows the
 * new editor, which has no control for the legacy fields but *preserves* them
 * through its `...rest` spread — so a stale `hover: 'first'` would sit in the
 * data permanently, invisible and unremovable, while the runtime's new hover
 * branch ignores it.
 *
 * Note this guards the *combination* only. `multiTimeline: true` itself stays
 * accepted while the feature flag ramps (roadmap item 10).
 *
 * `baselineTriggers` (update path only): the legacy editor preserves a stored
 * `type` through its Trigger/count writes, so pre-existing `mouseover` hovers
 * are grandfathered one-for-one among `wf:hover` triggers. A non-hover cannot
 * inherit that allowance — `mouseover` is only read on hover.
 */
export function findHoverConfigModelError(
  triggers: Triggers,
  baselineTriggers?: Triggers
): string | null {
  // Consumed per stored mouseover hover, so an update can carry the legacy
  // ones forward without also appending a new one the panel cannot author.
  // Hover-only: a click→hover swap cannot spend this allowance.
  let mouseoverAllowance = countStoredMouseoverHovers(baselineTriggers);

  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.HOVER) continue;
    const pluginConfig = config.pluginConfig as HoverTriggerConfig | undefined;
    if (!pluginConfig) continue;

    const isNewModel = typeof pluginConfig.multiTimeline === 'boolean';
    if (isNewModel) {
      const legacyField = LEGACY_HOVER_FIELDS.find(
        (field) => pluginConfig[field] !== undefined
      );
      if (legacyField) {
        return (
          `Trigger "${key}" pluginConfig sets both "multiTimeline" and the ` +
          `legacy field "${legacyField}"; the Designer shows one hover ` +
          `editor or the other, and the multi-timeline editor cannot show ` +
          `or clear "${legacyField}".`
        );
      }
    } else if (pluginConfig.eventMode !== undefined) {
      return (
        `Trigger "${key}" pluginConfig sets "eventMode" without a boolean ` +
        `"multiTimeline"; the Designer only offers the event type selector ` +
        `on the multi-timeline hover editor.`
      );
    }

    // EVENT_TYPE_OPTIONS in HoverTriggerInput is mouseenter/mouseleave only,
    // so `mouseover` cannot be newly authored. It is not rejected outright:
    // HoverTriggerInputLegacy carries the stored `type` back through its
    // Trigger/count handlers, so the panel does re-persist a legacy mouseover
    // on an unrelated edit. Rejecting that would make the API stricter than
    // the editor and strand those interactions.
    if (pluginConfig.type === 'mouseover') {
      if (mouseoverAllowance === 0) {
        return (
          `Trigger "${key}" pluginConfig.type "mouseover" is not offered by ` +
          `the Designer; the Type dropdown only writes mouseenter or mouseleave.`
        );
      }
      mouseoverAllowance -= 1;
    }
  }
  return null;
}

/**
 * Roles the panel may legitimately assign on THIS interaction: the union of
 * roles registered by whichever role-owning trigger the interaction carries.
 *
 * Only reachable for a role-owning trigger with `exclusivity: 'normal'` — the
 * standalone ones (mouse-move, navbar, dropdown) can never have a second
 * trigger to assign. The case this exists for is `wf:popover`, whose Open/Close
 * timelines are role-tagged but which deliberately allows extra triggers (Click,
 * Hover, Exit intent, Duration) alongside it; each of those has to
 * say which of the two it drives, or `sharedTimelineAnalysis` would treat it as
 * "play every timeline" and fire Open and Close together.
 *
 * A multi-timeline Hover (`pluginConfig.multiTimeline === true`) is
 * `exclusivity: 'normal'` too, so its `mouseEnter`/`mouseLeave` roles join this
 * set as well. That is wider than what the panel writes — it routes Hover
 * siblings via `assignedGroupId` — but it is a coherent shape the runtime
 * honors, so it is accepted rather than specially excluded.
 *
 * The owner itself is excluded where this set is consumed: it supplies the
 * roles rather than being assigned one. See `findAssignedTimelineRoleError`.
 */
function assignableTimelineRoles(triggers: Triggers): ReadonlySet<string> {
  const assignable = new Set<string>();
  for (const [key, config] of triggers) {
    const roles = getMultiTimelineRoles(key, config);
    if (roles) for (const role of roles) assignable.add(role);
  }
  return assignable;
}

/**
 * The panel writes `assignedGroupId` (`ix3TriggerTimelineAssigned`) to route a
 * trigger to a user-managed timeline group, and `assignedTimelineRole` to route
 * it to one of a role-owning trigger's registered roles (the "Assign" dropdown
 * on a role-based interaction such as a Popover's Open/Close). Any other role
 * string is still rejected, as is a role on an interaction that has no
 * role-owning trigger to supply it.
 *
 * `baselineTriggers` (update path only): IX3TriggerEditor rebuilds the config
 * by spreading the stored one (`{...triggerConfig, conditionalLogic}`,
 * `{...value, ...updates}`), so a conditions or playback edit re-persists a
 * stored role. Dropping it to satisfy this guard would change runtime routing,
 * so stored roles are grandfathered one-for-one on the same trigger key; a
 * click→hover (or any key) swap cannot reuse the role, and new ones still reject.
 */
export function findAssignedTimelineRoleError(
  triggers: Triggers,
  baselineTriggers?: Triggers
): string | null {
  const assignable = assignableTimelineRoles(triggers);
  // Roles the baseline's own owners registered. A stored role only needs the
  // allowance when it is not assignable now; if it WAS assignable in the
  // baseline, this update removed the trigger registering it, which leaves a
  // role-routed trigger with no owner and the Designer's Assign select gone.
  // The Designer itself clears those siblings when it deletes the last owner
  // (`store.ts`), so only a bypassing write gets here.
  const baselineAssignable = assignableTimelineRoles(baselineTriggers ?? []);
  // Keyed by trigger type + role so a stored click+open cannot license a
  // hover+open (or any other key) just because the role string matches.
  const allowanceByTriggerRole = new Map<string, number>();
  for (const [triggerKey, config] of baselineTriggers ?? []) {
    const role = config.assignedTimelineRole;
    if (role === undefined) continue;
    const slot = assignedRoleAllowanceKey(triggerKey, role);
    allowanceByTriggerRole.set(
      slot,
      (allowanceByTriggerRole.get(slot) ?? 0) + 1
    );
  }
  // Stored triggers that already carry both routing axes, grandfathered the
  // same way: before the group reducer cleared the role, the Designer could
  // write the pair itself, so saves made before that fix may still carry it.
  // Keyed by the stored group too: only the unchanged pair is licensed, since
  // `resolveAssignedTimelineIds` reads the group first and a changed group
  // under the same role is a new contradictory write.
  const bothAxesAllowance = new Map<string, number>();
  for (const [triggerKey, config] of baselineTriggers ?? []) {
    const role = config.assignedTimelineRole;
    if (role === undefined || config.assignedGroupId === undefined) continue;
    const slot = assignedBothAxesAllowanceKey(
      triggerKey,
      role,
      config.assignedGroupId
    );
    bothAxesAllowance.set(slot, (bothAxesAllowance.get(slot) ?? 0) + 1);
  }

  for (const [key, config] of triggers) {
    const role = config.assignedTimelineRole;
    if (role === undefined) continue;
    // Only STANDARD triggers route by role. Page load, Scroll and Mouse move
    // ignore the field — `LoadTriggerStrategy` plays every timeline the
    // interaction lists — and the Designer withholds Assign for them, so a
    // stored role would run Open and Close together while the row shows no
    // route at all.
    if (!isStandardRoutedTriggerConfig(key, config)) {
      // A role already stored on this same trigger key is grandfathered
      // one-for-one, exactly as on any other trigger: IX3TriggerEditor
      // rebuilds the config by spreading the stored one, so an unrelated edit
      // (or adding a Popover to an older interaction) re-persists it, and the
      // Designer has no control to clear it. Only a NEW role is rejected.
      const storedLeft =
        allowanceByTriggerRole.get(assignedRoleAllowanceKey(key, role)) ?? 0;
      if (storedLeft > 0) {
        allowanceByTriggerRole.set(
          assignedRoleAllowanceKey(key, role),
          storedLeft - 1
        );
        continue;
      }
      // Two ways to fail the helper: a non-standard key, or a standard key
      // whose `controlType` overrides it. Name whichever one applies.
      const keyControlType = expectedControlType(key);
      const controlType =
        keyControlType === 'standard'
          ? (config.controlType ?? keyControlType)
          : keyControlType;
      return (
        `Trigger "${key}" must not set assignedTimelineRole; it is a ` +
        `${controlType} trigger, which plays every timeline the ` +
        'interaction lists and never routes by role.'
      );
    }
    // Both routing axes at once. `resolveAssignedTimelineIds` reads
    // `assignedGroupId` first, so the role never routes anything: a `null`
    // group parks the trigger (it plays nothing) while the row's Assign still
    // shows the role, and a group id plays that group instead.
    if (config.assignedGroupId !== undefined) {
      const slot = assignedBothAxesAllowanceKey(
        key,
        role,
        config.assignedGroupId
      );
      const left = bothAxesAllowance.get(slot) ?? 0;
      if (left === 0) {
        return (
          `Trigger "${key}" sets both assignedTimelineRole "${String(role)}" ` +
          `and assignedGroupId ${JSON.stringify(config.assignedGroupId)}. ` +
          'assignedGroupId is read first, so the role never routes. Remove ' +
          'assignedGroupId to route by role, or remove assignedTimelineRole ' +
          'to route by group (null leaves the trigger unassigned).'
        );
      }
      bothAxesAllowance.set(slot, left - 1);
    }
    // The role OWNER supplies the roles; it does not consume one. Letting it
    // match the union would accept a shape the Designer never writes — a
    // `wf:popover` trigger assigned to Open — which `resolveAssignedTimelineIds`
    // would honour, pinning the popover's own state-change trigger to one of
    // its two timelines so it could open but never close.
    const ownsRoles = getMultiTimelineRoles(key, config) !== undefined;
    if (!ownsRoles && assignable.has(role)) continue;
    const slot = assignedRoleAllowanceKey(key, role);
    const remaining =
      ownsRoles || !baselineAssignable.has(role)
        ? (allowanceByTriggerRole.get(slot) ?? 0)
        : 0;
    if (remaining === 0) {
      if (ownsRoles) {
        return (
          `Trigger "${key}" must not set assignedTimelineRole; it registers ` +
          `the roles (${[...assignable].join(', ')}) that the interaction's ` +
          'other triggers are assigned to, and pinning it to one would leave ' +
          'the other role unreachable from this trigger.'
        );
      }
      return assignable.size
        ? `Trigger "${key}" assignedTimelineRole "${role}" is not a role this ` +
            `interaction's trigger registers. Expected one of: ` +
            `${[...assignable].join(', ')}.`
        : `Trigger "${key}" must not set assignedTimelineRole; this ` +
            `interaction has no role-based trigger, and the Designer writes ` +
            `assignedGroupId to route a trigger to a timeline group.`;
    }
    allowanceByTriggerRole.set(slot, remaining - 1);
  }
  return null;
}

const assignedRoleAllowanceKey = (triggerKey: string, role: unknown): string =>
  `${triggerKey}\0${String(role)}`;

const assignedBothAxesAllowanceKey = (
  triggerKey: string,
  role: unknown,
  groupId: string | null
): string =>
  `${assignedRoleAllowanceKey(triggerKey, role)}\0${JSON.stringify(groupId)}`;

// Events that never fire on the backdrop; see `findFiringBackdropRoleError`.
const NON_FIRING_BACKDROP_TRIGGER_KEYS: ReadonlySet<string> = new Set<string>([
  IX3_WF_EXTENSION_KEYS.FOCUS,
  IX3_WF_EXTENSION_KEYS.BLUR,
  IX3_WF_EXTENSION_KEYS.CHANGE,
]);

/**
 * The rule for a `firing` backdrop part (Popover's).
 *
 * A trigger targeting `wf:popover-backdrop` is a declaration of intent to
 * dismiss: the Designer's own target picker overwrites the trigger's
 * `assignedTimelineRole` to Close the moment Backdrop is picked
 * (`assignsTriggerRole` on the `popover-backdrop` target schema), on purpose,
 * without the author having to also find Assign. A stored role other than
 * Close on that pairing can therefore only come from a write that bypassed
 * the picker — DE, MCP, a file edit — and it is semantically broken either
 * way: the backdrop is only clickable while the popover IS open, so routing
 * its click to Open would just replay the Open timeline on an
 * already-open popover instead of dismissing it.
 *
 * No route at all (neither field set) is rejected too: `StandardTriggerStrategy`
 * reads that as play-all, so the click runs Open and Close together without
 * telling the Close role observer, and the popover stays open behind a
 * backdrop that keeps intercepting clicks. The parked shape
 * (`assignedGroupId: null`, plays nothing) stays allowed — it is what the
 * Designer writes for "unassigned" and when the role owner is deleted.
 *
 * A group id is rejected as well, with or without a role beside it. The
 * Designer's Assign on a popover interaction writes roles only, and
 * `resolveAssignedTimelineIds` reads the group first, so the click runs that
 * group's timelines (or nothing) without the Close role observer ever being
 * told — the same stuck backdrop.
 *
 * So is a routed Page load or Scroll trigger. Neither binds a click listener to
 * its target, yet a trigger that plays makes the backdrop take pointer events,
 * so it would intercept page clicks that do nothing.
 *
 * And a routed Focus, Blur or Change trigger: the backdrop is an `aria-hidden`
 * div with no tab stop and no form control inside, so those events never fire
 * on it, while the trigger still makes it swallow clicks. Parked is left alone
 * — it plays nothing, and the runtime does not make the backdrop take pointer
 * events for it.
 */
function findFiringBackdropRoleError(
  [key, config]: Triggers[number],
  partKey: string,
  noun: string,
  role: string
): string | null {
  const controlType = expectedControlType(key);
  if (
    (controlType === 'load' || controlType === 'scroll') &&
    config.assignedGroupId !== null
  ) {
    return (
      `Trigger "${key}" targets "${partKey}" ` +
      `but is a ${controlType} trigger, which binds no click listener to ` +
      'its target — the backdrop would take pointer events and intercept ' +
      'page clicks that do nothing. Use a Click trigger to dismiss from ' +
      'the backdrop.'
    );
  }
  if (
    NON_FIRING_BACKDROP_TRIGGER_KEYS.has(key) &&
    config.assignedGroupId !== null
  ) {
    return (
      `Trigger "${key}" targets "${partKey}" ` +
      'but listens for an event the backdrop never raises (it takes no ' +
      'focus and holds no form control) — the backdrop would take pointer ' +
      `events and intercept clicks without closing the ${noun}. Use a ` +
      'Click trigger to dismiss from the backdrop, or set assignedGroupId ' +
      'to null to leave it unassigned.'
    );
  }
  if (config.assignedGroupId != null) {
    return (
      `Trigger "${key}" targets "${partKey}" ` +
      `but sets assignedGroupId "${String(config.assignedGroupId)}" — a ` +
      `group route never tells the ${noun} to close, so the backdrop stays ` +
      'up. Remove assignedGroupId and set assignedTimelineRole to ' +
      `"${role}", or set assignedGroupId to null to ` +
      'leave it unassigned.'
    );
  }
  const assigned = config.assignedTimelineRole;
  if (assigned !== undefined && assigned !== role) {
    return (
      `Trigger "${key}" targets "${partKey}" ` +
      `but assignedTimelineRole is "${assigned}" — the backdrop is only ` +
      `clickable while the ${noun} is open, so a trigger pointed at it ` +
      `must route to "${role}" (the Designer's own ` +
      'target picker assigns this automatically when Backdrop is picked).'
    );
  }
  if (assigned === undefined && config.assignedGroupId === undefined) {
    return (
      `Trigger "${key}" targets "${partKey}" ` +
      'but sets neither assignedTimelineRole nor assignedGroupId, so it ' +
      'plays every timeline (Open and Close together) and never dismisses ' +
      `the ${noun}. Set assignedTimelineRole to "${role}", ` +
      'or assignedGroupId to null to leave it unassigned.'
    );
  }
  return null;
}

/**
 * The owner and part of every manifest part that triggers can fire from, by
 * part key.
 */
const COMPONENT_TRIGGER_PARTS: ReadonlyMap<
  string,
  {entry: ComponentManifestEntry; part: ComponentPart}
> = new Map(
  COMPONENT_MANIFEST.flatMap((entry) =>
    entry.parts
      .filter((part) => part.triggers)
      .map((part) => [part.key, {entry, part}] as const)
  )
);

/**
 * A trigger targeting a manifest component's backdrop dismisses the component
 * from it. A modal's backdrop is the dialog's own `::backdrop`: it always takes
 * clicks while the modal is open, and the runtime only tells a click on it
 * apart from one inside the dialog for the Click trigger. So a `click-only`
 * part takes Click alone, and that Click must route to the part's role or be
 * left unassigned (`assignedGroupId: null`). A `firing` part (Popover's) has
 * the rule in {@link findFiringBackdropRoleError}.
 */
export function findComponentBackdropRoleError(
  triggers: Triggers
): string | null {
  for (const trigger of triggers) {
    const [key, config, target] = trigger;
    const owned = Array.isArray(target)
      ? COMPONENT_TRIGGER_PARTS.get(target[0])
      : undefined;
    if (!owned?.part.triggers) continue;
    const partKey = owned.part.key;
    const noun = owned.entry.noun.singular;
    const {allowed, role} = owned.part.triggers;
    if (allowed === 'firing') {
      const error = findFiringBackdropRoleError(trigger, partKey, noun, role);
      if (error) return error;
      continue;
    }
    if (key !== IX3_WF_EXTENSION_KEYS.CLICK) {
      return (
        `Trigger "${key}" targets "${partKey}" ` +
        `but only "${IX3_WF_EXTENSION_KEYS.CLICK}" can fire from a ${noun}'s ` +
        'backdrop. Use a Click trigger to dismiss from the backdrop.'
      );
    }
    if (config.assignedGroupId === null) continue;
    if (config.assignedGroupId !== undefined) {
      return (
        `Trigger "${key}" targets "${partKey}" ` +
        `but sets assignedGroupId "${String(config.assignedGroupId)}" — a ` +
        `group route never tells the ${noun} to close. Remove ` +
        `assignedGroupId and set assignedTimelineRole to "${role}", or ` +
        'set assignedGroupId to null to leave it unassigned.'
      );
    }
    if (config.assignedTimelineRole !== role) {
      return (
        `Trigger "${key}" targets "${partKey}" ` +
        `but assignedTimelineRole is ${
          config.assignedTimelineRole === undefined
            ? 'unset'
            : `"${String(config.assignedTimelineRole)}"`
        } — the backdrop only exists while the ${noun} is open, so a ` +
        `trigger on it must route to "${role}".`
      );
    }
  }
  return null;
}

/**
 * `assignedInstances` must name instances the interaction's own component
 * triggers name, and only a NON-component trigger may carry it.
 *
 * The Designer's reducer already filters to the named set, so this guards the
 * paths that bypass it — Designer Extensions, MCP, imports. A path naming an
 * instance the interaction doesn't drive routes to nothing and can't be cleared
 * from the field; a component trigger IS one instance, so a narrowing on it
 * would imply a runtime choice that doesn't exist.
 *
 * Driven by `COMPONENT_INSTANCE_TRIGGER_KEYS` rather than naming `wf:popover`,
 * so a Modal or Slider trigger inherits the invariant by joining that list.
 */
export function findAssignedInstancesError(triggers: Triggers): string | null {
  const named = new Set<string>();
  const componentTriggerKeys: string[] = [];
  for (const [key, , target] of triggers) {
    if (!isComponentInstanceTriggerKey(key)) continue;
    componentTriggerKeys.push(key);
    // Only a `wf:inst` target names an instance; any other key whose value
    // happens to parse as a `[string, string]` pair (a two-class `wf:class`)
    // does not, matching `getComponentInstancePaths` and the runtime readers.
    if (target?.[0] !== IX3_WF_EXTENSION_KEYS.INST) continue;
    const path = readInstPath(target[1]);
    if (path) named.add(instPathKey(path[0], path[1]));
  }
  const expected = [...new Set(componentTriggerKeys)]
    .map((key) => `"${key}"`)
    .join(' / ');

  for (const [key, config] of triggers) {
    const assigned = config.assignedInstances;
    if (assigned === undefined) continue;
    // Any value, `[]` included: the Designer never writes the field onto a
    // component trigger (its reducer returns early), so the inert `[]` the
    // sibling case below tolerates cannot arrive on this row.
    if (isComponentInstanceTriggerKey(key)) {
      return (
        `Trigger "${key}" must not set assignedInstances; it already names one ` +
        `component instance through its own target.`
      );
    }
    // `[]` is "drives none" — inert, and exactly what the reducer writes while
    // the interaction's component trigger has no target yet (see
    // `narrowAssignedInstances`, which deliberately does not collapse an
    // explicit "none" into "drives every instance"). Rejecting it here made a
    // DE/MCP round-trip of that state fail the whole interaction.
    if (assigned.length === 0) continue;
    if (named.size === 0) {
      return (
        `Trigger "${key}" must not set assignedInstances; this interaction has ` +
        `no component trigger naming an instance.`
      );
    }
    for (const path of assigned) {
      if (!named.has(instPathKey(String(path[0]), String(path[1])))) {
        return (
          `Trigger "${key}" assignedInstances names [${String(path[0])}, ` +
          `${String(path[1])}], which no ${expected} trigger on this ` +
          `interaction drives.`
        );
      }
    }
  }
  return null;
}

/**
 * Prefix the Designer/store reserve inside the OPEN `pluginConfig` record for
 * their own transition state — the add-group flow's parked single-group-only
 * settings (`__wfClearedOnMultiGroup`, written by the store from #121905).
 * `pluginConfig` is an open JSON bag by design (plugins read their own keys and
 * ignore the rest), so a prefix documented only in a comment is not actually
 * reserved: a DE could schema-validly author `__wfClearedOnMultiGroup` on a
 * Hover, and the group round trip would then overwrite that value on the way in
 * and, on the way out, delete the key and hoist its nested entries into the
 * top-level config the plugin reads. {@link findReservedPluginConfigKeyError}
 * is what makes the reservation real at the DE/API boundary.
 */
export const RESERVED_PLUGIN_CONFIG_KEY_PREFIX = '__wf';

/**
 * Rejects `__wf`-prefixed `pluginConfig` keys (see
 * {@link RESERVED_PLUGIN_CONFIG_KEY_PREFIX}) so internal group-transition state
 * cannot be authored — or silently claimed — by a DE/API client.
 *
 * Not in `pluginConfigSchema` alongside the `__proto__`/`constructor` rejection:
 * that schema also validates data on the way OUT, and interactions the Designer
 * has already put in two-group mode carry a real stash. A hard schema rejection
 * would make those unreadable rather than unauthorable.
 *
 * `baselineTriggers` (update path only) grandfathers by COUNT per trigger key +
 * reserved key + reserved VALUE + target, not by trigger identity:
 * IX3TriggerEditor and DE clients alike rebuild a config by spreading the
 * stored one, so any edit to a stashed Hover re-persists our own key, and
 * keying on the whole trigger would fail every such read-modify-write. Keying
 * on the value (and the target, so the allowance can't be spent by relocating
 * the byte-identical key/value to a sibling trigger on the same key) is what
 * keeps that narrow allowance from becoming a write channel — the collapse
 * spreads the stash's members straight into the live `pluginConfig` the
 * plugin reads, so a client that could REPLACE the value under a
 * grandfathered name would be authoring post-collapse plugin state (a
 * different restored `eventMode`, or keys the plugin's own schema would never
 * have accepted) through a field nothing else validates. A verbatim echo
 * still lands; a rewrite does not —
 * except to the exact stash the Designer parks (see
 * {@link isDesignerMultiGroupStash}), whose only member is an `eventMode` the
 * live field already accepts, so it is exempt with or without a baseline.
 * Adding a reserved key where the baseline had none still rejects, which is the
 * case this guard exists for — again unless it is that exact stash, so a create
 * that copies a two-group Hover it read back is accepted.
 */
export function findReservedPluginConfigKeyError(
  triggers: Triggers,
  baselineTriggers?: Triggers
): string | null {
  const allowance = new Map<string, number>();
  for (const [triggerKey, config, target] of baselineTriggers ?? []) {
    for (const [key, value] of reservedPluginConfigEntries(
      config.pluginConfig
    )) {
      const slot = reservedKeyAllowanceKey(triggerKey, key, value, target);
      allowance.set(slot, (allowance.get(slot) ?? 0) + 1);
    }
  }

  for (const [triggerKey, config, target] of triggers) {
    for (const [key, value] of reservedPluginConfigEntries(
      config.pluginConfig
    )) {
      if (
        isDesignerMultiGroupStash(triggerKey, config.pluginConfig, key, value)
      )
        continue;
      const slot = reservedKeyAllowanceKey(triggerKey, key, value, target);
      const remaining = allowance.get(slot) ?? 0;
      if (remaining === 0) {
        return (
          `Trigger "${triggerKey}" must not set pluginConfig key "${key}"; ` +
          `the "${RESERVED_PLUGIN_CONFIG_KEY_PREFIX}" prefix is reserved for ` +
          `Designer-internal state.`
        );
      }
      allowance.set(slot, remaining - 1);
    }
  }
  return null;
}

/**
 * Whether a reserved entry is exactly what the group add parks on a Hover:
 * its cleared `eventMode`, on a trigger in role mode. Restoring it can only
 * write an `eventMode` the live field accepts, so it is no write channel, and
 * accepting it lets a DE create copy a two-group Hover verbatim — there is no
 * baseline on create to grandfather the stash by value.
 */
function isDesignerMultiGroupStash(
  triggerKey: string,
  pluginConfig: unknown,
  key: string,
  value: unknown
): boolean {
  if (
    triggerKey !== IX3_WF_EXTENSION_KEYS.HOVER ||
    key !== `${RESERVED_PLUGIN_CONFIG_KEY_PREFIX}ClearedOnMultiGroup` ||
    (pluginConfig as {multiTimeline?: unknown}).multiTimeline !== true ||
    typeof value !== 'object' ||
    value === null ||
    Array.isArray(value)
  ) {
    return false;
  }
  const {eventMode} = value as {eventMode?: unknown};
  return (
    Object.keys(value).length === 1 &&
    typeof eventMode === 'string' &&
    HOVER_EVENT_MODE_SET.has(eventMode)
  );
}

function reservedPluginConfigEntries(
  pluginConfig: unknown
): [string, unknown][] {
  if (typeof pluginConfig !== 'object' || pluginConfig === null) return [];
  return Object.entries(pluginConfig).filter(([key]) =>
    key.startsWith(RESERVED_PLUGIN_CONFIG_KEY_PREFIX)
  );
}

const reservedKeyAllowanceKey = (
  triggerKey: string,
  key: string,
  value: unknown,
  target: Triggers[number][2]
): string =>
  `${triggerKey}\0${key}\0${stableStringify(value)}\0${stableTriggerTargetKey(target)}`;

/**
 * Persist bounds for mouse-move smoothness (ms). Matches the Designer's
 * Smoothness number input (`unitRanges`), not the 0–100 slider. Not in
 * `ix3-wf-plugins/shared` — the published runtime imports that barrel.
 */
export const MOUSE_MOVE_SMOOTHNESS_MS_RANGE = {min: 0, max: 2000} as const;

/**
 * Persist bounds for mouse-move rest state (%). Slider and number input
 * share this range. Not in `ix3-wf-plugins/shared` for the same reason.
 */
export const MOUSE_MOVE_RESTING_STATE_PERCENT_RANGE = {
  min: 0,
  max: 100,
} as const;

function findInclusiveRangeError(
  value: unknown,
  min: number,
  max: number,
  field: string
): string | null {
  if (value === undefined) return null;
  if (
    typeof value !== 'number' ||
    !Number.isFinite(value) ||
    value < min ||
    value > max
  ) {
    return (
      `Trigger "${IX3_WF_EXTENSION_KEYS.MOUSE_MOVE}" pluginConfig.${field} ` +
      `must be a finite number between ${min} and ${max}; the Designer's ` +
      `input only persists that range.`
    );
  }
  return null;
}

/**
 * Mouse-move persist bounds match MouseMoveTriggerInput `unitRanges`:
 * smoothness 0–2000ms, restingState x/y 0–100. Absent keys are legal. The
 * smoothness slider is 0–100; the number input is the persist bound.
 */
export function findMouseMoveRangeError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.MOUSE_MOVE) continue;
    const pluginConfig = config.pluginConfig as
      | MouseMoveTriggerConfig
      | undefined;
    if (!pluginConfig) continue;

    const smoothnessError = findInclusiveRangeError(
      pluginConfig.smoothness,
      MOUSE_MOVE_SMOOTHNESS_MS_RANGE.min,
      MOUSE_MOVE_SMOOTHNESS_MS_RANGE.max,
      'smoothness'
    );
    if (smoothnessError) return smoothnessError;

    const restingState = pluginConfig.restingState;
    if (restingState == null || typeof restingState !== 'object') continue;

    const xError = findInclusiveRangeError(
      restingState.x,
      MOUSE_MOVE_RESTING_STATE_PERCENT_RANGE.min,
      MOUSE_MOVE_RESTING_STATE_PERCENT_RANGE.max,
      'restingState.x'
    );
    if (xError) return xError;

    const yError = findInclusiveRangeError(
      restingState.y,
      MOUSE_MOVE_RESTING_STATE_PERCENT_RANGE.min,
      MOUSE_MOVE_RESTING_STATE_PERCENT_RANGE.max,
      'restingState.y'
    );
    if (yError) return yError;
  }
  return null;
}

/**
 * Click and legacy-hover occurrence modes. Mirrors ClickTriggerInput OPTIONS
 * and HoverTriggerInputLegacy's Trigger dropdown — same six values.
 */
export const OCCURRENCE_COUNT_MODES = [
  'each',
  'first',
  'second',
  'odd',
  'even',
  'custom',
] as const;

/** NumberInput `min` on the click / legacy-hover custom count. */
export const CUSTOM_OCCURRENCE_COUNT_MIN = 1;

const OCCURRENCE_COUNT_MODE_SET: ReadonlySet<string> = new Set(
  OCCURRENCE_COUNT_MODES
);

const OCCURRENCE_COUNT_MODE_LIST = OCCURRENCE_COUNT_MODES.map(
  (mode) => `"${mode}"`
).join(', ');

function asPluginConfigRecord(
  pluginConfig: unknown
): Record<string, unknown> | undefined {
  if (
    pluginConfig == null ||
    typeof pluginConfig !== 'object' ||
    Array.isArray(pluginConfig)
  ) {
    return undefined;
  }
  return pluginConfig as Record<string, unknown>;
}

/**
 * Shared click / legacy-hover count rules. Absent mode is legal (create and
 * reset write no bag; the editors read `|| 'each'`). `custom` is only
 * persisted with the custom mode and must be a finite number >= 1 — the
 * NumberInput can emit fractions, so integers are not required.
 */
function findOccurrenceCountConfigError(
  triggerKey: string,
  pluginConfig: unknown,
  modeKey: 'click' | 'hover'
): string | null {
  const record = asPluginConfigRecord(pluginConfig);
  if (!record) return null;

  const mode = record[modeKey];
  const custom = record.custom;

  if (mode !== undefined) {
    if (typeof mode !== 'string' || !OCCURRENCE_COUNT_MODE_SET.has(mode)) {
      return (
        `Trigger "${triggerKey}" pluginConfig.${modeKey} must be one of ` +
        `${OCCURRENCE_COUNT_MODE_LIST}; the Designer's dropdown only writes ` +
        `those values.`
      );
    }
  }

  if (custom !== undefined) {
    if (mode !== 'custom') {
      return (
        `Trigger "${triggerKey}" pluginConfig.custom is only written when ` +
        `pluginConfig.${modeKey} is "custom"; the Designer drops custom ` +
        `when leaving that mode.`
      );
    }
    if (
      typeof custom !== 'number' ||
      !Number.isFinite(custom) ||
      custom < CUSTOM_OCCURRENCE_COUNT_MIN
    ) {
      return (
        `Trigger "${triggerKey}" pluginConfig.custom must be a finite ` +
        `number of at least ${CUSTOM_OCCURRENCE_COUNT_MIN}; the Designer's ` +
        `custom-count input only persists that domain.`
      );
    }
  } else if (mode === 'custom') {
    return (
      `Trigger "${triggerKey}" pluginConfig.custom is required when ` +
      `pluginConfig.${modeKey} is "custom"; the Designer always writes a ` +
      `count with that mode.`
    );
  }

  return null;
}

/**
 * `wf:click` pluginConfig value domain. ClickTriggerInput OPTIONS are
 * each/first/second/odd/even/custom; the custom NumberInput persists a
 * finite count >= 1. Absent pluginConfig is the create/reset default.
 * Read as `unknown`: the bag is an open JSON record.
 */
export function findClickPluginConfigError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.CLICK) continue;
    const error = findOccurrenceCountConfigError(
      key,
      config.pluginConfig,
      'click'
    );
    if (error) return error;
  }
  return null;
}

export const HOVER_EVENT_MODES = ['both', 'enter', 'leave'] as const;
const HOVER_EVENT_MODE_SET: ReadonlySet<string> = new Set(HOVER_EVENT_MODES);
export const HOVER_AUTHORABLE_TYPES = ['mouseenter', 'mouseleave'] as const;
const HOVER_AUTHORABLE_TYPE_SET: ReadonlySet<string> = new Set(
  HOVER_AUTHORABLE_TYPES
);

/**
 * `wf:hover` pluginConfig value domain. Complements
 * `findHoverConfigModelError` (model mix + mouseover grandfather): this
 * checks the values each editor can persist.
 *
 * - Legacy Type: mouseenter / mouseleave. `mouseover` is left to the model
 *   guard so a stored one stays grandfathered.
 * - Legacy Trigger: same occurrence modes as click, including custom >= 1.
 * - New-model Type: both / enter / leave. `'both'` is accepted — the panel
 *   stores absence to keep data small, but the control offers it and the
 *   runtime treats it as absent.
 */
export function findHoverPluginConfigValueError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.HOVER) continue;
    const record = asPluginConfigRecord(config.pluginConfig);
    if (!record) continue;

    const type = record.type;
    if (type !== undefined && type !== 'mouseover') {
      if (typeof type !== 'string' || !HOVER_AUTHORABLE_TYPE_SET.has(type)) {
        return (
          `Trigger "${key}" pluginConfig.type must be "mouseenter" or ` +
          `"mouseleave"; the Designer's Type dropdown only writes those ` +
          `values.`
        );
      }
    }

    const eventMode = record.eventMode;
    if (eventMode !== undefined) {
      if (
        typeof eventMode !== 'string' ||
        !HOVER_EVENT_MODE_SET.has(eventMode)
      ) {
        return (
          `Trigger "${key}" pluginConfig.eventMode must be one of "both", ` +
          `"enter", "leave"; the Designer's Type dropdown only writes those ` +
          `values.`
        );
      }
    }

    const occurrenceError = findOccurrenceCountConfigError(
      key,
      record,
      'hover'
    );
    if (occurrenceError) return occurrenceError;
  }
  return null;
}

/**
 * `wf:custom`'s Name field is the only editor that writes `eventName`, and a
 * text input can only ever produce a string. A non-string is unauthorable.
 * Before `readCustomEventName`, `CustomEventTriggerInput` seeded its state with
 * `pluginConfig?.eventName || ''`, which passed any truthy non-string straight
 * through to `.trim()` in the render body, and `getDisplayInfo` put the same
 * value in a React child slot on the collapsed trigger row.
 *
 * A blank name stays legal. The trigger registration seeded
 * `defaultValue: {eventName: ''}` from #85984 until #99791, so a year of
 * interactions store one, and it behaves exactly like the absent name the panel
 * still writes today — falsy at the runtime binding, "No event name" in warning
 * colour on the row, and cleared outright by the field's reset. Whitespace-only
 * is not that default: `validateAndCommit` treats `trim() === ''` as empty and
 * refuses to persist, so `'   '` is rejected here.
 *
 * Read as `unknown` rather than through `CustomTriggerConfig`: `pluginConfig` is
 * an open JSON bag, so the declared `eventName?: string` is a compile-time
 * promise no write path has ever enforced.
 */
export function findCustomEventNameError(triggers: Triggers): string | null {
  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.CUSTOM) continue;
    const eventName = (config.pluginConfig as {eventName?: unknown} | undefined)
      ?.eventName;
    if (eventName === undefined) continue;
    if (typeof eventName !== 'string') {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.CUSTOM}" pluginConfig.eventName ` +
        `must be a string; the Designer's Name field only writes text.`
      );
    }
    // Exact `''` is the seeded historical default. Whitespace-only is not:
    // the Name field treats trim-empty as invalid and does not persist it.
    if (eventName !== '' && eventName.trim() === '') {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.CUSTOM}" pluginConfig.eventName ` +
        `must be empty or contain a non-whitespace character; the Name ` +
        `field treats whitespace-only as empty and does not persist it.`
      );
    }
  }
  return null;
}

/**
 * Control types with no trigger-level playback at all, because the runtime
 * drives the timeline from somewhere else: scroll from ScrollTrigger's scrub /
 * toggle actions, continuous from the channel manager. IX3TriggerPlaybackControls
 * returns null for both, and neither `createBaseTriggerConfig` nor the trigger
 * registrations seed a `control` for them.
 *
 * Deliberately narrower than the inverse of PLAYBACK_EDITABLE_CONTROL_TYPES.
 * navbar / dropdown also hide the playback editor. The panel still writes
 * `control: 'play'` on them, but findGroupedTriggerControlError only engages
 * when two live timelines carry a `groupId`. Role-routed navbar/dropdown
 * timelines stamp `triggerMetadata.role` instead, so `control` is left
 * unguarded here rather than required.
 */
export const NO_PLAYBACK_CONTROL_TYPES: ReadonlySet<TriggerControlType> =
  new Set<TriggerControlType>(['scroll', 'continuous']);

const PLAYBACK_FIELDS = ['control', 'delay', 'jump', 'speed'] as const;

/**
 * Playback fields on a trigger whose panel never shows them. The existing
 * jump/speed/control guards *skip* these triggers (hasEditablePlayback) because
 * the panel also stops clearing the fields there; that leaves the fields
 * themselves unguarded, so a caller can attach a control the user cannot see,
 * edit, or remove.
 *
 * Keyed off the registry control type rather than the submitted one so the
 * result does not depend on whether the caller stamped `controlType`
 * (contradictory explicit values are findControlTypeMismatchError's job).
 *
 * Navbar / dropdown hide the same editor but still write `control: 'play'`.
 * Those keys reject delay / jump / speed only; `control` is left unguarded
 * until a role-routed pin lands (findGroupedTriggerControlError does not
 * cover these keys).
 */
export function findUneditablePlaybackFieldError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    const fields: readonly (typeof PLAYBACK_FIELDS)[number][] =
      NO_PLAYBACK_CONTROL_TYPES.has(expectedControlType(key))
        ? PLAYBACK_FIELDS
        : ROLE_ROUTED_NO_PLAYBACK_KEYS.has(key)
          ? (['delay', 'jump', 'speed'] as const)
          : [];
    for (const field of fields) {
      if (config[field] !== undefined) {
        return (
          `Trigger "${key}" must not set "${field}"; the Designer shows no ` +
          `playback settings for it, so the value could never be edited or ` +
          `cleared from the panel.`
        );
      }
    }
  }
  return null;
}

/**
 * The interaction's conditionalPlayback list (IX3ConditionalPlayback.tsx).
 *
 * Two rules, both structural in the panel rather than enforced anywhere else:
 *
 * 1. One entry per condition type. The Add menu disables an option once its
 *    type is present and fully disables at two, and update/remove are keyed by
 *    `type` — so a second entry of the same type is not just unauthorable, it
 *    would be uneditable and unremovable, with edits silently applying to both.
 * 2. No `skip-to-end` while a continuous trigger is present. The panel drops
 *    the option there (`selectHasContinuousTrigger`) because a mouse-move
 *    timeline is scrubbed by pointer position and has no "end" to skip to.
 *
 * Scroll scrub is deliberately not covered: the panel's filter checks
 * `controlType === 'continuous'` only, and still offers `skip-to-end` on
 * scroll. Matching that keeps the API from being stricter than the panel.
 *
 * This runs before `parseIX3DataStrict`, so entries are shape-checked first —
 * a `[null]` payload must return InteractionInvalid, not throw into Flux.
 *
 * `checkDuplicates` defaults true; the update path sets it false on a
 * trigger-only update so pre-existing duplicate rows stay editable.
 */
export function findConditionalPlaybackError(
  triggers: Triggers,
  conditionalPlayback: Interaction['conditionalPlayback'] | unknown,
  options?: {checkDuplicates?: boolean}
): string | null {
  if (conditionalPlayback == null) return null;
  if (!Array.isArray(conditionalPlayback)) {
    return 'conditionalPlayback must be an array of condition objects.';
  }
  if (conditionalPlayback.length === 0) return null;

  const checkDuplicates = options?.checkDuplicates !== false;
  const seenTypes = new Set<string>();
  for (const item of conditionalPlayback) {
    if (item == null || typeof item !== 'object') {
      return (
        'conditionalPlayback entries must be objects with a "type" and ' +
        '"behavior".'
      );
    }
    const type = (item as {type?: unknown}).type;
    if (typeof type !== 'string') {
      return 'conditionalPlayback entries must include a string "type".';
    }
    if (checkDuplicates) {
      if (seenTypes.has(type)) {
        return (
          `conditionalPlayback has more than one "${type}" entry; the ` +
          `Designer allows one row per condition type and keys edits by type, ` +
          `so a duplicate could not be edited or removed.`
        );
      }
      seenTypes.add(type);
    }
  }

  // Retained triggers on a conditionalPlayback-only update may be malformed
  // (multiplayer warns but keeps bad ops). Shape-check before reading
  // controlType so callers get InteractionInvalid instead of a TypeError.
  if (!Array.isArray(triggers)) {
    return 'triggers must be an array of trigger tuples.';
  }
  let hasContinuousTrigger = false;
  for (const trigger of triggers as unknown[]) {
    if (!Array.isArray(trigger) || trigger.length < 2) {
      return (
        'triggers entries must be [extensionKey, config] or ' +
        '[extensionKey, config, target] tuples.'
      );
    }
    const [key, config] = trigger as [unknown, unknown];
    if (typeof key !== 'string') {
      return 'triggers entries must start with a string extension key.';
    }
    if (config == null || typeof config !== 'object' || Array.isArray(config)) {
      return (
        `Trigger "${key}" is missing a config object; the Designer always ` +
        `writes one.`
      );
    }
    const typedConfig = config as {controlType?: unknown};
    if (
      typedConfig.controlType === 'continuous' ||
      CONTINUOUS_TRIGGER_KEYS.has(key)
    ) {
      hasContinuousTrigger = true;
    }
  }
  if (!hasContinuousTrigger) return null;

  for (const item of conditionalPlayback) {
    if (item == null || typeof item !== 'object') continue;
    const typed = item as {type?: unknown; behavior?: unknown};
    if (typed.behavior === 'skip-to-end') {
      const label = typeof typed.type === 'string' ? typed.type : 'condition';
      return (
        `conditionalPlayback "${label}" cannot use behavior ` +
        `"skip-to-end" on an interaction with a continuous trigger; the ` +
        `Designer offers only "dont-animate" there.`
      );
    }
  }
  return null;
}

/**
 * A trigger's control must be one its Control dropdown offers — resolved from
 * the same source the Designer registers, so the two can't drift.
 */
export function findTriggerControlAllowedError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    if (!hasEditablePlayback(key, config)) continue;
    // Exit intent and Duration have to name their control: the engine runs an
    // omitted one as `restart`, which the Designer never offers a one-shot and
    // which re-runs a timeline a sibling trigger already advanced. The Designer
    // always writes `play`, so only API- or code-authored data can omit it.
    if (config.control === undefined) {
      if (ONE_SHOT_TRIGGER_KEYS.has(key)) {
        return (
          `Trigger "${key}" must set control; it offers ` +
          `${ONE_SHOT_ALLOWED_CONTROLS.join(', ')}.`
        );
      }
      continue;
    }
    const allowed =
      getAllowedPlaybackControlsForTrigger(key, config.pluginConfig) ??
      STANDARD_TRIGGER_ALLOWED_CONTROLS;
    if (!allowed.includes(config.control)) {
      return (
        `Trigger "${key}" control "${config.control}" is not available in ` +
        `the Designer; it offers ${allowed.join(', ')}.`
      );
    }
  }
  return null;
}

/**
 * Stamp the canonical controlType on non-standard triggers that omit it, like
 * the Designer's create path and code-views' normalizeInteractionEntry. Explicit
 * values are left for findControlTypeMismatchError; standard triggers are fine
 * without one. Applied in the create/update responders before persistence.
 */
export function stampTriggerControlType(triggers: Triggers): Triggers {
  return triggers.map((trigger) => {
    const [key, config, target] = trigger;
    const expected = NON_STANDARD_TRIGGER_CONTROL_TYPES.get(key);
    if (expected === undefined || config.controlType !== undefined) {
      return trigger;
    }
    const nextConfig = {...config, controlType: expected};
    return (
      target === undefined ? [key, nextConfig] : [key, nextConfig, target]
    ) as Triggers[number];
  }) as Triggers;
}

export const SCROLL_TOGGLE_DEFAULTS = {
  enter: 'play',
  leave: 'none',
  enterBack: 'none',
  leaveBack: 'none',
} as const;

const SCROLL_TOGGLE_KEYS = Object.keys(SCROLL_TOGGLE_DEFAULTS) as Array<
  keyof typeof SCROLL_TOGGLE_DEFAULTS
>;

/**
 * The only toggle set Scroll direction ever carries (see `createBaseTriggerConfig`).
 * The runtime wires its playback itself and ignores these four, so any other
 * value would persist with no effect.
 */
export const SCROLL_DIRECTION_TOGGLES = {
  enter: 'play',
  leave: 'none',
  enterBack: 'none',
  leaveBack: 'reverse',
} as const;

/**
 * Omitting enter is coerced to 'none' at runtime (buildGSAPConfig), which
 * installs no onEnter without scrub. This stamp writes the panel's real
 * default ('play') at persist time so stored config matches what the panel
 * would have authored. 'none' is still a dropdown value and is left alone
 * when present. A Scroll direction trigger also gets its group model and
 * `clamp` stamped (see `stampScrollDirectionGroupModel`). Scroll scrub and
 * Scroll actions also get the `split` marker the Designer writes on them.
 */
export function stampScrollToggleActions(triggers: Triggers): Triggers {
  return triggers.map((trigger) => {
    // Non-tuples stay as-is so schema validation can name the bad entry
    // instead of this helper throwing on destructure.
    if (!Array.isArray(trigger)) {
      return trigger;
    }
    const [key, config] = trigger;
    if (key === IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION) {
      return stampScrollDirectionGroupModel(trigger);
    }
    // Scroll actions is the split variant that plays toggle actions without
    // scrub, so it needs the stamp as much as the legacy trigger does. Scroll
    // scrub scrubs and Scroll direction seeds its own full toggle set.
    // Scroll scrub is still stamped below, but only with the split marker.
    const isSplitScrub = key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB;
    if (
      key !== IX3_WF_EXTENSION_KEYS.SCROLL &&
      key !== IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS &&
      !isSplitScrub
    ) {
      return trigger;
    }
    // Malformed tuples (no config) stay as-is so schema validation reports them.
    if (config == null || typeof config !== 'object') {
      return trigger;
    }
    const scroll = config.scrollTriggerConfig;
    // Explicit null survives object-to-tuple and must stay missing so
    // findScrollTriggerError can reject it. Spreading null would invent {}.
    if (scroll == null || typeof scroll !== 'object' || Array.isArray(scroll)) {
      return trigger;
    }
    let changed = false;
    const nextScroll = {...scroll};
    // The split editors write `split: true` on every config they save, and the
    // runtime picks the split path from it, so a Scroll scrub / Scroll actions
    // trigger authored without it would run on the legacy path until someone
    // edits it in the panel. Legacy `wf:scroll` stays unmarked.
    if (
      key !== IX3_WF_EXTENSION_KEYS.SCROLL &&
      nextScroll.split === undefined
    ) {
      nextScroll.split = true;
      changed = true;
    }
    if (!isSplitScrub) {
      for (const toggle of SCROLL_TOGGLE_KEYS) {
        if (nextScroll[toggle] === undefined) {
          nextScroll[toggle] = SCROLL_TOGGLE_DEFAULTS[toggle];
          changed = true;
        }
      }
    }
    if (!changed) {
      return trigger;
    }
    const next = [...trigger];
    next[1] = {...config, scrollTriggerConfig: nextScroll};
    return next as Triggers[number];
  }) as Triggers;
}

/**
 * The Designer needs the boolean `pluginConfig.multiTimeline` on a Scroll
 * direction trigger before it shows the group-management affordance (creation
 * seeds `false`), so an interaction authored through DE/MCP/code-views without
 * it could not add or manage its reverse group when opened. An explicit boolean
 * is kept.
 *
 * It also fills an omitted `scrollTriggerConfig.clamp` from the trigger target,
 * the way the Designer seeds it (on for an element, off for the Page). The
 * runtime reads `clamp` by truthiness, so an omitted value on an element would
 * leave the thresholds unclamped with no Clamp control in the panel to change
 * it. An explicit value is kept; one that contradicts the target is rejected by
 * `findScrollDirectionClampError`.
 */
function stampScrollDirectionGroupModel(
  trigger: Triggers[number]
): Triggers[number] {
  const config = trigger[1];
  // Malformed tuples stay as-is so schema validation can name them.
  if (config == null || typeof config !== 'object') return trigger;
  const pluginConfig = config.pluginConfig;
  // Explicit null stays as-is too: the schema rejects it, and spreading it
  // would coerce it into a valid {multiTimeline: false}.
  if (
    pluginConfig !== undefined &&
    (pluginConfig === null ||
      typeof pluginConfig !== 'object' ||
      Array.isArray(pluginConfig))
  ) {
    return trigger;
  }
  let nextConfig = config;
  if (typeof pluginConfig?.multiTimeline !== 'boolean') {
    nextConfig = {
      ...nextConfig,
      pluginConfig: {...pluginConfig, multiTimeline: false},
    };
  }
  const scroll = config.scrollTriggerConfig;
  const target = trigger[2];
  // A missing or malformed scroll config or target is left for validation to
  // report; only a plain object with a tuple target can be stamped.
  if (
    scroll != null &&
    typeof scroll === 'object' &&
    !Array.isArray(scroll) &&
    scroll.clamp === undefined &&
    Array.isArray(target)
  ) {
    nextConfig = {
      ...nextConfig,
      scrollTriggerConfig: {
        ...scroll,
        clamp: target[0] !== IX3_WF_EXTENSION_KEYS.BODY,
      },
    };
  }
  if (nextConfig === config) return trigger;
  const next = [...trigger];
  next[1] = nextConfig;
  return next as Triggers[number];
}

/**
 * Stamp `tt` from property value shapes when the caller omits it, matching
 * WFS `normalizeTimelineEntry` / Designer tween-type selection. Explicit `tt`
 * is left for findActionTweenSlotError. Applied in the create/update
 * responders before persistence.
 *
 * `baselineTimelines` (update path only): an unchanged stored action that
 * still omits `tt` keeps that omit. Runtime defaults omitted `tt` to To (`0`),
 * so a name-only echo of legacy Display (`display: 'none'` + From opacity)
 * must not be rewritten to From (`1`) — that tween drops the scalar display.
 */
export function stampTweenTypeFromSlots(
  timelines: Timeline[],
  baselineTimelines?: readonly Timeline[]
): Timeline[] {
  return timelines.map((timeline) => {
    const actions = timeline.actions.map((action) => {
      if (action.tt !== undefined) {
        return action;
      }
      if (isUnchangedStoredAction(action, timeline, baselineTimelines)) {
        return action;
      }
      const inferred = inferTweenType(action.properties);
      if (inferred === undefined) {
        return action;
      }
      return {...action, tt: inferred};
    });
    return {...timeline, actions};
  });
}

/**
 * A trigger's controlType must match its registry control type. Missing values
 * are stamped (stampTriggerControlType), so this only catches an explicit
 * contradiction (e.g. wf:load with "standard") that binds the wrong strategy.
 */
export function findControlTypeMismatchError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    const expected = expectedControlType(key);
    if (config.controlType !== undefined && config.controlType !== expected) {
      return (
        `Trigger "${key}" requires controlType "${expected}" but got ` +
        `"${config.controlType}"; a mismatched controlType binds the wrong ` +
        `runtime strategy and the trigger never fires.`
      );
    }
  }
  return null;
}

/**
 * Scroll settings the Interactions panel has no writer for.
 *
 * IX3ScrollTriggerConfig.tsx authors `start`/`end`, `scrub`, `showMarkers`,
 * `clamp`, the `enter`/`leave`/`enterBack`/`leaveBack` toggle actions, a
 * boolean `pin` (a checkbox), and `endTrigger` (the "Separate end target"
 * checkbox + element picker). It has no control for `scroller` at all, and the
 * only `horizontal` it can emit is the `false` in its `defaultScrollTriggerConfig`
 * fallback — nothing turns it on. Pin is likewise a checkbox: the schema's
 * `boolean | TargetTuple` union is runtime capability, not an authoring surface,
 * and a tuple pin renders as a checked box the user cannot uncheck back to a
 * tuple.
 *
 * The runtime honors all of these (AnimationCoordinator's
 * `resolveScrollTriggerTargets` and its GSAP `ScrollTrigger` config), so an
 * API-authored `scroller`/`horizontal` value silently changes how a published
 * page scrolls while the panel shows nothing that explains it.
 */
export function findScrollTriggerConfigError(
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  key: string,
  where = `Trigger "${key}" scrollTriggerConfig`
): string | null {
  return (
    findScrollPanelOnlyOptionError(scroll, where) ??
    findSplitOnlyScrollOptionError(scroll, where) ??
    findReservedScrollDirectionError(scroll, where) ??
    findScrollKeyScrubModeError(key, scroll, where) ??
    findScrollKeyOptionMismatchError(key, scroll, where)
  );
}

/**
 * The `scroller`, `horizontal`, and tuple `pin` part of
 * `findScrollTriggerConfigError`: the options the Interactions panel has no
 * control for, whatever the trigger key. Exported so the code-view lane can
 * compose it with the key-specific checks explicitly instead of calling the
 * composite and de-duplicating by message text.
 */
export function findScrollPanelOnlyOptionError(
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  where: string
): string | null {
  if (scroll.scroller !== undefined) {
    return (
      `${where}.scroller is not offered by the Designer; scroll ` +
      `interactions always track the page scroller.`
    );
  }
  if (scroll.horizontal === true) {
    return (
      `${where}.horizontal is not offered by the Designer; the panel only ` +
      `authors vertical scroll ranges.`
    );
  }
  if (scroll.pin !== undefined && typeof scroll.pin !== 'boolean') {
    // The Pin control pins the trigger element, or one element picked with
    // "Other element", which it writes as a `wf:inst` tuple. Any other target
    // shape renders as an empty "Other element" the user can't read back.
    if (
      !Array.isArray(scroll.pin) ||
      scroll.pin[0] !== IX3_WF_EXTENSION_KEYS.INST
    ) {
      return (
        `${where}.pin must be a boolean or a "${IX3_WF_EXTENSION_KEYS.INST}" ` +
        `target; the Designer's Pin control pins the trigger element or one ` +
        `element picked with "Other element".`
      );
    }
    const pinTargetError =
      findTargetValuePresenceError(scroll.pin, `${where}.pin`) ??
      findScrollInstPickerValueError(scroll.pin, `${where}.pin`);
    if (pinTargetError) return pinTargetError;
  }
  if (scroll.endTrigger !== undefined) {
    // "Separate end target" is an element picker that writes a `wf:inst`
    // tuple. Any other target type would render as an empty picker while the
    // runtime still ends the range on it.
    if (
      !Array.isArray(scroll.endTrigger) ||
      scroll.endTrigger[0] !== IX3_WF_EXTENSION_KEYS.INST
    ) {
      return (
        `${where}.endTrigger must be a "${IX3_WF_EXTENSION_KEYS.INST}" ` +
        `target; the Designer's "Separate end target" picker selects one ` +
        `element.`
      );
    }
    const endTargetError = findScrollInstPickerValueError(
      scroll.endTrigger,
      `${where}.endTrigger`
    );
    if (endTargetError) return endTargetError;
  }
  return null;
}

/**
 * The `wf:inst` value an element picker ("Other element", "Separate end target")
 * writes is a `[componentId, elementId]` pair, or `''` before an element is
 * chosen. Any other value resolves to no element at runtime while the picker
 * renders empty, so it can't be read back or cleared meaningfully. The picker
 * also shows only the element ID, so an active third-slot Filter would change
 * which element the runtime resolves without the panel showing it.
 */
function findScrollInstPickerValueError(
  target: TargetTuple,
  where: string
): string | null {
  const filterContext = target[2];
  if (
    filterContext !== undefined &&
    filterContext.relationship !== RELATIONSHIP_TYPES.NONE
  ) {
    return (
      `${where}: the element picker does not support Filter; the target ` +
      `must not carry an active filterContext.`
    );
  }
  if (target[1] === '') return null;
  const shapeError = validateTargetValue(target[0], target[1]);
  return shapeError ? `${where}: ${shapeError}` : null;
}

/**
 * The runtime picks scrubbed versus toggle-action playback from the `scrub`
 * value alone (any non-null `scrub` attaches the timeline as a scrubbed
 * animation), so a split key whose `scrub` contradicts it would persist as a
 * different trigger than its key and Designer UI say. Scroll scrub needs a
 * `scrub` value; Scroll actions and Scroll direction need it null or omitted.
 * Legacy `wf:scroll` accepts either. Shared by the DE/MCP invariant above and
 * the code-view interactions lane.
 */
export function findScrollKeyScrubModeError(
  key: string,
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  where: string
): string | null {
  const scrubs = scroll.scrub != null;
  if (key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB && !scrubs) {
    return (
      `${where}.scrub is required for "${key}"; without it the runtime ` +
      `plays the timelines as toggle actions instead of scrubbing them.`
    );
  }
  if (
    (key === IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS ||
      key === IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION) &&
    scrubs
  ) {
    return (
      `${where}.scrub must be null for "${key}"; a scrub value makes the ` +
      `runtime scrub the timelines instead of playing them as actions.`
    );
  }
  return null;
}

/**
 * Options whose meaning depends on the trigger key, which the runtime cannot
 * see (it builds from `scrollTriggerConfig` alone):
 * - `scrollDirection` is the sole marker that a config is a Scroll direction
 *   trigger, so `wf:scroll-direction` must carry it (without it the config runs
 *   as an ordinary toggle scroll trigger) and no other key may (it would run as
 *   Scroll direction). `directionEdgesOnly` is likewise Scroll direction only.
 * - `wf:scroll-direction` has no Pin control, so a pin (`true` or a target)
 *   would apply at runtime with no way to see or clear it in the panel, and a
 *   `pinSpacing` would have no pin to space.
 * - `wf:scroll-direction` is never batched, so a `stagger` would be ignored and
 *   the editor has no Trigger stagger control to clear it.
 * - `wf:scroll-direction` plays itself and ignores the toggle actions, so only
 *   the canonical set the Designer seeds (or none) is accepted.
 * - `wf:scroll-direction` also never sets up `outOfViewReset` (its playback
 *   wiring returns before the reset), so any value but the no-op `'none'` would
 *   never take effect.
 * - `wf:scroll-scrub` scrubs instead of playing toggle actions and its editor
 *   has no controls for them, so only the canonical set the Designer seeds (or
 *   none) is accepted. Legacy `wf:scroll` can switch back to actions mode, so it
 *   keeps any value.
 * - `stagger` and `outOfViewReset` are ignored while scrubbing (the runtime
 *   only applies them to toggle-action playback), so a scrubbed config that
 *   carries either would persist a setting that never takes effect.
 * Shared by the DE/MCP invariant above and the code-view interactions lane.
 */
export function findScrollKeyOptionMismatchError(
  key: string,
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  where: string
): string | null {
  const isDirection = key === IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION;
  if (isDirection && scroll.scrollDirection === undefined) {
    return (
      `${where}.scrollDirection is required for "${key}" (use "down"); it is ` +
      `the only marker the runtime has that this is a Scroll direction ` +
      `trigger, so without it the timelines play as ordinary scroll actions.`
    );
  }
  if (isDirection && scroll.pin !== undefined && scroll.pin !== false) {
    return (
      `${where}.pin is not supported for "${key}"; the Scroll direction ` +
      `editor has no Pin control, so a pin would apply at runtime with no ` +
      `way to inspect or clear it.`
    );
  }
  if (isDirection && scroll.pinSpacing !== undefined) {
    return (
      `${where}.pinSpacing is not supported for "${key}"; the Scroll ` +
      `direction editor has no Pin control, so there is no pin for it to ` +
      `space.`
    );
  }
  if (isDirection && scroll.stagger !== undefined) {
    return (
      `${where}.stagger is not supported for "${key}"; the runtime wires ` +
      `Scroll direction one trigger at a time and never batches it, and the ` +
      `editor has no Trigger stagger control, so it would never take effect ` +
      `and could not be cleared.`
    );
  }
  // `outOfViewReset: 'none'` is the explicit no-op the select's default writes,
  // so it is the same as leaving the key off (see the scrub check below).
  if (
    isDirection &&
    scroll.outOfViewReset !== undefined &&
    scroll.outOfViewReset !== 'none'
  ) {
    return (
      `${where}.outOfViewReset is not supported for "${key}"; the runtime ` +
      `wires Scroll direction playback itself and never sets up the reset, ` +
      `and the editor has no Out of view control, so it would never take ` +
      `effect.`
    );
  }
  if (isDirection) {
    // Scroll direction wires its own playback and never reads the toggle
    // actions, so the panel has no control for them and the Designer only ever
    // writes the canonical set. Omitting them stays valid.
    const toggle = (
      Object.keys(SCROLL_DIRECTION_TOGGLES) as Array<
        keyof typeof SCROLL_DIRECTION_TOGGLES
      >
    ).find(
      (option) =>
        scroll[option] !== undefined &&
        scroll[option] !== SCROLL_DIRECTION_TOGGLES[option]
    );
    if (toggle) {
      return (
        `${where}.${toggle} must be "${SCROLL_DIRECTION_TOGGLES[toggle]}" ` +
        `(or omitted) for "${key}"; the runtime plays Scroll direction itself ` +
        `and ignores the toggle actions, so a different value would never take ` +
        `effect.`
      );
    }
  }
  if (key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB) {
    const toggle = SCROLL_TOGGLE_KEYS.find(
      (option) =>
        scroll[option] !== undefined &&
        scroll[option] !== SCROLL_TOGGLE_DEFAULTS[option]
    );
    if (toggle) {
      return (
        `${where}.${toggle} must be "${SCROLL_TOGGLE_DEFAULTS[toggle]}" ` +
        `(or omitted) for "${key}"; the runtime scrubs instead of playing ` +
        `the toggle actions and the editor has no controls for them, so a ` +
        `different value would never take effect.`
      );
    }
  }
  if (!isDirection) {
    const directionOnly = (
      ['scrollDirection', 'directionEdgesOnly'] as const
    ).find((option) => scroll[option] !== undefined);
    if (directionOnly) {
      return (
        `${where}.${directionOnly} is only valid for ` +
        `"${IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION}"; on "${key}" it would ` +
        `make the runtime treat the trigger as Scroll direction.`
      );
    }
  }
  if (scroll.scrub != null) {
    // `outOfViewReset: 'none'` is the explicit no-op the select's default
    // writes, so it is the same as leaving the key off.
    const toggleOnly = (['stagger', 'outOfViewReset'] as const).find(
      (option) =>
        scroll[option] !== undefined &&
        !(option === 'outOfViewReset' && scroll[option] === 'none')
    );
    if (toggleOnly) {
      return (
        `${where}.${toggleOnly} is not supported with scrub; the runtime ` +
        `ignores it while scrubbing, so it would never take effect.`
      );
    }
  }
  return null;
}

/**
 * `wf:scroll-direction` has no Clamp control, and the Designer seeds `clamp`
 * from the trigger target when the interaction is created or retargeted: on for
 * an element, off for the Page. A `clamp` that contradicts the target (an
 * element with `clamp: false`, the Page with `clamp: true`) would shift the
 * published thresholds with nothing in the panel to show or clear it. Omitting
 * `clamp` stays valid. Shared by the DE/MCP invariant and the code-view
 * interactions lane, which both have the trigger target in hand.
 */
export function findScrollDirectionClampError(
  key: string,
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  target: unknown,
  where: string
): string | null {
  if (key !== IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION) return null;
  // The lane runs before the target's own shape check, so it may not be a tuple.
  if (scroll.clamp === undefined || !Array.isArray(target)) return null;
  const isPage = target[0] === IX3_WF_EXTENSION_KEYS.BODY;
  if (isPage && scroll.clamp === true) {
    return (
      `${where}.clamp must be false (or omitted) for "${key}" on a Page ` +
      `target; the editor has no Clamp control and seeds it off for the page.`
    );
  }
  if (!isPage && scroll.clamp === false) {
    return (
      `${where}.clamp must be true (or omitted) for "${key}" on an element ` +
      `target; the editor has no Clamp control and seeds it on for an element.`
    );
  }
  return null;
}

/**
 * The runtime never pins `<body>` (wrapping it in a pin-spacer breaks document
 * scrolling), and the Designer clears a boolean `pin` when the target changes
 * to the Page, so a `pin: true` on a Page target is a no-op the panel would
 * still show as a checked Pin with an empty "Other element" picker. Pinning on
 * a Page goes through a `wf:inst` tuple instead. Only the split Scroll scrub
 * and Scroll actions keys are checked (Scroll direction rejects `pin` outright,
 * and legacy `wf:scroll` may carry the value from before the Designer cleared
 * it). Scroll actions on a Page also rejects an `outOfViewReset` other than
 * `'none'`: it watches the trigger element, and `<body>` never leaves the
 * viewport, so the value cannot do anything and the panel hides its control for
 * a Page (the Page retarget already clears it). A legacy `wf:scroll` marked
 * `split: true` with no scrub is the same actions editor and runtime, so it
 * gets this check too (but not the `pin` one). Scroll scrub rejects it as a
 * scrub-mismatch option instead. `clamp` and `showMarkers` stay valid on a Page:
 * the Designer seeds `clamp: true` for new Scroll scrub/actions interactions,
 * whose default target is the Page, and both settings are honored at runtime.
 * Shared by the DE/MCP invariant and the code-view interactions lane, which both
 * have the trigger target in hand.
 */
export function findScrollPageTargetError(
  key: string,
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  target: unknown,
  where: string
): string | null {
  const isSplitLegacyActions =
    key === IX3_WF_EXTENSION_KEYS.SCROLL &&
    scroll.split === true &&
    scroll.scrub == null;
  if (
    key !== IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB &&
    key !== IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS &&
    !isSplitLegacyActions
  ) {
    return null;
  }
  // The lane runs before the target's own shape check, so it may not be a tuple.
  if (!Array.isArray(target)) return null;
  if (target[0] !== IX3_WF_EXTENSION_KEYS.BODY) return null;
  if (scroll.pin === true && !isSplitLegacyActions) {
    return (
      `${where}.pin cannot be true for "${key}" on a Page target; the runtime ` +
      `never pins <body>. Pin a specific element with a ` +
      `"${IX3_WF_EXTENSION_KEYS.INST}" target instead.`
    );
  }
  if (
    (key === IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS || isSplitLegacyActions) &&
    scroll.outOfViewReset !== undefined &&
    scroll.outOfViewReset !== 'none'
  ) {
    return (
      `${where}.outOfViewReset must be "none" (or omitted) for "${key}" on a ` +
      `Page target; it watches the trigger element and <body> never leaves ` +
      `the viewport, so it would never take effect.`
    );
  }
  return null;
}

/**
 * Rejects `scrollDirection: 'up'`. The schema accepts it so stored data parses,
 * but the runtime only builds the 'down' polarity today, so an authored 'up'
 * would validate and then play the wrong way. Shared by the DE/MCP invariant
 * above and the code-view interactions lane.
 */
export function findReservedScrollDirectionError(
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  where: string
): string | null {
  if (scroll.scrollDirection !== 'up') return null;
  return (
    `${where}.scrollDirection "up" is reserved; the runtime only builds the ` +
    `"down" polarity, so scrolling up would not play forward as requested.`
  );
}

/**
 * Rejects a split-only scroll option on a config without `split: true`. Shared
 * by the DE/MCP invariant above and the code-view interactions lane, so every
 * authoring path refuses a config the runtime would silently run on the legacy
 * path. It also rejects `vh`/`vw`/`rem` `start`/`end` positions on such a
 * config, since only the split path converts those units.
 */
export function findSplitOnlyScrollOptionError(
  scroll: NonNullable<Triggers[number][1]['scrollTriggerConfig']>,
  where: string
): string | null {
  if (scroll.split === true) return null;
  // `outOfViewReset: 'none'` is the explicit no-op the select's default writes,
  // so it is the same as leaving the key off (see the scrub check above).
  const splitOnly = SPLIT_ONLY_SCROLL_OPTIONS.find(
    (option) =>
      scroll[option] !== undefined &&
      !(option === 'outOfViewReset' && scroll[option] === 'none')
  );
  if (splitOnly) {
    return (
      `${where}.${splitOnly} requires "split": true; the runtime only ` +
      `applies it to configs the split Scroll editors write, so without ` +
      `it the option is silently ignored.`
    );
  }
  // `vh`/`vw`/`rem` positions are split-only too: the legacy path hands the
  // string to GSAP unresolved, which reads `3vh` as 3 pixels.
  const unitPosition = (['start', 'end'] as const).find(
    (position) =>
      typeof scroll[position] === 'string' &&
      hasResolvableScrollUnit(scroll[position])
  );
  if (!unitPosition) return null;
  return (
    `${where}.${unitPosition} uses a vh/vw/rem unit, which requires ` +
    `"split": true; the runtime only converts them for configs the split ` +
    `Scroll editors write, so without it GSAP would read them as pixels.`
  );
}

/**
 * Scroll options the runtime applies only when `split` is set (see `split` in
 * the scroll trigger schema). Checked here, not in the Zod schema: the schema
 * also parses stored documents, and an invariant only runs on authoring writes.
 */
const SPLIT_ONLY_SCROLL_OPTIONS = [
  'directionEdgesOnly',
  'scrollDirection',
  'outOfViewReset',
  'stagger',
  'pinSpacing',
] as const;

/**
 * Every trigger key that carries a `scrollTriggerConfig`: legacy `wf:scroll`
 * plus the three split keys, which `createBaseTriggerConfig` seeds the same way.
 */
const SCROLL_CONFIG_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.SCROLL,
  IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB,
  IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS,
  IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION,
]);

/**
 * A wf:scroll trigger needs both a target and a scrollTriggerConfig. Both are
 * schema-optional but the Designer always writes them, and without them the
 * runtime resolves nothing / ScrollTriggerStrategy.bind bails. Mirrors code-views.
 *
 * Also rejects a defined scrollTriggerConfig on any other trigger key. The
 * shared trigger schema accepts the field under every key, and publish-time
 * target collection walks pin/endTrigger/scroller without looking at the key,
 * so a click (etc.) carrying that config would stamp `data-wf-target` and
 * change published behavior the panel can neither show nor clear.
 */
export function findScrollTriggerError(triggers: Triggers): string | null {
  for (const trigger of triggers) {
    const [key, config, target] = trigger;
    if (config == null || typeof config !== 'object') {
      return (
        `Trigger "${key}" is missing a config object; the Designer always ` +
        `writes one.`
      );
    }
    if (!SCROLL_CONFIG_TRIGGER_KEYS.has(key)) {
      if (config.scrollTriggerConfig != null) {
        return (
          `Trigger "${key}" must not set "scrollTriggerConfig"; that field ` +
          `is only authored on the scroll triggers ` +
          `(${[...SCROLL_CONFIG_TRIGGER_KEYS].join(', ')}) and the ` +
          `Designer has no control for it on other triggers.`
        );
      }
      continue;
    }
    if (target === undefined) {
      return (
        `Trigger "${key}" requires a trigger ` +
        `target — the element whose scroll position drives the timelines.`
      );
    }
    const scroll = config.scrollTriggerConfig;
    // Explicit null survives the object-to-tuple transform (it only drops
    // undefined) and would throw in findScrollTriggerConfigError. Treat it
    // as missing so the caller gets InteractionInvalid, not a Flux crash.
    if (scroll == null) {
      return (
        `Trigger "${key}" requires a ` +
        `"scrollTriggerConfig" (its scroll start/end settings); without it ` +
        `the runtime ignores the trigger.`
      );
    }
    if (typeof scroll !== 'object' || Array.isArray(scroll)) {
      return `Trigger "${key}" scrollTriggerConfig must be an object.`;
    }
    const configError = findScrollTriggerConfigError(scroll, key);
    if (configError) return configError;
    const clampError = findScrollDirectionClampError(
      key,
      scroll,
      target,
      `Trigger "${key}" scrollTriggerConfig`
    );
    if (clampError) return clampError;
    const pageTargetError = findScrollPageTargetError(
      key,
      scroll,
      target,
      `Trigger "${key}" scrollTriggerConfig`
    );
    if (pageTargetError) return pageTargetError;
  }
  return null;
}

/**
 * A target value must be present (no-value types use ""); z.unknown() admits an
 * absent one the Designer never emits. Mirrors the tools' `requireValue` gate.
 */
function findTargetValuePresenceError(
  target: TargetTuple,
  where: string
): string | null {
  return target[1] === undefined
    ? `${where}: target.value is required (use "" for target types that ` +
        `take no value, e.g. ${IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY}).`
    : null;
}

/**
 * wf:any-element needs a non-"none" filter relationship — the Designer disables
 * "none" for it (targets/index.ts `disabledFilterRelationships: [NONE]`).
 */
function findAnyElementFilterError(
  target: TargetTuple,
  where: string
): string | null {
  if (target[0] !== IX3_WF_EXTENSION_KEYS.ANY_ELEMENT) return null;
  const filterContext = target[2];
  return !filterContext ||
    filterContext.relationship === RELATIONSHIP_TYPES.NONE
    ? `${where}: "${IX3_WF_EXTENSION_KEYS.ANY_ELEMENT}" requires a filter ` +
        `relationship other than "none".`
    : null;
}

/**
 * A trigger's own target is context-restricted: wf:body is only offered for
 * scroll triggers, wf:viewport only for mouse-move triggers
 * (targets/index.ts `getTargetUiConfig`). Action-only keys are also hidden
 * for `targetContext: 'trigger'` as both base and filterBy, so an active
 * nested filterBy must be inspected too — a click trigger filtered by
 * wf:trigger-only binds against no triggerElement and never fires.
 */
export function findTriggerTargetContextError(
  triggerKey: string,
  target: TargetTuple
): string | null {
  if (ACTION_ONLY_TARGET_KEYS.has(target[0])) {
    return (
      `Trigger target "${target[0]}" is only valid on timeline actions, ` +
      `not trigger targets.`
    );
  }
  // The component instance is read off a `wf:inst` target alone
  // (`getComponentInstancePaths`), so any other kind leaves the trigger naming
  // no popover while its siblings' popover targets still resolve nothing.
  if (
    isComponentInstanceTriggerKey(triggerKey) &&
    target[0] !== IX3_WF_EXTENSION_KEYS.INST
  ) {
    return (
      `Trigger "${triggerKey}" must use a "${IX3_WF_EXTENSION_KEYS.INST}" ` +
      `target naming its component instance, not "${target[0]}".`
    );
  }
  const filterContext = target[2];
  if (
    filterContext?.filterBy &&
    filterContext.relationship !== RELATIONSHIP_TYPES.NONE &&
    ACTION_ONLY_TARGET_KEYS.has(filterContext.filterBy[0])
  ) {
    return (
      `Trigger "${triggerKey}" target filter: "${filterContext.filterBy[0]}" ` +
      `is only valid on timeline actions, not as a Filter-by on a trigger ` +
      `target (there is no trigger element to filter against at bind time).`
    );
  }
  // The three triggers with no user-facing selector. Each fires from a
  // page-level signal — a custom event on the document, the pointer leaving the
  // viewport, a clock — so the element it binds to is bookkeeping, not part of
  // what the author asked for, and the Designer writes the hidden body target
  // for all three (`hideTargetEditor`).
  //
  // Required, not merely allowed: `StandardTriggerStrategy` binds once per
  // resolved element, so a DE/MCP or code-view caller supplying a class or
  // selector target would arm one timer or listener per match and run the
  // interaction once per element from a single exit or timeout. The body target
  // resolves to exactly one.
  if (
    triggerKey === IX3_WF_EXTENSION_KEYS.CUSTOM ||
    triggerKey === IX3_WF_EXTENSION_KEYS.EXIT_INTENT ||
    triggerKey === IX3_WF_EXTENSION_KEYS.DURATION
  ) {
    if (target[0] !== IX3_WF_EXTENSION_KEYS.BODY) {
      return (
        `Trigger "${triggerKey}" must use the hidden ` +
        `"${IX3_WF_EXTENSION_KEYS.BODY}" target the Designer writes.`
      );
    }
    // The hidden body target resolves to exactly one element (document.body)
    // only when unfiltered. An active relationship filter runs at resolve
    // time same as any other target's, and `document.body` matches no
    // ordinary selector's relationship, so a filter here silently empties the
    // element list `StandardTriggerStrategy` binds to. Scoped to the two new
    // keys: `wf:custom` predates this check, so a stored one carrying a filter
    // must stay editable (no baseline reaches this function).
    if (
      triggerKey !== IX3_WF_EXTENSION_KEYS.CUSTOM &&
      filterContext?.filterBy &&
      filterContext.relationship !== RELATIONSHIP_TYPES.NONE
    ) {
      return (
        `Trigger "${triggerKey}" target: the hidden ` +
        `"${IX3_WF_EXTENSION_KEYS.BODY}" target cannot carry a Filter-by ` +
        `relationship (there is no trigger element to filter against at ` +
        `bind time).`
      );
    }
  }
  if (
    target[0] === IX3_WF_EXTENSION_KEYS.BODY &&
    !SCROLL_CONFIG_TRIGGER_KEYS.has(triggerKey) &&
    triggerKey !== IX3_WF_EXTENSION_KEYS.CUSTOM &&
    // Exit intent and Duration write the same hidden body target `wf:custom`
    // does — they have no user-facing selector, but a STANDARD trigger still
    // has to bind to something. Without them here, a create carrying the
    // target the Designer itself writes is rejected at the DE/MCP boundary.
    triggerKey !== IX3_WF_EXTENSION_KEYS.EXIT_INTENT &&
    triggerKey !== IX3_WF_EXTENSION_KEYS.DURATION
  ) {
    return (
      `Trigger target "${IX3_WF_EXTENSION_KEYS.BODY}" is only valid on ` +
      `a "${IX3_WF_EXTENSION_KEYS.SCROLL}" trigger (or one of its ` +
      `scroll-scrub, scroll-actions or scroll-direction variants) or the ` +
      `hidden "${IX3_WF_EXTENSION_KEYS.CUSTOM}", ` +
      `"${IX3_WF_EXTENSION_KEYS.EXIT_INTENT}" or ` +
      `"${IX3_WF_EXTENSION_KEYS.DURATION}" default target.`
    );
  }
  if (
    target[0] === IX3_WF_EXTENSION_KEYS.VIEWPORT &&
    triggerKey !== IX3_WF_EXTENSION_KEYS.MOUSE_MOVE
  ) {
    return (
      `Trigger target "${IX3_WF_EXTENSION_KEYS.VIEWPORT}" is only valid ` +
      `on a "${IX3_WF_EXTENSION_KEYS.MOUSE_MOVE}" trigger.`
    );
  }
  return null;
}

/**
 * All trigger-scoped invariants (run on create, and on update when triggers
 * are replaced). `baselineTriggers` is the stored trigger list on the update
 * path, so legacy values the panel preserves stay editable (see
 * `findHoverConfigModelError`).
 */
export function findTriggerInvariantError(
  triggers: Triggers,
  baselineTriggers?: Triggers
): string | null {
  const error =
    findStandaloneTriggerError(triggers) ??
    findMultipleLoadTriggerError(triggers) ??
    findLoadTriggerPurityError(triggers) ??
    findControlTypeMismatchError(triggers) ??
    findScrollTriggerError(triggers) ??
    findConditionsCapabilityError(triggers) ??
    findConditionalOutcomeError(triggers) ??
    findConditionValuePresenceError(triggers) ??
    findContinuousConditionsError(triggers) ??
    findHoverConfigModelError(triggers, baselineTriggers) ??
    findAssignedTimelineRoleError(triggers, baselineTriggers) ??
    findComponentBackdropRoleError(triggers) ??
    findAssignedInstancesError(triggers) ??
    findReservedPluginConfigKeyError(triggers, baselineTriggers) ??
    findMouseMoveRangeError(triggers) ??
    findCustomEventNameError(triggers) ??
    findClickPluginConfigError(triggers) ??
    findHoverPluginConfigValueError(triggers) ??
    findDurationPluginConfigError(triggers) ??
    findUneditablePlaybackFieldError(triggers) ??
    findTriggerControlAllowedError(triggers) ??
    findTriggerJumpError(triggers) ??
    findTriggerSpeedError(triggers);
  if (error) return error;

  for (const [key, , target] of triggers) {
    if (!target) {
      // Exit intent and Duration require a target too, but the Designer never
      // shows them a picker — point at the hidden body target, not a prompt.
      if (
        key === IX3_WF_EXTENSION_KEYS.CUSTOM ||
        key === IX3_WF_EXTENSION_KEYS.EXIT_INTENT ||
        key === IX3_WF_EXTENSION_KEYS.DURATION
      ) {
        return (
          `Trigger "${key}" must use the hidden ` +
          `"${IX3_WF_EXTENSION_KEYS.BODY}" target the Designer writes.`
        );
      }
      // A component trigger IS the instance it names, and the owner and role
      // checks read that instance off its `wf:inst` target. Without one the
      // engine binds no handler, yet the trigger still counts as the owner of
      // its roles. The Designer always writes the target, unset as `''`.
      if (isComponentInstanceTriggerKey(key)) {
        return (
          `Trigger "${key}" requires a "${IX3_WF_EXTENSION_KEYS.INST}" ` +
          `target naming its component instance; the Designer always writes one.`
        );
      }
      if (TRIGGER_REQUIRES_TARGET_KEYS.has(key)) {
        return (
          `Trigger "${key}" requires a target element; the Designer always ` +
          `prompts for one in the trigger configuration panel.`
        );
      }
      continue;
    }
    const where = `trigger "${key}" target`;
    const targetError =
      findTargetValuePresenceError(target, where) ??
      findAnyElementFilterError(target, where) ??
      findTriggerTargetContextError(key, target);
    if (targetError) return targetError;
  }
  return null;
}

/**
 * The Duration trigger's mode and duration, which its own panel can only ever
 * write inside these bounds.
 *
 * `pluginConfig` is an open JSON record, so the declared `DurationTriggerConfig`
 * is a compile-time promise no write path enforces. Left unchecked, a
 * code-authored `{mode: 'timeOnSite', duration: 'five'}` commits cleanly and the
 * runtime turns it into `NaN` — `setTimeout` then fires immediately, the
 * opposite of a duration. The runtime clamps defensively too; this is what stops
 * the value being stored in the first place.
 */
export function findDurationPluginConfigError(
  triggers: Triggers
): string | null {
  for (const [key, config] of triggers) {
    if (key !== IX3_WF_EXTENSION_KEYS.DURATION) continue;
    const pluginConfig = config.pluginConfig as
      | {mode?: unknown; duration?: unknown}
      | undefined;

    const mode = pluginConfig?.mode;
    if (
      mode !== undefined &&
      !DURATION_TRIGGER_MODES.includes(mode as DurationTriggerMode)
    ) {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.DURATION}" pluginConfig.mode must ` +
        `be one of ${DURATION_TRIGGER_MODES.join(', ')}; the Designer's ` +
        `Measure control only writes those.`
      );
    }

    const duration = pluginConfig?.duration;
    if (duration === undefined) continue;
    // The floor is the effective mode's, not the range's: `idle` counts down
    // from the last input, so a stored 0 schedules the first timer at 0ms and
    // fires before any activity can reset it — the opposite of an idle trigger.
    // The Designer's field already clamps to this; nothing else did.
    const minimum = minimumDurationSeconds(
      (mode as DurationTriggerMode | undefined) ?? DEFAULT_DURATION_TRIGGER_MODE
    );
    if (
      typeof duration !== 'number' ||
      !Number.isFinite(duration) ||
      duration < minimum ||
      duration > DURATION_TRIGGER_SECONDS_RANGE.max
    ) {
      return (
        `Trigger "${IX3_WF_EXTENSION_KEYS.DURATION}" pluginConfig.duration ` +
        `must be a finite number of seconds between ` +
        `${minimum} and ` +
        `${DURATION_TRIGGER_SECONDS_RANGE.max}; the Designer's duration ` +
        `field only persists that range.`
      );
    }
  }
  return null;
}

/**
 * Size bound for the open `z.unknown()` value fields the `pluginConfig` guard
 * does not reach: target values, `filterContext.filterBy` values, condition
 * values, and animation property values. A single write can otherwise store
 * megabytes in one record, and IX3 data is published to site visitors against a
 * runtime with a 29 KB gzipped budget.
 *
 * Size only. The `unknown` is legitimate for plugin flexibility, so this caps
 * how much a caller can store, never the shape of it (`undefined` and sparse
 * animation forms stay legal). The whole interaction shares one running budget
 * so many small sibling values can't sum past the cap.
 *
 * Called from the untrusted ingress boundary (`validateClient`, which serves
 * both the Designer Extension iframe and page-automation paths), deliberately
 * NOT from the schemas. `parseIX3DataStrict` is also the server-side write
 * validator, so a schema-level cap would make an already-oversized interaction
 * unsaveable and lock its owner out of editing the site.
 */
type ValueBudget = ReturnType<typeof createIx3ValueBudget>;

// Every open field of one target tuple: the value itself and, when present, the
// filterContext.filterBy value. Both share the interaction's running budget.
function checkTargetTuple(
  budget: ValueBudget,
  target: TargetTuple,
  where: string
): string | null {
  const valueError = budget.add(target[1], `${where} value`);
  if (valueError) return valueError;
  const filterBy = target[2]?.filterBy;
  if (filterBy) {
    return budget.add(filterBy[1], `${where} filterContext.filterBy value`);
  }
  return null;
}

export function findOversizedValueError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  // One budget across the whole interaction: byte and node totals accumulate,
  // so many sibling values that are each individually small still fail in
  // aggregate (the published-payload risk is the total, not any one value).
  const budget = createIx3ValueBudget();

  for (const [key, config, target] of triggers) {
    if (target) {
      const error = checkTargetTuple(budget, target, `trigger "${key}" target`);
      if (error) return error;
    }
    // conditionalLogic.conditions[*][1] is also an open z.unknown() value.
    const conditions = config?.conditionalLogic?.conditions ?? [];
    for (let i = 0; i < conditions.length; i++) {
      const error = budget.add(
        conditions[i]![1],
        `trigger "${key}" condition ${i} value`
      );
      if (error) return error;
    }
    // scrollTriggerConfig carries three more target tuples (pin — in its
    // non-boolean form — endTrigger, scroller), each with the same open value
    // and optional filterBy the primary target has.
    const scroll = config?.scrollTriggerConfig;
    if (scroll) {
      const scrollTargets: Array<[string, TargetTuple | undefined]> = [
        ['pin', typeof scroll.pin === 'boolean' ? undefined : scroll.pin],
        ['endTrigger', scroll.endTrigger],
        ['scroller', scroll.scroller],
      ];
      for (const [field, scrollTarget] of scrollTargets) {
        if (!scrollTarget) continue;
        const error = checkTargetTuple(
          budget,
          scrollTarget,
          `trigger "${key}" scrollTriggerConfig.${field}`
        );
        if (error) return error;
      }
      // start/end are free-form position strings (numeric branch has no length
      // cap), so a megabyte digit string here also reaches published output.
      for (const field of ['start', 'end'] as const) {
        const error = budget.add(
          scroll[field],
          `trigger "${key}" scrollTriggerConfig.${field}`
        );
        if (error) return error;
      }
    }
  }

  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const at = `timeline "${timeline.id}" action "${action.id}"`;

      for (const target of action.targets) {
        const error = checkTargetTuple(budget, target, `${at} target`);
        if (error) return error;
      }

      // properties: Record<extensionKey, Record<propertyName, AnimationValue>>
      for (const [extensionKey, values] of Object.entries(
        action.properties ?? {}
      )) {
        for (const [property, value] of Object.entries(values ?? {})) {
          // The property NAME is persisted and published too, and is
          // unbounded, so charge it against the same budget before its value.
          const nameError = budget.add(
            property,
            `${at} property "${extensionKey}" name`
          );
          if (nameError) return nameError;
          const error = budget.add(
            value,
            `${at} property "${extensionKey}.${property}" value`
          );
          if (error) {
            return error;
          }
        }
      }
    }
  }

  return null;
}

/**
 * Write-path count caps (Gap 8 / DSYS-7212). Numbers must match
 * `packages/systems/ix3/store/constants.ts` — drift-tested in
 * designerInvariantsDrift.test.ts. Schema cannot import the store package.
 */
export const IX3_MAX_TRIGGERS_PER_INTERACTION = 20;
export const IX3_MAX_ACTIONS_PER_TIMELINE = 200;
export const IX3_MAX_TARGETS_PER_ACTION = 20;
/** Mirrors store `IX3_MAX_TIMELINE_GROUPS`. */
export const IX3_MAX_TIMELINES_PER_INTERACTION = 5;

/** Peak counts across an interaction's triggers + timelines. */
export type InteractionCountSnapshot = {
  triggerCount: number;
  timelineCount: number;
  maxActionsPerTimeline: number;
  maxTargetsPerAction: number;
};

/**
 * Measures the four Gap 8 count dimensions for create/update comparison.
 */
export function measureInteractionCounts(
  triggers: Triggers,
  timelines: readonly Timeline[]
): InteractionCountSnapshot {
  let maxActionsPerTimeline = 0;
  let maxTargetsPerAction = 0;
  for (const timeline of timelines) {
    maxActionsPerTimeline = Math.max(
      maxActionsPerTimeline,
      timeline.actions.length
    );
    for (const action of timeline.actions) {
      maxTargetsPerAction = Math.max(
        maxTargetsPerAction,
        action.targets.length
      );
    }
  }
  return {
    triggerCount: triggers.length,
    timelineCount: timelines.length,
    maxActionsPerTimeline,
    maxTargetsPerAction,
  };
}

/**
 * When the update exemption raises the ceiling above the policy cap, name
 * that ceiling so growing 201 → 202 does not report "at most 200".
 */
function countCeilingNote(ceiling: number, policyCap: number): string {
  return `(existing over-cap peak ${ceiling}; the policy cap is ${policyCap})`;
}

/**
 * Rejects interactions that exceed DE/MCP per-interaction count caps.
 *
 * Create: call without `baseline` for hard caps. Cloning a Designer-authored
 * over-cap interaction via get → create is therefore rejected; there is no
 * source-interaction baseline on create, and adding one would be a new API.
 * Update: pass `baseline` from the existing stored shape so a dimension that
 * was already over cap stays editable for non-increasing re-saves (Designer
 * can legally author 200+ actions / 20+ targets). Peaks are
 * interaction-wide (`maxActionsPerTimeline` / `maxTargetsPerAction`), so a
 * sibling timeline may grow up to that peak. Growth past max(cap, existing)
 * and cross-dimension invent still reject. Name-only updates skip this
 * helper entirely at the host.
 */
export function findInteractionCountError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baseline?: InteractionCountSnapshot
): string | null {
  const triggerCeiling = Math.max(
    IX3_MAX_TRIGGERS_PER_INTERACTION,
    baseline?.triggerCount ?? 0
  );
  if (triggers.length > triggerCeiling) {
    if (triggerCeiling > IX3_MAX_TRIGGERS_PER_INTERACTION) {
      return (
        `An interaction may define at most ${triggerCeiling} triggers ` +
        `${countCeilingNote(triggerCeiling, IX3_MAX_TRIGGERS_PER_INTERACTION)}.`
      );
    }
    return `An interaction may define at most ${IX3_MAX_TRIGGERS_PER_INTERACTION} triggers.`;
  }

  const timelineCeiling = Math.max(
    IX3_MAX_TIMELINES_PER_INTERACTION,
    baseline?.timelineCount ?? 0
  );
  if (timelines.length > timelineCeiling) {
    if (timelineCeiling > IX3_MAX_TIMELINES_PER_INTERACTION) {
      return (
        `An interaction may define at most ${timelineCeiling} timelines ` +
        `${countCeilingNote(timelineCeiling, IX3_MAX_TIMELINES_PER_INTERACTION)}; ` +
        `split it into multiple interactions.`
      );
    }
    return (
      `An interaction may define at most ${IX3_MAX_TIMELINES_PER_INTERACTION} timelines; ` +
      `split it into multiple interactions.`
    );
  }

  const actionCeiling = Math.max(
    IX3_MAX_ACTIONS_PER_TIMELINE,
    baseline?.maxActionsPerTimeline ?? 0
  );
  const targetCeiling = Math.max(
    IX3_MAX_TARGETS_PER_ACTION,
    baseline?.maxTargetsPerAction ?? 0
  );

  for (const timeline of timelines) {
    if (timeline.actions.length > actionCeiling) {
      if (actionCeiling > IX3_MAX_ACTIONS_PER_TIMELINE) {
        return (
          `A timeline may define at most ${actionCeiling} actions ` +
          `${countCeilingNote(actionCeiling, IX3_MAX_ACTIONS_PER_TIMELINE)}; ` +
          `split the animation across multiple timelines.`
        );
      }
      return (
        `A timeline may define at most ${IX3_MAX_ACTIONS_PER_TIMELINE} actions; ` +
        `split the animation across multiple timelines.`
      );
    }
    for (const action of timeline.actions) {
      if (action.targets.length > targetCeiling) {
        if (targetCeiling > IX3_MAX_TARGETS_PER_ACTION) {
          return (
            `An action may target at most ${targetCeiling} elements ` +
            `${countCeilingNote(targetCeiling, IX3_MAX_TARGETS_PER_ACTION)}; ` +
            `use a class or selector target to affect many elements at once.`
          );
        }
        return (
          `An action may target at most ${IX3_MAX_TARGETS_PER_ACTION} elements; ` +
          `use a class or selector target to affect many elements at once.`
        );
      }
    }
  }
  return null;
}

/**
 * Action-target invariants (value presence + wf:any-element filter). Run on
 * create and on update when timelines are replaced.
 */
export function findTimelineTargetInvariantError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      for (const target of action.targets) {
        const where = `timeline "${timeline.id}" action "${action.id}" target`;
        // Reject tuples whose length is outside the valid {2, 3} range; the
        // schema union is 2-tuple or 3-tuple and runtime resolvers expect no
        // other shape.
        const len = (target as unknown[]).length;
        if (len !== 2 && len !== 3) {
          return `${where}: target tuple must have length 2 or 3; got ${len}.`;
        }
        // body/viewport are trigger-context-only and cannot be action targets
        // (plan R4: wire only here, not in findActionTargetContextError).
        if (TRIGGER_CONTEXT_ONLY_TARGET_KEYS.has(target[0])) {
          return (
            `${where}: "${target[0]}" is a trigger-context-only target and ` +
            `cannot be used as an action target. To target the page body use ` +
            `wf:selector with "body", or target elements by class/selector.`
          );
        }
        const error =
          findTargetValuePresenceError(target, where) ??
          findAnyElementFilterError(target, where);
        if (error) return error;
        // A 3-tuple's filterContext must have a filterBy field; missing it
        // means the stored data is corrupt (R5: only reject genuinely absent).
        const filterContext = target[2];
        if (
          filterContext !== undefined &&
          filterContext.filterBy === undefined
        ) {
          return (
            `${where}: filterContext is present but missing "filterBy"; ` +
            `the stored filterContext is corrupt.`
          );
        }
      }
    }
  }
  return null;
}

/**
 * Trigger keys with no real trigger element — `StandardTriggerStrategy`
 * supplies the hidden `document.body` binding as `triggerElement` for these,
 * so `wf:trigger-only` / `-parent` action targets resolve to the wrong thing
 * whenever one of them drives the timeline the action sits on. Not only when
 * it happens to be the interaction's first trigger: an element trigger like
 * Click can share an interaction with a page-intent trigger like Exit intent
 * or Duration.
 *
 * Exported so the Designer's target dropdown (`hasNoTriggerElement` in
 * ix3-wf-plugins/designer/src/targets) hides exactly the targets the write
 * paths reject.
 */
export const NO_TRIGGER_ELEMENT_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.LOAD,
  IX3_WF_EXTENSION_KEYS.CUSTOM,
  IX3_WF_EXTENSION_KEYS.EXIT_INTENT,
  IX3_WF_EXTENSION_KEYS.DURATION,
]);

/** Whether `timeline` is one this trigger's routing lets it dispatch. */
function triggerRoutesToTimeline(
  config: Triggers[number][1],
  timeline: Pick<Timeline, 'groupId' | 'triggerMetadata'>
): boolean {
  // Mirrors IX3Engine.resolveAssignedTimelineIds, which is what actually
  // decides this at runtime. Explicit `null` is "unassigned, play nothing" —
  // distinct from an absent value, which is the legacy "play them all".
  if (config.assignedGroupId === null) return false;
  if (config.assignedGroupId) {
    return timeline.groupId === config.assignedGroupId;
  }
  if (config.assignedTimelineRole) {
    return timeline.triggerMetadata?.role === config.assignedTimelineRole;
  }
  return true;
}

/**
 * How many `reuse` links the runtime follows from a routed timeline: IX3Engine's
 * `runTimelineAction` redirects it to its `reuse.sourceTimelineId` once, then
 * AnimationCoordinator's `resolveAlias` follows 11 more (its MAX_ALIAS_DEPTH is
 * 10, but the depth counter starts at 0 and only bails once depth EXCEEDS 10).
 * Every Designer and code-view walk that mirrors the runtime's reach reads this
 * one bound, so they cannot drift apart; it also ends a cycle.
 */
export const MAX_REUSE_CHAIN_LINKS = 12;

/**
 * The live timelines outside `timelineIds` that those timelines reuse, directly
 * or through a chain: the `reuseSources` of
 * `findActionTargetContextViolations`. The code-view lane, unlike the DE path,
 * accepts a reuse source owned by another interaction, and the Designer's
 * target dropdown mirrors it, so both read this one walk and cannot drift
 * apart. The walk is bounded by `MAX_REUSE_CHAIN_LINKS`, which documents the
 * runtime's reach, and that bound also ends a cycle.
 */
export function collectExternalReuseSources(
  timelineIds: readonly string[],
  timelines: Readonly<Record<string, Timeline>>
): Timeline[] {
  const ownIds = new Set(timelineIds);
  const found = new Map<string, Timeline>();
  for (const timelineId of timelineIds) {
    let sourceId = timelines[timelineId]?.reuse?.sourceTimelineId;
    for (
      let depth = 0;
      sourceId != null && depth < MAX_REUSE_CHAIN_LINKS;
      depth++
    ) {
      const source = timelines[sourceId];
      if (!source || source.deleted) break;
      if (!ownIds.has(sourceId)) found.set(sourceId, source);
      sourceId = source.reuse?.sourceTimelineId;
    }
  }
  return [...found.values()];
}

/**
 * The key of an elementless trigger that can dispatch `timeline`, or
 * `undefined` when every such trigger on the interaction is routed elsewhere.
 *
 * Exported so the Designer's target dropdown can hide exactly the targets this
 * invariant rejects — one rule, so the panel can't offer something the DE and
 * code-view write paths then refuse.
 */
export function findElementlessTriggerKeyForTimeline(
  triggers: Triggers,
  timeline: Pick<Timeline, 'id' | 'groupId' | 'triggerMetadata'>,
  // The interaction's timelines. A `reuse` alias plays this timeline's actions
  // (IX3Engine redirects it to `reuse.sourceTimelineId`), so a trigger routed
  // to the alias dispatches them too — Navbar or Dropdown Close replays Open.
  timelines: readonly Pick<
    Timeline,
    'id' | 'groupId' | 'triggerMetadata' | 'reuse'
  >[] = []
): string | undefined {
  const byId = new Map(timelines.map((candidate) => [candidate.id, candidate]));
  // The whole chain, since the coordinator resolves one alias through another
  // (a DE write may chain them), and plays whatever it ends on. Bounded like the
  // runtime (`MAX_REUSE_CHAIN_LINKS`) so this invariant doesn't miss a
  // trigger-only violation the runtime would still execute at the 12th hop. The
  // bound also ends a cycle.
  const reachesTimeline = (candidate: (typeof timelines)[number]) => {
    let sourceId = candidate.reuse?.sourceTimelineId;
    for (
      let depth = 0;
      sourceId != null && depth < MAX_REUSE_CHAIN_LINKS;
      depth++
    ) {
      if (sourceId === timeline.id) return true;
      sourceId = byId.get(sourceId)?.reuse?.sourceTimelineId;
    }
    return false;
  };
  const aliases = timelines.filter(reachesTimeline);
  for (const [key, config] of triggers) {
    if (!NO_TRIGGER_ELEMENT_KEYS.has(key)) continue;
    // Page load ignores routing: `LoadTriggerStrategy` runs every timeline on
    // the interaction, whatever `assignedGroupId` (even a parked `null`) or
    // `assignedTimelineRole` it still carries. Its Control can still be "none",
    // so it takes the same no-op exemption below, just without a routing test.
    const routes = (candidate: Pick<Timeline, 'groupId' | 'triggerMetadata'>) =>
      key === IX3_WF_EXTENSION_KEYS.LOAD ||
      triggerRoutesToTimeline(config, candidate);
    // An Exit intent or Duration narrowed to no popovers (`[]`) fires from
    // nothing — the runtime's `pageLevelFireTargets` returns no elements for
    // it — so it dispatches no action here either.
    if (
      (key === IX3_WF_EXTENSION_KEYS.EXIT_INTENT ||
        key === IX3_WF_EXTENSION_KEYS.DURATION) &&
      config.assignedInstances?.length === 0
    ) {
      continue;
    }
    // A Page load, Custom event, Exit intent or Duration whose Control is
    // "none" and that has no conditional logic runs nothing:
    // `runTimelineAction` takes the 'none' arm and there is no outcome to
    // dispatch. Only on a timeline whose control comes from the trigger — a
    // role timeline (`triggerMetadata.role`) reads its control from its own
    // settings and ignores this one; non-routing metadata (`axes`) left behind
    // by a collapsed timeline does not, matching `getEffectivePlaybackConfig`.
    // That match holds with `timelineGroups` on; the flag-off runtime keeps the
    // legacy truthiness check, so roleless metadata there still reads its own
    // settings, a fallback that goes away when the flag does.
    // With conditional logic the trigger's own Control is not what runs: each
    // outcome does, and `executeConditionalOutcome` narrows it to its pinned
    // `targetTimelineId` (every routed timeline when unpinned, nothing for
    // "none"). A candidate no outcome reaches is not dispatched by this key.
    const outcomeReaches = (candidate: Pick<Timeline, 'id'>) => {
      const {conditionalLogic} = config;
      if (!conditionalLogic) return true;
      return [conditionalLogic.ifTrue, conditionalLogic.ifFalse].some(
        (outcome) =>
          outcome != null &&
          outcome.control !== 'none' &&
          (!outcome.targetTimelineId ||
            outcome.targetTimelineId === candidate.id)
      );
    };
    const dispatches = (
      candidate: Pick<Timeline, 'id' | 'groupId' | 'triggerMetadata'>
    ) =>
      routes(candidate) &&
      outcomeReaches(candidate) &&
      !(
        config.control === 'none' &&
        config.conditionalLogic === undefined &&
        candidate.triggerMetadata?.role == null
      );
    if (dispatches(timeline) || aliases.some(dispatches)) {
      return key;
    }
  }
  return undefined;
}

/**
 * wf:trigger-only / -parent action targets reference the trigger element, which
 * doesn't exist for page-load / custom / exit-intent / duration triggers (the
 * Designer hides them there). Needs both the trigger and action targets, so
 * it runs when either is authored.
 *
 * Per timeline rather than per interaction, because routing decides which
 * trigger actually drives which timeline: Click assigned to group A and
 * Duration to group B leaves a wf:trigger-only action in group A perfectly
 * valid, since `resolveAssignedTimelineIds` never lets Duration dispatch it.
 */
export function findActionTargetContextError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  const errors = findActionTargetContextViolations(triggers, timelines);
  if (errors.length === 0 || !baselineTriggers || !baselineTimelines) {
    return errors[0]?.message ?? null;
  }
  // An update grandfathers what the stored interaction already had, as the
  // code-view lane does: this scan covers every elementless sibling, not just
  // the primary trigger, so a previously accepted interaction can carry a
  // violation that an unrelated edit must not start rejecting. Consumed
  // one-for-one, so one stored violation cannot grandfather a second one with
  // the same id added beside it.
  const baseline = findActionTargetContextViolations(
    baselineTriggers,
    baselineTimelines
  ).map(({id}) => id);
  return (
    errors.find(({id}) => {
      const grandfatheredAt = baseline.indexOf(id);
      if (grandfatheredAt === -1) return true;
      baseline.splice(grandfatheredAt, 1);
      return false;
    })?.message ?? null
  );
}

/**
 * Every violation `findActionTargetContextError` would report, one per target,
 * for a caller that grandfathers persisted ones: comparing only the first lets
 * an old violation mask a newly authored one on another timeline or action.
 */
export function findActionTargetContextErrors(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string[] {
  return findActionTargetContextViolations(triggers, timelines).map(
    ({message}) => message
  );
}

/**
 * A `findActionTargetContextErrors` violation with an `id` to grandfather it
 * by. The id is the target's location and key, without the trigger the
 * message names: that is just the first elementless trigger routed to the
 * timeline, so removing or reordering triggers changes the message while the
 * stored violation stays the same.
 */
export type ActionTargetContextViolation = {id: string; message: string};

export function findActionTargetContextViolations(
  triggers: Triggers,
  timelines: readonly Timeline[],
  /**
   * Timelines owned by other interactions that one of `timelines` reuses,
   * directly or through a chain. The runtime plays a `reuse` alias's source
   * wherever it lives, so a source outside the interaction still dispatches
   * its actions for these triggers. Only the code-view lane can write one; the
   * Designer and the DE path keep reuse within an interaction.
   */
  reuseSources: readonly Timeline[] = []
): ActionTargetContextViolation[] {
  const errors: ActionTargetContextViolation[] = [];
  // Cheap bail so the common single-element-trigger interaction never walks
  // its timelines at all.
  if (!triggers.some(([key]) => NO_TRIGGER_ELEMENT_KEYS.has(key))) {
    return errors;
  }
  const ownIds = new Set(timelines.map(({id}) => id));
  const externalSources = reuseSources.filter(({id}) => !ownIds.has(id));
  // An external timeline is reached only through one of this interaction's
  // aliases, never routed to directly, so it joins the alias chain without
  // the `groupId` / role that would let a trigger match it on its own.
  const chain = [
    ...timelines,
    ...externalSources.map(({id, reuse}) => ({id, reuse})),
  ];
  const scanned: Array<[Timeline, Pick<Timeline, 'id'>]> = [
    ...timelines.map((timeline): [Timeline, Timeline] => [timeline, timeline]),
    ...externalSources.map((source): [Timeline, Pick<Timeline, 'id'>] => [
      source,
      {id: source.id},
    ]),
  ];
  for (const [timeline, routing] of scanned) {
    // A reuse alias plays its source's actions, so its own body never runs
    // (`AnimationCoordinator.createTimeline` redirects it) even though the
    // schema lets it carry some. The source is scanned in its own right, so a
    // trigger that reaches it through the alias is still caught there.
    if (timeline.reuse?.sourceTimelineId) continue;
    const primaryKey = findElementlessTriggerKeyForTimeline(
      triggers,
      routing,
      chain
    );
    if (primaryKey === undefined) continue;
    for (const action of timeline.actions) {
      for (const target of action.targets) {
        const where = `timeline "${timeline.id}" action "${action.id}" target`;
        // `getTargetUiConfig` returns shouldShow: false for both trigger-only
        // targets on load/custom before it branches on filteringContext, so
        // they are hidden as a base Target *and* in the Filter-by picker. An
        // inactive none-filter filterBy is a stamped artifact, not a choice.
        const filterContext = target[2];
        const candidates: Array<[string, string]> = [[target[0], where]];
        if (
          filterContext?.filterBy &&
          filterContext.relationship !== RELATIONSHIP_TYPES.NONE
        ) {
          candidates.push([filterContext.filterBy[0], `${where} filter`]);
        }
        for (const [key, label] of candidates) {
          if (
            key === IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY ||
            key === IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY_PARENT
          ) {
            errors.push({
              id: `${label}: "${key}"`,
              message:
                `${label}: "${key}" is not valid when the interaction's ` +
                `trigger is "${primaryKey}" (there is no trigger element to ` +
                `target).`,
            });
          }
        }
      }
    }
  }
  return errors;
}

const isMouseMoveTimelineRole = (role: string | undefined): boolean =>
  role === TIMELINE_ROLE_NAMES.MOUSE_X || role === TIMELINE_ROLE_NAMES.MOUSE_Y;

const hasMouseMoveTrigger = (triggers: Triggers): boolean =>
  triggers.some(([key]) => key === IX3_WF_EXTENSION_KEYS.MOUSE_MOVE);

const hasScrollScrubTrigger = (triggers: Triggers): boolean =>
  triggers.some(
    ([key, config]) =>
      (key === IX3_WF_EXTENSION_KEYS.SCROLL ||
        key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB) &&
      config.scrollTriggerConfig?.scrub != null
  );

const hasSplitScrollScrubTrigger = (triggers: Triggers): boolean =>
  triggers.some(
    ([key, config]) =>
      key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB &&
      config.scrollTriggerConfig?.scrub != null
  );

/**
 * A percentage-based timeline (a `canvasDuration`, the fixed 0–100% canvas) is
 * only authorable on Mouse X/Y timelines for Mouse Move interactions, or on the
 * single scroll-scrub timeline for Scroll interactions. Cross-field, so it runs
 * when either triggers or timelines are (re)authored.
 */
export function findPercentTimelineError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    if (timeline.canvasDuration === undefined) {
      // `wf:scroll-scrub` is a percent canvas from creation: every Designer
      // path seeds the marker, and the editor reads a markerless scrub timeline
      // as a legacy seconds canvas, so one created without it would open and
      // run on the wrong canvas. Legacy `wf:scroll` timelines stay markerless.
      if (
        !timeline.triggerMetadata?.role &&
        hasSplitScrollScrubTrigger(triggers)
      ) {
        return (
          `Timeline "${timeline.id}" must set canvasDuration ` +
          `(${PERCENT_CANVAS_DURATION_S} is the 0-100% canvas) for ` +
          `${IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB} interactions.`
        );
      }
      continue;
    }

    const role = timeline.triggerMetadata?.role;
    const isMouseMovePercentTimeline =
      hasMouseMoveTrigger(triggers) && isMouseMoveTimelineRole(role);
    const isScrollScrubTimeline = hasScrollScrubTrigger(triggers) && !role;

    if (!isMouseMovePercentTimeline && !isScrollScrubTimeline) {
      return (
        `Timeline "${timeline.id}" sets a percentage canvasDuration, ` +
        `which is only valid on Mouse X/Y timelines for ` +
        `${IX3_WF_EXTENSION_KEYS.MOUSE_MOVE} interactions or on ` +
        `${IX3_WF_EXTENSION_KEYS.SCROLL} scrub interactions.`
      );
    }
  }
  return null;
}

export function findMouseFollowContextError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  const hasMouseMove = hasMouseMoveTrigger(triggers);

  for (const timeline of timelines) {
    const role = timeline.triggerMetadata?.role;
    const mouseFollowActions = timeline.actions.filter((action) =>
      Object.hasOwn(action.properties ?? {}, IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW)
    );

    if (mouseFollowActions.length === 0) continue;

    if (!hasMouseMove || !isMouseMoveTimelineRole(role)) {
      return (
        `Action type "${IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW}" in timeline ` +
        `"${timeline.id}" is only valid on Mouse X/Y timelines for ` +
        `${IX3_WF_EXTENSION_KEYS.MOUSE_MOVE} interactions.`
      );
    }

    // Only one Follow mouse action is allowed per timeline (UI enforces this,
    // plan R6: per timeline to match UI copy).
    if (mouseFollowActions.length > 1) {
      return (
        `Timeline "${timeline.id}" has ${mouseFollowActions.length} ` +
        `"${IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW}" actions; only one Follow ` +
        `mouse is allowed per timeline.`
      );
    }

    // Panel/lifecycle keeps followMode aligned with the timeline role; agent
    // upserts do not. x-only on mouseY (and y-only on mouseX) makes the
    // runtime skip the action. Absent / "full" stay valid for legacy.
    const followMode =
      mouseFollowActions[0]?.properties?.[IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW]
        ?.followMode;
    if (followMode === 'y-only' && role === TIMELINE_ROLE_NAMES.MOUSE_X) {
      return (
        `Timeline "${timeline.id}" action "${mouseFollowActions[0]!.id}" ` +
        `"${IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW}" followMode "y-only" conflicts ` +
        `with role "mouseX"; use "full", "x-only", or omit followMode.`
      );
    }
    if (followMode === 'x-only' && role === TIMELINE_ROLE_NAMES.MOUSE_Y) {
      return (
        `Timeline "${timeline.id}" action "${mouseFollowActions[0]!.id}" ` +
        `"${IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW}" followMode "x-only" conflicts ` +
        `with role "mouseY"; use "full", "y-only", or omit followMode.`
      );
    }
  }
  return null;
}

/**
 * Trigger roles for interactions that drive multiple timelines.
 *
 * Derived from the shared role definitions so new roles are picked up
 * automatically. Hover is handled separately because it is only
 * conditionally multi-timeline.
 */
export const MULTI_TIMELINE_TRIGGER_ROLES: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map<string, ReadonlySet<string>>([
  [
    IX3_WF_EXTENSION_KEYS.MOUSE_MOVE,
    new Set(Object.values(MOUSE_MOVE_TIMELINE_ROLES).map(({role}) => role)),
  ],
  [
    IX3_WF_EXTENSION_KEYS.NAVBAR,
    new Set(Object.values(COMPONENT_TIMELINE_ROLES).map(({role}) => role)),
  ],
  [
    IX3_WF_EXTENSION_KEYS.DROPDOWN,
    new Set(Object.values(COMPONENT_TIMELINE_ROLES).map(({role}) => role)),
  ],
  // Every entry, as in `ROLE_ROUTED_NO_PLAYBACK_KEYS`.
  ...COMPONENT_MANIFEST.map((entry): [string, ReadonlySet<string>] => [
    entry.trigger.key,
    new Set(componentRoles(entry)),
  ]),
]);

/**
 * Valid timeline roles for hover when `pluginConfig.multiTimeline` is enabled.
 */
const HOVER_MULTI_TIMELINE_ROLES: ReadonlySet<string> = new Set(
  Object.values(HOVER_TIMELINE_ROLES).map(({role}) => role)
);

/**
 * Returns the valid timeline roles for a trigger, or `undefined` if the
 * trigger doesn't route timelines by role.
 *
 * Hover is the only conditional case: roles are only required when the
 * persisted `pluginConfig.multiTimeline` flag is `true`.
 */
export function getMultiTimelineRoles(
  triggerKey: string,
  config: Triggers[number][1]
): ReadonlySet<string> | undefined {
  if (triggerKey === IX3_WF_EXTENSION_KEYS.HOVER) {
    const pluginConfig = config.pluginConfig as
      | {multiTimeline?: unknown}
      | undefined;
    return pluginConfig?.multiTimeline === true
      ? HOVER_MULTI_TIMELINE_ROLES
      : undefined;
  }
  return MULTI_TIMELINE_TRIGGER_ROLES.get(triggerKey);
}

/**
 * The roles a trigger key can own WHEN role-routing, independent of whether the
 * config currently declares it. The `config`-taking variant above answers "is
 * this trigger in role mode right now", which is the wrong question for a caller
 * deciding whether to PUT it in role mode: the store promotes a Hover to
 * `multiTimeline: true` when it joins an interaction that already carries role
 * timelines, and needs the key's role contract to check those persisted roles
 * are ones this key actually emits. Undefined for keys that never route by role.
 */
export function timelineRolesForTriggerKey(
  triggerKey: string
): ReadonlySet<string> | undefined {
  if (triggerKey === IX3_WF_EXTENSION_KEYS.HOVER) {
    return HOVER_MULTI_TIMELINE_ROLES;
  }
  return MULTI_TIMELINE_TRIGGER_ROLES.get(triggerKey);
}

/**
 * Whether a `pluginConfig` marks its trigger as a NEW-MODEL role-group owner —
 * one that CAN route by role, whichever way the flag currently points. A boolean
 * discriminator, not merely a defined one: `buildHover` only takes the new-model
 * runtime path for `true`/`false`, so a JSON `null` from saved/imported data
 * stays on the legacy path, and treating it as new-model would let the group
 * flow write new-model data the runtime still reads as legacy count fields.
 *
 * THE definition — the store's role-owner resolution, this file's role
 * invariant, and the Designer's `isNewModelGroupTrigger` all have to agree for
 * the routed-role exemption to hold, so they share this one rather than each
 * keeping a copy (the store and Designer adopt it in #121905).
 */
export function isNewModelRoleOwner(pluginConfig: unknown): boolean {
  return (
    typeof pluginConfig === 'object' &&
    pluginConfig !== null &&
    typeof (pluginConfig as {multiTimeline?: unknown}).multiTimeline ===
      'boolean'
  );
}

/**
 * Order-independent stringification, so a signature built from a trigger's own
 * CONTENT is stable no matter how the JSON was serialized. Only the shapes
 * `TriggerConfig` can hold — JSON objects, arrays, primitives — so recursion
 * terminates; `undefined` members are dropped the way `JSON.stringify` drops
 * them, since an absent field and an explicitly-undefined one are the same
 * persisted trigger.
 */
function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, member]) => member !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries
      .map(
        ([key, member]) => `${JSON.stringify(key)}:${stableStringify(member)}`
      )
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * Identity of the OFFENDING TRIGGER INSTANCE, for grandfathering an offense the
 * baseline already carried. Its own content, not its position: trigger order is
 * authored data, so any positional scheme — the absolute array index, or an
 * ordinal among triggers of the same key — moves an UNCHANGED offense to a new
 * signature the moment anything is inserted, deleted, or reordered ahead of it,
 * and newly rejects an update that never touched it. The bare extension key is
 * too coarse in the other direction: multiple Hovers are legal, so a baseline
 * offense on Hover A would grandfather a NEWLY authored one on Hover B while A
 * was being repaired.
 *
 * Content closes both. The allowance is a COUNTED map, so N identical baseline
 * offenders admit exactly N identical ones in the update and no more — add a
 * second byte-identical offending Hover and the second spend finds the allowance
 * empty. Two triggers that serialize the same are, by construction, not
 * distinguishable by anything this validator could report on, and swapping which
 * array slot holds one leaves the offense multiset untouched.
 *
 * The cost, deliberately accepted: editing the OFFENDING trigger itself changes
 * its identity and forfeits the allowance, so a DE update that alters that
 * trigger has to repair the offense too. That is the trigger the offense is
 * about — the grandfathering exists to keep UNRELATED edits (an action-only
 * rewrite, or a change to a different trigger) from failing on persisted data
 * they never touched, and those all echo the offender verbatim.
 *
 * Target is part of that content: a trigger's target picks which elements
 * receive its behavior, so moving the byte-identical key/config to a
 * different target changes who is affected and is not an unchanged persisted
 * offense — omitting it would let that move consume the old target's
 * allowance and silently pass the new one.
 */
function triggerIdentity(
  triggerKey: string,
  config: unknown,
  target: Triggers[number][2]
): string {
  return `${triggerKey}\0${stableTriggerTargetKey(target)}\0${stableStringify(config)}`;
}

/**
 * Every downgraded-trigger offense the role check would report, keyed so an
 * update can grandfather one that already existed unchanged. Split out of
 * {@link findTimelineRoleError}'s first branch only — the other role checks
 * predate this validator and keep their unconditional behavior.
 */
function collectDowngradedRoleOffenses(
  triggers: Triggers,
  timelines: readonly Timeline[]
): {signature: string; message: string}[] {
  // Every role SOME trigger in this interaction still routes. The check below
  // is per trigger, but for a trigger that CANNOT express role mode the
  // question it asks — "is this role left undriven?" — is per interaction: the
  // store deliberately parks a LEGACY Hover (no boolean discriminator, so it
  // can never be promoted) next to the role owner, and the owner beside it is
  // still routing both rows. Judging that sibling alone would reject a shape
  // the Designer authors on purpose, and since this invariant runs on DE/API
  // create and update, the host would refuse to touch an interaction the
  // Designer can keep editing.
  const routedRoles = new Set<string>();
  for (const [triggerKey, config] of triggers) {
    for (const role of getMultiTimelineRoles(triggerKey, config) ?? []) {
      routedRoles.add(role);
    }
  }

  const offenses: {signature: string; message: string}[] = [];
  for (const [triggerKey, config, target] of triggers) {
    const identity = triggerIdentity(triggerKey, config, target);
    if (getMultiTimelineRoles(triggerKey, config)) continue;
    // Not role-routing RIGHT NOW, but the key can be: Hover derives that from
    // its own `pluginConfig.multiTimeline`, so a trigger-only DE/API update
    // can clear the flag while the mouseEnter/mouseLeave timelines it was
    // routing stay behind, and every other check skips the trigger. Nothing
    // else catches it — the timelines are untouched and individually valid —
    // and `buildHover` then emits role-less callbacks, so BOTH role rows play
    // on BOTH mouse events, which is the exact confusion roles exist to
    // prevent. Only this key's OWN contract counts: a single-group Hover
    // sitting beside a Navbar's `open`/`close` rows is legitimate.
    const ownRoles = timelineRolesForTriggerKey(triggerKey);
    if (!ownRoles) continue;
    // The interaction-wide exemption applies ONLY to a trigger that cannot
    // express role mode at all — a legacy Hover with no boolean
    // discriminator, which the store can never promote and deliberately parks
    // next to the role owner. An EXPLICIT `multiTimeline: false` is a
    // different animal: the store flips every new-model same-key trigger to
    // `true` when the roles are created (the store's `roleOwningTriggers`
    // loop, #121905), so a false one beside a role-routing sibling is only
    // reachable by a trigger-only DE/API update — and that downgraded Hover
    // emits role-LESS callbacks, so `StandardTriggerStrategy` falls back to
    // its undefined static assignment and plays BOTH role rows on EVERY mouse
    // event. A sibling still routing the roles doesn't make that trigger
    // harmless; it just hides it.
    const exemptWhenRouted = !isNewModelRoleOwner(config.pluginConfig);
    for (const timeline of timelines) {
      if (timeline.deleted) continue;
      const role = timeline.triggerMetadata?.role;
      if (
        role == null ||
        !ownRoles.has(role) ||
        (exemptWhenRouted && routedRoles.has(role))
      ) {
        continue;
      }
      offenses.push({
        signature: `${identity}:${timeline.id}:${role}`,
        message:
          `Timeline "${timeline.id}" has role "${role}", which trigger ` +
          `"${triggerKey}" only routes while it is in role mode. Keep the ` +
          `trigger role-routing or remove the role from its timelines.`,
      });
    }
  }
  return offenses;
}

/**
 * Validates that every timeline for a multi-timeline trigger has a unique,
 * registered role. Invalid role assignments prevent the runtime from routing
 * timelines correctly.
 *
 * An interaction can carry more than one role owner (a Popover next to a
 * multi-timeline Hover; see `assignableTimelineRoles`) and its timelines are one
 * shared list, so each timeline is checked against the UNION of the roles every
 * owner registers. Checking owner by owner would fail the Hover's timelines
 * against the Popover's roles, and the reverse.
 *
 * The downgraded-trigger half accepts a baseline for the same reason
 * {@link findStaleRoleOutcomeError} does. Deleting the role OWNER leaves a
 * legacy Hover beside the roles it can't route — an orphan state the Designer
 * itself produces (from #121905, `resolveRoleOwnerTrigger` finds no owner and
 * skips the collapse) and offers repair UI for. Nothing routes those roles any
 * more, so the interaction-wide exemption above can't save it, and without a
 * baseline this validator would reject every DE/API edit to a persisted
 * interaction the Designer can still open — including the incremental edits
 * that repair it.
 * Grandfathered by COUNT, like its neighbor, against signatures keyed by the
 * offending trigger's own content (see {@link triggerIdentity}): an insert,
 * delete, or reorder elsewhere keeps its allowance, while a SECOND downgraded
 * trigger is a distinct offense rather than a free ride on the first one's.
 */
export function findTimelineRoleError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  const downgraded = collectDowngradedRoleOffenses(triggers, timelines);
  if (downgraded.length > 0) {
    const allowances =
      baselineTriggers !== undefined
        ? collectDowngradedRoleOffenses(
            baselineTriggers,
            baselineTimelines ?? []
          ).reduce(
            (counts, offense) =>
              counts.set(
                offense.signature,
                (counts.get(offense.signature) ?? 0) + 1
              ),
            new Map<string, number>()
          )
        : null;
    for (const offense of downgraded) {
      const remaining = allowances?.get(offense.signature) ?? 0;
      if (allowances && remaining > 0) {
        allowances.set(offense.signature, remaining - 1);
        continue;
      }
      return offense.message;
    }
  }

  const owners = new Set<string>();
  for (const [triggerKey, config] of triggers) {
    if (getMultiTimelineRoles(triggerKey, config)) owners.add(triggerKey);
  }
  if (owners.size === 0) return null;

  const allowedRoles = assignableTimelineRoles(triggers);
  const expected = [...allowedRoles].join(', ');
  // Every owner shares the one timeline list and the one union, so a single
  // pass reaches the verdict each owner would; the message names them all
  // rather than an arbitrary one whose roles are only some of `expected`.
  const owned = [...owners].map((key) => `"${key}"`).join(' / ');
  const claimedRoles = new Set<string>();

  for (const timeline of timelines) {
    const role = timeline.triggerMetadata?.role;
    if (!role) {
      return (
        `Timeline "${timeline.id}" requires a triggerMetadata.role because ` +
        `trigger ${owned} drives multiple timelines and the runtime ` +
        `uses the role to tell them apart. Expected one of: ${expected}.`
      );
    }
    if (!allowedRoles.has(role)) {
      return (
        `Timeline "${timeline.id}" role "${role}" is not a role trigger ` +
        `${owned} registers. Expected one of: ${expected}.`
      );
    }
    if (claimedRoles.has(role)) {
      return (
        `Trigger ${owned} has more than one timeline with role ` +
        `"${role}"; each role drives a single timeline.`
      );
    }
    claimedRoles.add(role);
  }
  return null;
}

/**
 * An overlay trigger (Popover, Modal) together with the roles its interaction
 * must keep and the targets that resolve from the overlays it names. The two
 * share the role-set and ownership rules below; only these facts differ.
 */
export type OverlayTriggerOwner = {
  triggerKey: string;
  roles: readonly string[];
  boundTargetKeys: ReadonlySet<string>;
  /** The bound target whose stored path names an instance, if there is one. */
  elementKey?: string;
  noun: string;
  /** Whether the Designer, code-views lane, DE and MCP may create it yet. */
  authoring: boolean;
};

/**
 * An owner for each manifest component that binds instances and holds its
 * state changes (Popover, Modal), in manifest order.
 */
const COMPONENT_OWNERS: ReadonlyArray<{
  owner: OverlayTriggerOwner;
  requiresFullRoleSet: boolean;
}> = COMPONENT_MANIFEST.filter(
  ({binding, protocol}) =>
    binding === 'instance' && protocol === 'held-transition'
).map((entry) => ({
  owner: {
    triggerKey: entry.trigger.key,
    roles: componentRoles(entry),
    boundTargetKeys: new Set(componentPartKeys(entry)),
    elementKey: entry.parts.find(({kind}) => kind === 'element')?.key,
    noun: entry.noun.singular,
    authoring: entry.authoring,
  },
  requiresFullRoleSet: entry.trigger.requiresFullRoleSet,
}));

/**
 * Each manifest component, in manifest order: every trigger whose bound targets
 * resolve from the instances it names. Hosts that clean up or vet those
 * targets read this list instead of naming `wf:popover`.
 */
export const INSTANCE_TRIGGER_OWNERS: readonly OverlayTriggerOwner[] =
  COMPONENT_OWNERS.map(({owner}) => owner);

/** The {@link INSTANCE_TRIGGER_OWNERS} that authoring paths may create. */
export const AUTHORING_INSTANCE_TRIGGER_OWNERS: readonly OverlayTriggerOwner[] =
  INSTANCE_TRIGGER_OWNERS.filter(({authoring}) => authoring);

function findIncompleteOverlayRoleSetError(
  owner: OverlayTriggerOwner,
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  if (!triggers.some(([key]) => key === owner.triggerKey)) {
    return null;
  }
  const claimed = new Set(
    timelines.map((timeline) => timeline.triggerMetadata?.role)
  );
  const missing = owner.roles.filter((role) => !claimed.has(role));
  if (missing.length === 0) return null;
  return (
    `Trigger "${owner.triggerKey}" needs a timeline for each ` +
    `of its roles; missing: ${missing.join(', ')}.`
  );
}

function findOverlayTargetOwnerError(
  owner: OverlayTriggerOwner,
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  if (triggers.some(([key]) => key === owner.triggerKey)) {
    return null;
  }
  const requirement =
    `requires the interaction to have a "${owner.triggerKey}" ` +
    `trigger; ${owner.noun} targets resolve from the ${owner.noun}s that ` +
    'trigger names.';
  for (const [key, , target] of triggers) {
    if (target && owner.boundTargetKeys.has(target[0])) {
      return `Trigger "${key}" target "${target[0]}" ${requirement}`;
    }
  }
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      for (const target of action.targets) {
        if (owner.boundTargetKeys.has(target[0])) {
          return (
            `timeline "${timeline.id}" action "${action.id}" target ` +
            `"${target[0]}" ${requirement}`
          );
        }
      }
    }
  }
  return null;
}

/**
 * A component interaction (a popover's) has to keep a timeline for each of
 * its roles, for each component whose trigger requires its full role set.
 *
 * `findTimelineRoleError` only vets the timelines that are present, which Mouse
 * move needs: its interval roles are optional. The Designer seeds a popover's
 * Open and Close together and cannot remove either, so a set missing one only
 * arrives by hand — and fails silently. A sibling trigger assigned to the
 * missing role routes to no timeline, so the role observer never hears it and
 * that trigger stops opening or closing the popover.
 */
export function findIncompleteComponentRoleSetError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  for (const {owner, requiresFullRoleSet} of COMPONENT_OWNERS) {
    if (!requiresFullRoleSet) continue;
    const error = findIncompleteOverlayRoleSetError(owner, triggers, timelines);
    if (error) return error;
  }
  return null;
}

/**
 * A component's parts resolve from the instances the interaction's own
 * component triggers name. `wf:popover-element` / `wf:popover-backdrop`
 * resolve from the popovers the interaction's own `wf:popover` triggers name
 * (runtime `resolvePopovers`), so without one they bind to nothing. The
 * Designer only offers them under a popover trigger; the code-views lane
 * applies the same rule per entry (`validateComponentActionTargetOwnership`).
 */
export function findComponentTargetOwnerError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  for (const {owner} of COMPONENT_OWNERS) {
    const error = findOverlayTargetOwnerError(owner, triggers, timelines);
    if (error) return error;
  }
  return null;
}

/**
 * All stale-role-outcome offenses in one interaction, each with a stable
 * `signature` so the update path can grandfather ones that already existed
 * unchanged (see {@link findStaleRoleOutcomeError}). The signature encodes the
 * offense identity — the offending trigger's own content
 * ({@link triggerIdentity}), the pinned/assigned timeline, and the role that
 * makes it stale — so an offense survives grandfathering only when the same
 * contradiction was present on the same trigger in the baseline. Keyed by bare
 * extension key, a baseline offense on Hover A would excuse a newly authored
 * one on Hover B; keyed by position, an insert ahead of A would reject A.
 */
function collectStaleRoleOutcomeOffenses(
  triggers: Triggers,
  timelines: readonly Timeline[]
): {signature: string; message: string}[] {
  const offenses: {signature: string; message: string}[] = [];
  for (const [key, config, target] of triggers) {
    const identity = triggerIdentity(key, config, target);
    const conditionalLogic = config.conditionalLogic;
    if (!conditionalLogic) continue;
    if (triggerRoutesByCallbackRole(key, config)) {
      // Callback-routed triggers skip the static branch below — their role is
      // emitted at runtime, ahead of the static fields — but a PINNED outcome
      // is still checkable, and dead in a way the static case isn't: the
      // callback hands `executeConditionalOutcome` only the emitted role's
      // timelines, so a pin on a role this trigger can never emit (a Hover
      // narrowed to `eventMode: 'leave'` pinned to the mouse-enter timeline)
      // intersects to an empty set and silently runs nothing. The panel
      // withholds these targets once IX3TriggerEditor's `conditionTimelines`
      // filters by the same predicate (#121905), so DE/API clients shouldn't be
      // able to author what it refuses.
      // A TARGETLESS outcome on a callback-routed trigger means "the timeline of
      // whichever role fired", so it is dead for the same reason the static
      // branch's targetless case is: if no live timeline carries a role this
      // trigger can EMIT, every callback resolves an empty set
      // (`getTimelineIdsForRole` matches `triggerMetadata.role` exactly, with no
      // play-all fallback) and the outcome always runs nothing. An enter-only
      // role-mode Hover whose only remaining row is `mouseLeave` is exactly
      // that. Checked once per trigger, not per outcome, because the question is
      // about the trigger's dispatch set.
      const targetlessOutcome = (
        [conditionalLogic.ifTrue, conditionalLogic.ifFalse] as const
      ).some(
        (outcome) =>
          outcome && outcome.control !== 'none' && !outcome.targetTimelineId
      );
      if (
        targetlessOutcome &&
        !timelines.some(
          (timeline) =>
            !timeline.deleted &&
            timeline.triggerMetadata?.role != null &&
            triggerEmitsCallbackRole(key, config, timeline.triggerMetadata.role)
        )
      ) {
        offenses.push({
          signature: `cbt:${identity}`,
          message:
            `Trigger "${key}" has a conditional outcome with no ` +
            `targetTimelineId, which only means "the matching role"; no live ` +
            `timeline carries a role this trigger emits, so the outcome would ` +
            `run nothing.`,
        });
      }
      for (const outcome of [
        conditionalLogic.ifTrue,
        conditionalLogic.ifFalse,
      ]) {
        if (!outcome || outcome.control === 'none') continue;
        const pinnedId = outcome.targetTimelineId;
        if (!pinnedId) continue;
        const pinnedRole = timelines.find(
          (timeline) => !timeline.deleted && timeline.id === pinnedId
        )?.triggerMetadata?.role;
        if (pinnedRole == null) continue;
        if (triggerEmitsCallbackRole(key, config, pinnedRole)) continue;
        offenses.push({
          signature: `cb:${identity}:${pinnedId}:${pinnedRole}`,
          message:
            `Trigger "${key}" has a conditional outcome pinned to timeline ` +
            `"${pinnedId}", whose role "${pinnedRole}" this trigger never ` +
            `emits, so the outcome would run nothing.`,
        });
      }
      continue;
    }
    const role = config.assignedTimelineRole;
    if (!role || routesByAssignedGroup(config)) continue;
    // A PINNED outcome dies the same way once its timeline leaves the assigned
    // role: static routing narrows dispatch to that role's timelines
    // (resolveAssignedTimelineIds) and `executeConditionalOutcome` intersects
    // the pin with them, so a timelines-only update that moves the pinned
    // timeline to another role leaves the outcome running nothing.
    for (const outcome of [conditionalLogic.ifTrue, conditionalLogic.ifFalse]) {
      if (!outcome || outcome.control === 'none') continue;
      const pinnedId = outcome.targetTimelineId;
      if (!pinnedId) continue;
      const pinnedTimeline = timelines.find(
        (timeline) => !timeline.deleted && timeline.id === pinnedId
      );
      if (!pinnedTimeline) continue;
      const pinnedRole = pinnedTimeline.triggerMetadata?.role;
      if (pinnedRole === role) continue;
      offenses.push({
        signature: `sp:${identity}:${pinnedId}:${pinnedRole ?? ''}`,
        message:
          `Trigger "${key}" has a conditional outcome pinned to timeline ` +
          `"${pinnedId}", which does not carry the trigger's assigned role ` +
          `"${role}", so the outcome would run nothing.`,
      });
    }
    const targetless = (
      [conditionalLogic.ifTrue, conditionalLogic.ifFalse] as const
    ).some(
      (outcome) =>
        outcome && outcome.control !== 'none' && !outcome.targetTimelineId
    );
    if (!targetless) continue;
    if (
      timelines.some(
        (timeline) =>
          !timeline.deleted && timeline.triggerMetadata?.role === role
      )
    ) {
      continue;
    }
    offenses.push({
      signature: `st:${identity}:${role}`,
      message:
        `Trigger "${key}" has a conditional outcome with no targetTimelineId, ` +
        `which only means "the matching role" while a timeline carries the ` +
        `assigned role "${role}"; no timeline does, so the outcome would run ` +
        `nothing.`,
    });
  }
  return offenses;
}

/**
 * The static half of `findConditionalOutcomeError`'s role-relative exemption,
 * re-checked against the live timelines. That guard runs on triggers alone, so
 * it can only ask whether the trigger ADVERTISES a role; here we can ask whether
 * anything carries it. A trigger pinned to `assignedTimelineRole: 'gone'` with
 * no timeline in that role resolves to `[]` at runtime
 * (resolveAssignedTimelineIds), so a targetless outcome accepted on the strength
 * of that role silently does nothing. `findTimelineRoleError` doesn't cover it —
 * it only validates roles for multi-timeline plugins — and the Designer
 * withholds "Matching action group" in exactly this case (from #121905,
 * IX3TriggerEditor `getAllowsRoleRelativeTarget` requires a matching timeline),
 * so DE/API clients should not be able to author what the panel refuses.
 *
 * The STATIC branch asks a different question per outcome shape: a
 * callback-routed trigger (Navbar/Dropdown, Hover in role mode) emits its role
 * at runtime ahead of the static fields, so its TARGETLESS exemption doesn't
 * depend on the assignment matching anything here, and its PINNED outcomes are
 * checked in the callback-routed branch below. A static trigger's pinned
 * outcomes are checked here: the pin must stay on a timeline carrying the
 * assigned role.
 *
 * On the update path, offenses the baseline already carried are grandfathered
 * by COUNT — see the body.
 */
export function findStaleRoleOutcomeError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  const offenses = collectStaleRoleOutcomeOffenses(triggers, timelines);
  if (offenses.length === 0) return null;
  // Update path only: this validator is new, and the contradictory shape it
  // rejects (an event-filtered callback trigger pinned to the opposite role, or
  // a targetless role outcome with no timeline in that role) was accepted by the
  // extension API before it existed. A read-modify-write that leaves the
  // trigger/pin/role untouched — e.g. an action-only `params.timelines` edit —
  // must not newly reject persisted data, so an offense whose signature already
  // existed in the baseline is grandfathered. New or changed contradictions
  // still reject. On create (no baseline) nothing is grandfathered, preserving
  // the original behavior exactly.
  //
  // Grandfathered by COUNT, not membership. The signature deliberately omits
  // the outcome branch (`ifTrue`/`ifFalse`) and the trigger's index — keying on
  // the trigger KEY is what lets a reorder, or an edit elsewhere in the same
  // trigger, keep its allowance — but that also makes two distinct
  // contradictions collide: duplicating a stale pin into the other branch, or
  // adding a SECOND Hover with the same key and the same invalid pin, produces
  // a signature the baseline already holds. A `Set` would wave the new one
  // through. Spending one allowance per baseline occurrence accepts exactly
  // what was already there and rejects the increment.
  const allowances =
    baselineTriggers !== undefined
      ? collectStaleRoleOutcomeOffenses(
          baselineTriggers,
          baselineTimelines ?? []
        ).reduce(
          (counts, offense) =>
            counts.set(
              offense.signature,
              (counts.get(offense.signature) ?? 0) + 1
            ),
          new Map<string, number>()
        )
      : null;
  for (const offense of offenses) {
    const remaining = allowances?.get(offense.signature) ?? 0;
    if (allowances && remaining > 0) {
      allowances.set(offense.signature, remaining - 1);
      continue;
    }
    return offense.message;
  }
  return null;
}

/**
 * With 2+ grouped timelines the panel locks STANDARD triggers to Play, and the
 * store writes `play` on both entry points (group-add and trigger-add). The
 * runtime reads the trigger's control for each grouped timeline and treats a
 * missing one as `restart` (IX3Engine.getEffectivePlaybackConfig), so only an
 * explicit `play` matches the panel. LOAD/SCROLL/CONTINUOUS ignore group
 * routing and keep their own control.
 */
export function findGroupedTriggerControlError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  let groupedCount = 0;
  for (const timeline of timelines) {
    if (!timeline.deleted && timeline.groupId != null) groupedCount++;
  }
  if (groupedCount < 2) return null;

  for (const [key, config] of triggers) {
    if ((config.controlType ?? 'standard') !== 'standard') continue;
    if (config.control === 'play') continue;
    return (
      `Trigger "${key}" must use control "play" when the interaction has ` +
      `multiple action groups; the Designer offers only Play there.`
    );
  }
  return null;
}

/**
 * Grouped STANDARD playback reads jump/speed from timeline `settings`
 * (`getEffectivePlaybackConfig`), so trigger-level jump/speed are inert.
 * Delay still fires. LOAD / SCROLL / CONTINUOUS keep trigger timing.
 *
 * Update path: grandfather unchanged values when the baseline was already
 * grouped. Reject create, ungrouped→grouped that keeps jump/speed, and
 * leftover settings jump/speed after a timeline loses its own `groupId`
 * and a STANDARD play-all trigger can reach it. Skip role
 * (`triggerMetadata`) timelines. Delay is out of scope here: leftover
 * `settings.delay` is migrated onto STANDARD triggers on collapse because
 * single-timeline playback ignores it.
 */
export function findGroupedTriggerJumpSpeedError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  const nextGrouped = hasLiveGroupedTimeline(timelines);
  const baselineGrouped =
    baselineTriggers !== undefined &&
    hasLiveGroupedTimeline(baselineTimelines ?? []);

  if (nextGrouped) {
    const hasUngroupedTimeline =
      hasUngroupedStandardPlaybackTimeline(timelines);
    const staleAllowance = new Map<string, number>();
    if (baselineGrouped) {
      for (const [key, config] of baselineTriggers) {
        if (!hasGroupedInertJumpOrSpeed(key, config)) continue;
        // Same skip as the next-side loop, so a play-all slot is not left
        // for a newly assigned trigger to claim.
        if (!routesByAssignedGroup(config) && hasUngroupedTimeline) {
          continue;
        }
        const slot = groupedJumpSpeedAllowanceKey(key, config);
        staleAllowance.set(slot, (staleAllowance.get(slot) ?? 0) + 1);
      }
    }

    for (const [key, config] of triggers) {
      if (!hasGroupedInertJumpOrSpeed(key, config)) continue;
      // Play-all STANDARD jump/speed is the source for an ungrouped
      // sibling timeline. Group-assigned triggers still read from
      // settings, so their trigger jump/speed stay inert.
      if (!routesByAssignedGroup(config) && hasUngroupedTimeline) {
        continue;
      }
      const slot = groupedJumpSpeedAllowanceKey(key, config);
      const remaining = staleAllowance.get(slot) ?? 0;
      if (remaining > 0) {
        staleAllowance.set(slot, remaining - 1);
        continue;
      }
      const fields =
        config.jump !== undefined && config.speed !== undefined
          ? 'jump and speed'
          : config.jump !== undefined
            ? 'jump'
            : 'speed';
      return (
        `Trigger "${key}" must not set ${fields} when a timeline has a ` +
        `groupId; grouped STANDARD playback reads those fields from the ` +
        `timeline's settings. Move ${fields} onto that timeline's settings, ` +
        `or omit ${fields} on the trigger.`
      );
    }
  }

  if (!baselineGrouped) return null;

  // Leftover settings jump/speed are only the wrong source when a STANDARD
  // play-all trigger drives the ungrouped timeline.
  if (!standardTriggerPlaysUngrouped(triggers)) return null;

  const baselineById = new Map(
    (baselineTimelines ?? []).map((timeline) => [timeline.id, timeline])
  );

  for (const timeline of timelines) {
    if (timeline.deleted) continue;
    // Role playback still reads jump/speed from settings.
    if (timeline.triggerMetadata != null) continue;
    const jump = timeline.settings?.jump;
    const speed = timeline.settings?.speed;
    if (jump === undefined && speed === undefined) continue;
    const baseline = baselineById.get(timeline.id);
    const lostGroupId =
      baseline != null &&
      !baseline.deleted &&
      baseline.groupId != null &&
      timeline.groupId == null;
    if (!lostGroupId) continue;
    const fields =
      jump !== undefined && speed !== undefined
        ? 'jump and speed'
        : jump !== undefined
          ? 'jump'
          : 'speed';
    return (
      `Timeline "${timeline.id}" must not keep settings.${fields} after ` +
      `groupId is removed; ungrouped STANDARD playback reads those fields ` +
      `from the trigger. Move ${fields} onto a standard trigger, or omit ` +
      `${fields} from the timeline's settings.`
    );
  }
  return null;
}

function hasLiveGroupedTimeline(timelines: readonly Timeline[]): boolean {
  return timelines.some(
    (timeline) => !timeline.deleted && timeline.groupId != null
  );
}

/** Ungrouped non-role timelines read jump/speed from a play-all STANDARD trigger. */
function hasUngroupedStandardPlaybackTimeline(
  timelines: readonly Timeline[]
): boolean {
  return timelines.some(
    (timeline) =>
      !timeline.deleted &&
      timeline.groupId == null &&
      timeline.triggerMetadata == null
  );
}

/**
 * Matches the runtime's truthy `assignedGroupId` check
 * (resolveAssignedTimelineIds): `''` falls through to role/play-all routing
 * the same as `undefined`, so it must not be treated as a concrete group
 * assignment by any static check either. `null` is the non-obvious member:
 * it returns `true` here not because it names a group, but because the
 * runtime short-circuits it to `[]` ("play nothing") before role/play-all
 * routing, so no play-all or role check applies to it.
 */
function routesByAssignedGroup(config: Triggers[number][1]): boolean {
  return config.assignedGroupId !== undefined && config.assignedGroupId !== '';
}

/** Mirrors store.ts `isStandardRoutedTrigger` without importing the store. */
function isStandardRoutedTriggerConfig(
  triggerKey: string,
  config: Triggers[number][1]
): boolean {
  if (expectedControlType(triggerKey) !== 'standard') return false;
  return (config.controlType ?? 'standard') === 'standard';
}

/** Play-all STANDARD triggers read jump/speed from the trigger once groupId is gone. */
function standardTriggerPlaysUngrouped(triggers: Triggers): boolean {
  return triggers.some(([key, config]) => {
    if (!isStandardRoutedTriggerConfig(key, config)) return false;
    return !routesByAssignedGroup(config);
  });
}

function hasGroupedInertJumpOrSpeed(
  triggerKey: string,
  config: Triggers[number][1]
): boolean {
  if (!isStandardRoutedTriggerConfig(triggerKey, config)) return false;
  return config.jump !== undefined || config.speed !== undefined;
}

function groupedJumpSpeedAllowanceKey(
  triggerKey: string,
  config: Triggers[number][1]
): string {
  return (
    `${triggerKey}\0${String(config.jump)}\0${String(config.speed)}\0` +
    `${String(config.assignedGroupId)}`
  );
}

/**
 * A non-null `assignedGroupId` with no matching timeline `groupId` is
 * skipped at runtime (`IX3Engine.resolveAssignedTimelineIds` /
 * `StandardTriggerStrategy`; `AnimationCoordinator`'s ease-flip scan
 * intentionally diverges). Allowed: `null` (store parks removed groups
 * there), a matching id, and role-routed triggers (Hover / Navbar /
 * Dropdown) whose timelines carry `triggerMetadata`.
 * Rejected: unmatched assignment on a discrete STANDARD trigger.
 * LOAD / SCROLL / CONTINUOUS ignore the field.
 *
 * Update path: grandfather an unchanged stored pairing so RMW of
 * already-broken data still commits. Key by trigger type, group id, and
 * canonical target. W7 slug → style-block id is the host's job on both
 * sides. When `update-interaction` omits timelines, the host passes the
 * stored ones.
 */
export function findOrphanedGroupAssignmentError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  const liveTimelines = timelines.filter((timeline) => !timeline.deleted);
  const baselineLive = baselineTimelines?.filter(
    (timeline) => !timeline.deleted
  );

  const staleAllowance = new Map<string, number>();
  if (baselineTriggers !== undefined) {
    for (const [key, config, target] of baselineTriggers) {
      if (!isStandardRoutedTriggerConfig(key, config)) continue;
      const assignedGroupId = config.assignedGroupId;
      if (assignedGroupId == null) continue;
      if (
        !isStaleGroupAssignment(
          assignedGroupId,
          baselineLive ?? [],
          key,
          config
        )
      ) {
        continue;
      }
      const slot = orphanedAssignmentAllowanceKey(key, assignedGroupId, target);
      staleAllowance.set(slot, (staleAllowance.get(slot) ?? 0) + 1);
    }
  }

  for (const [key, config, target] of triggers) {
    if (!isStandardRoutedTriggerConfig(key, config)) continue;
    const assignedGroupId = config.assignedGroupId;
    if (assignedGroupId == null) continue;
    if (!isStaleGroupAssignment(assignedGroupId, liveTimelines, key, config)) {
      continue;
    }
    const slot = orphanedAssignmentAllowanceKey(key, assignedGroupId, target);
    const remaining = staleAllowance.get(slot) ?? 0;
    if (remaining > 0) {
      staleAllowance.set(slot, remaining - 1);
      continue;
    }
    return (
      `Trigger "${key}" assignedGroupId "${assignedGroupId}" matches no ` +
      `timeline groupId, so the runtime will skip this trigger and the ` +
      `interaction will not run. Set groupId on the timeline this trigger ` +
      `should play, or omit assignedGroupId to play every timeline.`
    );
  }
  return null;
}

function orphanedAssignmentAllowanceKey(
  triggerKey: string,
  assignedGroupId: string,
  target: Triggers[number][2]
): string {
  return `${triggerKey}\0${assignedGroupId}\0${stableTriggerTargetKey(target)}`;
}

/**
 * Grandfather key for stored orphans. Canonicalize class/attribute wrap
 * and default filterContext, and sort object keys so a stored target that
 * host normalize rebuilt in a different property order still matches.
 * Style-block lookup is the host's job.
 */
function stableTriggerTargetKey(target: Triggers[number][2]): string {
  if (target === undefined) return '';
  try {
    return JSON.stringify(canonicalizeTriggerTarget(target), (_key, value) =>
      value != null && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(
            Object.entries(value).sort(([left], [right]) =>
              left < right ? -1 : 1
            )
          )
        : value
    );
  } catch {
    return String(target);
  }
}

function canonicalizeTriggerTarget(target: Triggers[number][2]): unknown {
  if (!Array.isArray(target)) return target;
  const [extensionKey, value, filterContext] = target;
  const canonicalValue = canonicalizeTargetValue(extensionKey, value);
  if (filterContext === undefined || isDefaultFilterContext(filterContext)) {
    return [extensionKey, canonicalValue];
  }
  return [
    extensionKey,
    canonicalValue,
    canonicalizeFilterContext(filterContext),
  ];
}

function canonicalizeTargetValue(
  extensionKey: unknown,
  value: unknown
): unknown {
  if (extensionKey === IX3_WF_EXTENSION_KEYS.CLASS) {
    return typeof value === 'string' ? [value] : value;
  }
  if (extensionKey === IX3_WF_EXTENSION_KEYS.ATTRIBUTE) {
    return canonicalizeAttributeSelector(value);
  }
  return value;
}

function canonicalizeAttributeSelector(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.startsWith('[')) return trimmed;
    return `[${trimmed}]`;
  }
  // `[base, scope]`: wrap the selector, not the DirectScope / SelectorScope.
  if (Array.isArray(value) && value.length === 2 && isScope(value[1])) {
    return [canonicalizeAttributeSelector(value[0]), value[1]];
  }
  return value;
}

function isDefaultFilterContext(filter: FilterContext): boolean {
  const filterBy = filter.filterBy;
  return (
    filter.relationship === RELATIONSHIP_TYPES.NONE &&
    (filter.firstMatchOnly ?? false) === false &&
    Array.isArray(filterBy) &&
    filterBy[0] === IX3_WF_EXTENSION_KEYS.CLASS &&
    Array.isArray(filterBy[1]) &&
    filterBy[1].length === 0
  );
}

function canonicalizeFilterContext(filter: FilterContext): FilterContext {
  const filterBy = filter.filterBy;
  const firstMatchOnly = filter.firstMatchOnly ?? false;
  if (!Array.isArray(filterBy) || filterBy.length < 2) {
    return {...filter, firstMatchOnly};
  }
  return {
    ...filter,
    firstMatchOnly,
    filterBy: [filterBy[0], canonicalizeTargetValue(filterBy[0], filterBy[1])],
  };
}

function isStaleGroupAssignment(
  assignedGroupId: string,
  timelines: readonly Timeline[],
  triggerKey: string,
  config: Triggers[number][1]
): boolean {
  if (timelines.some((timeline) => timeline.groupId === assignedGroupId)) {
    return false;
  }
  // Only role-routed triggers get the metadata fallback. A click orphan
  // still resolves to [] even if another timeline has leftover metadata.
  if (
    getMultiTimelineRoles(triggerKey, config) &&
    timelines.some((timeline) => timeline.triggerMetadata != null)
  ) {
    return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Value shapes (DSYS-7205) + feature-flag parity (DSYS-7208)
// ---------------------------------------------------------------------------

/**
 * Trigger types the Designer offers to every user today: the code-views lane's
 * always-on `SUPPORTED_TRIGGER_KEYS` plus any flag-gated trigger whose Statsig
 * gate has ramped to 100%. `wf:mouse-move` GA'd on 2026-07-21
 * (`ff-styl-2076-mouse-move-and-wf-element-triggers`), so it joined this set.
 * Until headless flag evaluation lands (DSYS-7208) this static list is
 * expanded by hand as each capability GAs; a drift test keeps it aligned with
 * code-views. navbar / dropdown stay gated (not yet GA).
 *
 * `wf:focus` / `wf:blur` / `wf:change` are runtime-only (no Designer
 * `addTriggerSchema`) and must stay out of this set so DE/MCP/code-views
 * cannot author them.
 */
export const DEFAULT_ON_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.CLICK,
  IX3_WF_EXTENSION_KEYS.HOVER,
  IX3_WF_EXTENSION_KEYS.LOAD,
  IX3_WF_EXTENSION_KEYS.SCROLL,
  IX3_WF_EXTENSION_KEYS.CUSTOM,
  IX3_WF_EXTENSION_KEYS.MOUSE_MOVE,
]);

/**
 * Action types the Designer offers to every user today. `wf:mouse-follow`
 * joined the default-on set with the 2026-07-21 Mouse Move GA. `wf:variable`,
 * `wf:rive`, and `wf:animate-rive` joined after their Designer flags shipped
 * at 100% (DES-7572; Statsig STALE_PROBABLY_LAUNCHED / STALE_ALL_TRUE).
 */
export const DEFAULT_ON_ACTION_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.TRANSFORM,
  IX3_WF_EXTENSION_KEYS.STYLE,
  IX3_WF_EXTENSION_KEYS.CLASS,
  IX3_WF_EXTENSION_KEYS.LOTTIE,
  IX3_WF_EXTENSION_KEYS.SPLINE,
  IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW,
  IX3_WF_EXTENSION_KEYS.VARIABLE,
  IX3_WF_EXTENSION_KEYS.RIVE,
  IX3_WF_EXTENSION_KEYS.ANIMATE_RIVE,
]);

/**
 * The Scroll split trigger keys, behind `ff-styl-ix3-scroll-split`. The schema
 * package can't read Statsig, so the host evaluates the flag and passes these
 * keys to {@link findTriggerValueFlagError} when it is on.
 */
export const SCROLL_SPLIT_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB,
  IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS,
  IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION,
]);

const FLAG_GATED_TRIGGER_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.NAVBAR,
  IX3_WF_EXTENSION_KEYS.DROPDOWN,
  ...COMPONENT_MANIFEST.map(({trigger}) => trigger.key),
  IX3_WF_EXTENSION_KEYS.EXIT_INTENT,
  IX3_WF_EXTENSION_KEYS.DURATION,
  ...SCROLL_SPLIT_TRIGGER_KEYS,
]);

/**
 * Reserved for action types still behind a real Designer gate. The schema
 * package can't read Statsig, so the host evaluates each flag and passes the
 * enabled keys to {@link findTimelineValueFlagError}.
 */
const FLAG_GATED_ACTION_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.CUSTOM_PROPERTY,
]);

export const isDefaultOnTriggerKey = (key: string): boolean =>
  DEFAULT_ON_TRIGGER_KEYS.has(key);

export const isDefaultOnActionKey = (key: string): boolean =>
  DEFAULT_ON_ACTION_KEYS.has(key);

// ---------------------------------------------------------------------------
// Designer authoring invariants — constants (DES-7371/7372/7374/7375)
// ---------------------------------------------------------------------------

/**
 * Animatable property names per action extension key — mirrors
 * `SUPPORTED_ACTION_PROPERTIES` in code-views/unified/lanes/interactions/
 * validation.ts (a second hand-maintained copy; the lane does not re-export
 * this constant). Drift-tested against ix3-wf-plugins/designer/src/actions/
 * index.ts `addProperty` ids in designerInvariantsDrift.test.ts.
 */
export const SUPPORTED_ACTION_PROPERTIES: Readonly<
  Record<string, readonly string[]>
> = {
  [IX3_WF_EXTENSION_KEYS.TRANSFORM]: TRANSFORM_ACTION_PROPERTIES,
  [IX3_WF_EXTENSION_KEYS.STYLE]: STYLE_ACTION_PROPERTIES,
  // Designer addCategory('class').addProperty('class'). Nested value shape is
  // validated in validateWfClassPropertyValue.
  [IX3_WF_EXTENSION_KEYS.CLASS]: ['class'],
  // Preset bags (no addProperty drift path) — hand-maintained from
  // designer presets + shared ActionConfig types. Nested value checks live in
  // validatePresetPropertyValue.
  [IX3_WF_EXTENSION_KEYS.LOTTIE]: ['manualDuration', 'lottie'],
  [IX3_WF_EXTENSION_KEYS.SPLINE]: ['objectId', 'animatingState', 'spline'],
  [IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW]: [
    'axis',
    'leaveBehavior',
    'onEnter',
    'anchor',
    'groupId',
    'syncedActionId',
    'followMode',
  ],
  // Preset bags from designer presets + shared ActionConfig types
  // (variable.tsx / rive.tsx / animate-rive.tsx). Nested checks in
  // validatePresetPropertyValue.
  [IX3_WF_EXTENSION_KEYS.VARIABLE]: ['variable'],
  [IX3_WF_EXTENSION_KEYS.RIVE]: ['rive'],
  [IX3_WF_EXTENSION_KEYS.ANIMATE_RIVE]: ['rive'],
};

/**
 * Properties for which the Designer offers any Random mode
 * (`supportsRandom: true` in designer/src/actions/index.ts).
 * Color props support the value-set wrapper only (`ix3-random-array`);
 * numeric unit-input props also support min/max (`ix3-random`) via
 * {@link ACTION_PROPERTIES_SUPPORTING_RANDOM_RANGE}.
 */
export const ACTION_PROPERTIES_SUPPORTING_RANDOM: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  [IX3_WF_EXTENSION_KEYS.TRANSFORM]: new Set([
    'opacity',
    'autoAlpha',
    'x',
    'y',
    'xPercent',
    'yPercent',
    'scale',
    'scaleX',
    'scaleY',
    'z',
    'skewX',
    'skewY',
    'rotation',
    'rotationX',
    'rotationY',
    'transformPerspective',
    'width',
    'height',
  ]),
  [IX3_WF_EXTENSION_KEYS.STYLE]: new Set([
    'backgroundColor',
    'borderColor',
    'color',
  ]),
};

/**
 * Properties for which the Designer offers Random min/max range mode
 * (`ix3-random`). Colors are excluded — the panel only authors color value
 * sets (`ix3-random-array`); see `toRandomValue` in propertyModes.ts.
 */
export const ACTION_PROPERTIES_SUPPORTING_RANDOM_RANGE: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  [IX3_WF_EXTENSION_KEYS.TRANSFORM]: new Set([
    'opacity',
    'autoAlpha',
    'x',
    'y',
    'xPercent',
    'yPercent',
    'scale',
    'scaleX',
    'scaleY',
    'z',
    'skewX',
    'skewY',
    'rotation',
    'rotationX',
    'rotationY',
    'transformPerspective',
    'width',
    'height',
  ]),
};

/**
 * Properties for which the Designer offers the Additive (+=) mode
 * (supportsAdditive: true). Colors do not support additive.
 */
export const ACTION_PROPERTIES_SUPPORTING_ADDITIVE: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  [IX3_WF_EXTENSION_KEYS.TRANSFORM]: new Set([
    'opacity',
    'autoAlpha',
    'x',
    'y',
    'xPercent',
    'yPercent',
    'scale',
    'scaleX',
    'scaleY',
    'z',
    'skewX',
    'skewY',
    'rotation',
    'rotationX',
    'rotationY',
    'transformPerspective',
    'width',
    'height',
  ]),
};

/**
 * Properties registered with `animatable: false` in designer/src/actions —
 * only valid inside a Set action (tt: 3). The Designer calls convertActionToSet
 * and emits tt=3 for every *new* action that contains one of these.
 *
 * Exception — `wf:class.class` and `wf:transform.display`: before Set actions
 * shipped (#95644 / STYL-2062), these could live on tt:0 mixed with animatable
 * props; the panel still keeps those properties editable until deselected.
 * Timeline RMW must grandfather that unchanged payload (see
 * findTimelineValueFlagError + allowLegacyNonAnimatable).
 */
export const NON_ANIMATABLE_ACTION_PROPERTIES: Readonly<
  Record<string, ReadonlySet<string>>
> = {
  [IX3_WF_EXTENSION_KEYS.TRANSFORM]: new Set(['display']),
  [IX3_WF_EXTENSION_KEYS.STYLE]: new Set([
    'zIndex',
    'position',
    'overflow',
    'pointerEvents',
  ]),
  [IX3_WF_EXTENSION_KEYS.CLASS]: new Set(['class']),
};

/**
 * Click-family triggers the Designer always requires a target element for
 * (the target picker is mandatory in IX3TriggerEditor). Load and custom
 * triggers are intentionally excluded: load doesn't bind to an element, and
 * custom already has its own body-target invariant.
 */
export const TRIGGER_REQUIRES_TARGET_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.CLICK,
  IX3_WF_EXTENSION_KEYS.HOVER,
  IX3_WF_EXTENSION_KEYS.FOCUS,
  IX3_WF_EXTENSION_KEYS.BLUR,
  IX3_WF_EXTENSION_KEYS.CHANGE,
  // Not click-family and never shown a picker, but both bind through
  // StandardTriggerStrategy, which resolves an empty element list from a
  // missing target and then never installs the handler — no error anywhere,
  // the interaction just does nothing. The Designer always writes
  // `['wf:body', '']` for them, so this only makes the code-authored path match
  // what the Designer already guarantees.
  IX3_WF_EXTENSION_KEYS.EXIT_INTENT,
  IX3_WF_EXTENSION_KEYS.DURATION,
]);

/**
 * Target extensionKeys offered only in trigger contexts (the Designer shows
 * them in the trigger target picker but never in the action target picker).
 * Inverse of ACTION_ONLY_TARGET_KEYS.
 */
export const TRIGGER_CONTEXT_ONLY_TARGET_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.BODY,
  IX3_WF_EXTENSION_KEYS.VIEWPORT,
]);

/**
 * The default filterContext the Designer writes for every element-scoped
 * target (targetUtils.ts / useInteractionHandlers.ts). Stamped by the host
 * build path when a caller omits the filterContext so Scope resolves correctly.
 * `relationship: none` with `filterBy: [wf:class, []]` is the Designer
 * placeholder; the class array stays empty until the user picks a filter.
 */
export const DEFAULT_FILTER_CONTEXT: Readonly<FilterContext> = {
  relationship: 'none',
  filterBy: [IX3_WF_EXTENSION_KEYS.CLASS, []],
  firstMatchOnly: false,
};

/**
 * Whether the conditions capability is default-on for all users. Static `false`
 * until `ff-ix3-conditions` GAs at 100% — same hand-maintained pattern as
 * DEFAULT_ON_TRIGGER_KEYS (navbar remains false until its GA). Flip to `true`
 * at GA; do not read a live flag here (see DSYS-7208).
 */
export const CONDITIONS_DEFAULT_ON = false;

/** Minimum number of values in a random-array (ix3-random-array) set. */
export const MIN_RANDOM_SET_VALUES = 2;

/** Maximum number of values in a random-array (ix3-random-array) set. */
export const MAX_RANDOM_SET_VALUES = 12;

/**
 * Per-extension-key target value shape, matching what the Designer emits. This
 * is *shape* validation for the DE/MCP surface only — it deliberately does NOT
 * tighten the shared `targetValueSchema` (whose z.unknown() feeds the runtime,
 * publish, and MPS paths) and does NOT enforce size/depth bounds (W6) or
 * resolve the wf:class slug-vs-style-block-id duality at this layer — the
 * DE/MCP host normalizes slugs to style-block ids on write (W7). Third-party
 * namespaces carry no shape constraint. Returns an error message, or null.
 */
const isDirectScope = (scope: unknown): boolean =>
  typeof scope === 'string' &&
  [
    TargetScope.ALL,
    TargetScope.CHILDREN,
    TargetScope.SIBLINGS,
    TargetScope.PARENT,
    TargetScope.NEXT,
    TargetScope.PREVIOUS,
  ].includes(scope as TargetScope);

const isSelectorScope = (scope: unknown): boolean =>
  Array.isArray(scope) &&
  scope.length === 2 &&
  [
    TargetScope.DESCENDANTS,
    TargetScope.ANCESTORS,
    TargetScope.FIRST_DESCENDANT,
    TargetScope.FIRST_ANCESTOR,
  ].includes(scope[0] as TargetScope) &&
  typeof scope[1] === 'string' &&
  scope[1].length > 0;

const isScope = (scope: unknown): boolean =>
  isDirectScope(scope) || isSelectorScope(scope);

/** Shared with W7 host normalize — keep DirectScope / SelectorScope in one place. */
export {isDirectScope, isSelectorScope, isScope};

const unwrapScopedValue = (
  value: unknown
): {base: unknown; error: string | null} => {
  if (!Array.isArray(value) || value.length !== 2) {
    return {base: value, error: null};
  }
  const scope = value[1];
  if (isScope(scope)) {
    return {base: value[0], error: null};
  }
  if (Array.isArray(value[0])) {
    return {
      base: value[0],
      error: 'target scope must be a valid DirectScope or SelectorScope.',
    };
  }
  return {base: value, error: null};
};

/**
 * A target value carries a Scope wrapper when it is a `[base, scope]` 2-tuple —
 * either a recognized DirectScope/SelectorScope in the second slot, or a base
 * wrapped in an array (the Designer's scoped-class shape `[[ids], scope]`).
 * Mirrors `unwrapScopedValue`'s scoped detection.
 */
const hasScopeWrapper = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length === 2 &&
  (isScope(value[1]) || Array.isArray(value[0]));

/**
 * Class-only Scope detection. Designer-native scoped class is `[[ids], scope]`
 * (`Array.isArray(value[0])`); SelectorScope in the second slot is also Scope.
 * A flat `[string, DirectScope]` is *not* treated as Scope here: without the
 * style store that form is ambiguous with a two-id array whose second id
 * collides with a DirectScope token (e.g. `['btn', 'parent']`). W7 normalize
 * rewrites real scopes to the nested form, which this still rejects (#116556).
 */
const hasClassScopeWrapper = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length === 2 &&
  (Array.isArray(value[0]) || isSelectorScope(value[1]));

/**
 * DirectScope / SelectorScope is not authorable in the native Designer: the
 * Scope dropdown is unreachable there (create always stamps a none-filter
 * 3-tuple and the panel hides Scope for any stamped target), so the DE/MCP API
 * must not accept it either. Callers narrow to a matching set via Filter.
 */
const scopeUnsupportedError = (extensionKey: string): string =>
  `"${extensionKey}" does not support Scope; the Designer narrows targets ` +
  `through Filter, so DirectScope / SelectorScope cannot be created through ` +
  `the API.`;

/** The manifest component and part for each part key. */
const COMPONENT_PART_OWNERS: ReadonlyMap<
  string,
  {entry: ComponentManifestEntry; part: ComponentPart}
> = new Map(
  COMPONENT_MANIFEST.flatMap((entry) =>
    entry.parts.map((part) => [part.key, {entry, part}] as const)
  )
);

/**
 * A manifest component's part: a leaf target resolved from the interaction's
 * own component triggers, storing an optional [componentId, elementId]
 * fallback path (a modal's backdrop is the dialog's own `::backdrop`, so the
 * dialog's path addresses both). That path is what the runtime falls back to
 * for a trigger with no instance in context (an invoker Click, a Page load, an
 * Exit intent). Empty is legal while the component trigger is unset: a
 * component interaction seeded from the add-interaction menu carries its
 * actions before any instance is picked, and assigning the trigger backfills
 * it. Leaf target: no Scope.
 */
function validateComponentPartValue(
  extensionKey: string,
  value: unknown,
  {entry: {noun}, part}: {entry: ComponentManifestEntry; part: ComponentPart}
): string | null {
  if (part.storesPath === false) {
    // Stores nothing: Popover's backdrop is injected beside the popover and has
    // no authored id, so the runtime resolves it from the firing trigger or from
    // the interaction's own popover triggers. A stored path would orphan as
    // soon as the popover trigger was retargeted or deleted.
    //
    // The [componentId, elementId] pair is still accepted, because data written
    // before the target went path-free carries one and the resolver still reads
    // it as a last resort. Leaf target: no Scope either way.
    if (Array.isArray(value) && value.length === 2 && isScope(value[1])) {
      return (
        `"${extensionKey}" does not support Scope; it addresses the ` +
        `interaction's ${noun.plural}, not an authored element.`
      );
    }
    if (value === '' || value == null) return null;
    return Array.isArray(value) &&
      value.length === 2 &&
      value.every((entry) => typeof entry === 'string' && entry.length > 0)
      ? null
      : `"${extensionKey}" value must be empty; it is resolved from the ` +
          `interaction's ${noun.plural} at runtime.`;
  }
  if (Array.isArray(value) && value.length === 2 && isScope(value[1])) {
    return (
      `"${extensionKey}" does not support Scope. Use ` +
      `[componentId, elementId] for the ${noun.singular}.`
    );
  }
  if (value === '' || value == null) return null;
  return Array.isArray(value) &&
    value.length === 2 &&
    value.every((entry) => typeof entry === 'string' && entry.length > 0)
    ? null
    : `"${extensionKey}" value must be [componentId, elementId] for the ` +
        `${noun.singular}, or empty while its ${noun.singular} trigger is unset.`;
}

export function validateTargetValue(
  extensionKey: string,
  value: unknown
): string | null {
  const component = COMPONENT_PART_OWNERS.get(extensionKey);
  if (component) {
    return validateComponentPartValue(extensionKey, value, component);
  }
  switch (extensionKey) {
    case IX3_WF_EXTENSION_KEYS.BODY:
    case IX3_WF_EXTENSION_KEYS.VIEWPORT:
      return value === '' ? null : `"${extensionKey}" takes no value; use "".`;
    case IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY: {
      // Designer leaf target (hideFilterUIWhenBase): no DirectScope/SelectorScope.
      if (Array.isArray(value) && value.length === 2 && isScope(value[1])) {
        return (
          `"${extensionKey}" does not support Scope; the Designer hides ` +
          `Scope UI for this target type. Use "".`
        );
      }
      return value === '' ? null : `"${extensionKey}" takes no value; use "".`;
    }
    case IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY_PARENT: {
      // Legal as filterBy only (base rejected in findAuthoredTargetError).
      // Scope shape still validated when present on filterBy values.
      const {base, error: scopeError} = unwrapScopedValue(value);
      if (scopeError) return scopeError;
      return base === '' ? null : `"${extensionKey}" takes no value; use "".`;
    }
    case IX3_WF_EXTENSION_KEYS.ANY_ELEMENT:
      return value === '*' ? null : `"${extensionKey}" value must be "*".`;
    case IX3_WF_EXTENSION_KEYS.CLASS: {
      if (hasClassScopeWrapper(value))
        return scopeUnsupportedError(extensionKey);
      // Slug string or array of style block id strings. Host W7 normalizes
      // slugs to style-block id arrays before persist; this shape check stays
      // dual so the boundary can accept either input form.
      if (typeof value === 'string') {
        return value.length > 0
          ? null
          : 'wf:class value must be a non-empty class slug.';
      }
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((entry) => typeof entry === 'string' && entry.length > 0)
      ) {
        return null;
      }
      return (
        'wf:class value must be a non-empty class slug string or a non-empty ' +
        'array of style block id strings.'
      );
    }
    case IX3_WF_EXTENSION_KEYS.INST: {
      // Designer leaf target (hideFilterUIWhenBase): no DirectScope/SelectorScope.
      if (Array.isArray(value) && value.length === 2 && isScope(value[1])) {
        return (
          `"${extensionKey}" does not support Scope; the Designer hides ` +
          `Scope UI for this target type. Use [componentId, elementId].`
        );
      }
      return Array.isArray(value) &&
        value.length === 2 &&
        value.every((entry) => typeof entry === 'string' && entry.length > 0)
        ? null
        : 'wf:inst value must be [componentId, elementId].';
    }
    case IX3_WF_EXTENSION_KEYS.ID:
    case IX3_WF_EXTENSION_KEYS.SELECTOR:
    case IX3_WF_EXTENSION_KEYS.ATTRIBUTE: {
      if (hasScopeWrapper(value)) return scopeUnsupportedError(extensionKey);
      if (typeof value !== 'string' || value.length === 0) {
        return `"${extensionKey}" value must be a non-empty string.`;
      }
      if (extensionKey === IX3_WF_EXTENSION_KEYS.ID && value.startsWith('#')) {
        return 'wf:id value must be an element id without a leading "#".';
      }
      return null;
    }
    default:
      // An unrecognized key in the wf: namespace is a caller typo: the cases
      // above are exactly the target types both plugin registries expose, and
      // the runtime has no resolver for anything else, so the target would
      // commit and then match nothing. Other namespaces stay unconstrained.
      return extensionKey.startsWith('wf:')
        ? `"${extensionKey}" is not a known Webflow target type.`
        : null;
  }
}

/**
 * Reject trigger types outside the Designer's default-on set. A flag-gated key
 * in `enabledGatedTriggerKeys` (its flag is on for this session) is accepted.
 */
export function findFlagGatedTriggerError(
  triggers: Triggers,
  enabledGatedTriggerKeys?: ReadonlySet<string>
): string | null {
  for (const [key] of triggers) {
    if (
      !isDefaultOnTriggerKey(key) &&
      !(FLAG_GATED_TRIGGER_KEYS.has(key) && enabledGatedTriggerKeys?.has(key))
    ) {
      const reason = FLAG_GATED_TRIGGER_KEYS.has(key)
        ? 'it is gated behind a feature flag'
        : 'it is not a known default-on Designer trigger';
      return (
        `Trigger "${key}" is not in the Designer's default capability set ` +
        `(${reason}) and cannot be created through the API.`
      );
    }
  }
  return null;
}

/**
 * Reject action-property types outside the Designer's default-on set. A
 * flag-gated key in `enabledGatedActionKeys` (its flag is on for this session)
 * is accepted.
 *
 * `baselineTimelines` (update path only): a flag-gated group the edit left
 * structurally unchanged on the same timeline + action is kept even when its
 * flag is off, so a flag rollback does not strand an edit to a sibling action.
 * A new or changed group is still rejected.
 */
export function findFlagGatedActionError(
  timelines: readonly Timeline[],
  enabledGatedActionKeys?: ReadonlySet<string>,
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      for (const key of Object.keys(action.properties ?? {})) {
        if (
          !isDefaultOnActionKey(key) &&
          !(
            FLAG_GATED_ACTION_KEYS.has(key) && enabledGatedActionKeys?.has(key)
          ) &&
          !(
            FLAG_GATED_ACTION_KEYS.has(key) &&
            isUnchangedActionPropertyGroup(
              action,
              key,
              findTimelineActionById(baselineTimelines, timeline.id, action.id)
            )
          )
        ) {
          const reason = FLAG_GATED_ACTION_KEYS.has(key)
            ? 'it is gated behind a feature flag'
            : 'it is not a known default-on Designer action type';
          return (
            `Action type "${key}" in timeline "${timeline.id}" is not in the ` +
            `Designer's default capability set (${reason}) and cannot be ` +
            `created through the API.`
          );
        }
      }
    }
  }
  return null;
}

/**
 * Action/trigger base target types whose Designer leaf UI hides Filter
 * (`hideFilterUIWhenBase: true` for Trigger element / Element / Popover /
 * Backdrop). Active filterContext is rejected; stamped `relationship: 'none'`
 * is allowed.
 * Exported for designerInvariantsDrift.test.ts (Designer registry mirror).
 */
export const LEAF_NO_FILTER_TARGET_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY,
  IX3_WF_EXTENSION_KEYS.INST,
  // Every entry, as in `ROLE_ROUTED_NO_PLAYBACK_KEYS`: a validation rule.
  ...COMPONENT_MANIFEST.flatMap(componentPartKeys),
]);

/**
 * Disallowed-key + value-shape policy for one target tuple, including an
 * active nested `filterBy`. Scroll pin/endTrigger/scroller reuse this via
 * {@link findTriggerValueFlagError}. Pass `onTriggerTarget` for primary and
 * scroll-config tuples so action-only Filter-by keys (wf:trigger-only[-parent])
 * are rejected the same way on every trigger-side target — not just the
 * primary (see findTriggerTargetContextError for the parallel primary walk).
 */
const findAuthoredTargetError = (
  target: TargetTuple,
  where: string,
  options?: {asFilterBy?: boolean; onTriggerTarget?: boolean}
): string | null => {
  const asFilterBy = options?.asFilterBy === true;
  const onTriggerTarget = options?.onTriggerTarget === true;
  // Action-only keys on a trigger-side target (primary or scroll sub-target)
  // bind against no triggerElement and never fire. Apply before the
  // filter-by-only Parent-of-trigger carve-out so a bare trigger-only-parent
  // base on a scroll field is rejected as a trigger target, not redirected.
  if (onTriggerTarget && ACTION_ONLY_TARGET_KEYS.has(target[0])) {
    return asFilterBy
      ? `${where}: "${target[0]}" is only valid on timeline actions, not as a ` +
          `Filter-by on a trigger target (there is no trigger element to ` +
          `filter against at bind time).`
      : `${where}: "${target[0]}" is only valid on timeline actions, ` +
          `not trigger targets.`;
  }
  // Parent of trigger is filter-by only — never a base Target type picker option.
  if (!asFilterBy && target[0] === IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY_PARENT) {
    return (
      `${where}: target type "${IX3_WF_EXTENSION_KEYS.TRIGGER_ONLY_PARENT}" ` +
      `is not offered as a base Target by the Designer; use it only as ` +
      `filterBy (Parent of trigger).`
    );
  }
  const disallowedError = findDisallowedTargetKeyError(target, where, {
    asFilterBy,
  });
  if (disallowedError) return disallowedError;
  // Value presence is enforced separately; only shape-check present values.
  if (target[1] !== undefined) {
    const shapeError = validateTargetValue(target[0], target[1]);
    if (shapeError) return `${where}: ${shapeError}`;
  }
  const filterContext = target[2];
  if (
    !asFilterBy &&
    LEAF_NO_FILTER_TARGET_KEYS.has(target[0]) &&
    filterContext !== undefined &&
    filterContext.relationship !== RELATIONSHIP_TYPES.NONE
  ) {
    return (
      `${where}: "${target[0]}" does not support Filter; the Designer hides ` +
      `Filter UI for this target type.`
    );
  }
  // Reject firstMatchOnly only with relationship "none". The Designer clears
  // the flag when switching to none, but preserves it when switching among
  // Filter relationships (including ones that hide the checkbox, e.g.
  // direct-parent-of). Accepting that sticky state keeps DE/MCP read-modify-
  // write compatible with native-authored targets until the editor clears it.
  if (
    filterContext !== undefined &&
    filterContext.firstMatchOnly === true &&
    filterContext.relationship === RELATIONSHIP_TYPES.NONE
  ) {
    return `${where}: firstMatchOnly cannot be true when relationship is none.`;
  }
  if (
    filterContext?.filterBy &&
    filterContext.relationship !== RELATIONSHIP_TYPES.NONE
  ) {
    return findAuthoredTargetError(filterContext.filterBy, `${where} filter`, {
      asFilterBy: true,
      onTriggerTarget,
    });
  }
  return null;
};

/** The `[wf:inst, '']` the element pickers write before an element is chosen. */
function isUnpickedInstPlaceholder(target: TargetTuple): boolean {
  return (
    target[0] === IX3_WF_EXTENSION_KEYS.INST &&
    target[1] === '' &&
    target[2] === undefined
  );
}

/**
 * Trigger-scoped flag parity + trigger-target value shapes. Run on create and
 * on update when triggers are replaced.
 */
export function findTriggerValueFlagError(
  triggers: Triggers,
  enabledGatedTriggerKeys?: ReadonlySet<string>
): string | null {
  const flagError = findFlagGatedTriggerError(
    triggers,
    enabledGatedTriggerKeys
  );
  if (flagError) return flagError;
  for (const [key, config, target] of triggers) {
    if (target) {
      const error = findAuthoredTargetError(target, `trigger "${key}" target`, {
        onTriggerTarget: true,
      });
      if (error) return error;
    }
    // scrollTriggerConfig carries three more target tuples (pin — in its
    // non-boolean form — endTrigger, scroller). Same policy as the primary
    // target so wf:id / malformed values / action-only Filter-by keys cannot
    // hide there.
    const scroll = config?.scrollTriggerConfig;
    if (scroll) {
      const scrollTargets: Array<[string, TargetTuple | undefined]> = [
        ['pin', typeof scroll.pin === 'boolean' ? undefined : scroll.pin],
        ['endTrigger', scroll.endTrigger],
        ['scroller', scroll.scroller],
      ];
      for (const [field, scrollTarget] of scrollTargets) {
        if (!scrollTarget) continue;
        // The Pin → Other element and Separate end target pickers persist
        // [wf:inst, ''] until an element is chosen (findScrollTriggerConfigError
        // accepts it for pin), so a native-authored interaction in that state
        // must round-trip through create/update rather than fail the value
        // check on the placeholder.
        if (
          (field === 'pin' || field === 'endTrigger') &&
          isUnpickedInstPlaceholder(scrollTarget)
        ) {
          continue;
        }
        const error = findAuthoredTargetError(
          scrollTarget,
          `trigger "${key}" scrollTriggerConfig.${field}`,
          {onTriggerTarget: true}
        );
        if (error) return error;
        // The split keys also take the trigger-target context rule the
        // primary target gets (`wf:viewport` is mouse-move only), so the DE/MCP
        // path and the code-views lane accept the same sub-tuples.
        if (
          key === IX3_WF_EXTENSION_KEYS.SCROLL_SCRUB ||
          key === IX3_WF_EXTENSION_KEYS.SCROLL_ACTIONS ||
          key === IX3_WF_EXTENSION_KEYS.SCROLL_DIRECTION
        ) {
          const contextError = findTriggerTargetContextError(key, scrollTarget);
          if (contextError) {
            return `trigger "${key}" scrollTriggerConfig.${field}: ${contextError}`;
          }
        }
      }
    }
  }
  return null;
}

/**
 * Timeline-scoped flag parity + action-target value shapes. Run on create and
 * on update when the timeline set is replaced.
 *
 * `baselineTimelines` (update path only): when an action already stored the
 * same `tt` + `wf:class.class` payload, skip the Set-only rejection for that
 * Class property so legacy pre-Set mixed actions remain RMW-editable (#95644).
 * New or mutated Class-on-non-Set writes still reject.
 *
 * `enabledGatedActionKeys`: the flag-gated action keys whose flag is on for
 * this session (see {@link findFlagGatedActionError}).
 */
export function findTimelineValueFlagError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[],
  enabledGatedActionKeys?: ReadonlySet<string>
): string | null {
  const flagError = findFlagGatedActionError(
    timelines,
    enabledGatedActionKeys,
    baselineTimelines
  );
  if (flagError) return flagError;
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const at = `timeline "${timeline.id}" action "${action.id}"`;
      for (const target of action.targets) {
        const error = findAuthoredTargetError(target, `${at} target`);
        if (error) return error;
      }
      const baselineAction = findTimelineActionById(
        baselineTimelines,
        timeline.id,
        action.id
      );
      for (const [extensionKey, props] of Object.entries(
        action.properties ?? {}
      )) {
        for (const [propertyName, value] of Object.entries(props ?? {})) {
          const propError = validateActionPropertyShape(
            extensionKey,
            propertyName,
            value,
            action.tt,
            {
              allowLegacyNonAnimatable: isUnchangedLegacyNonAnimatable(
                action,
                extensionKey,
                propertyName,
                value,
                baselineAction
              ),
              allowLegacySplineFlatChannel: isUnchangedLegacySplineFlatChannel(
                extensionKey,
                propertyName,
                value,
                baselineAction
              ),
            }
          );
          if (propError) {
            return `${at} property "${extensionKey}.${propertyName}": ${propError}`;
          }
        }
      }
    }
  }
  return null;
}

/**
 * The action carries a stored property group under `extensionKey`, and the
 * edit left it structurally unchanged.
 */
function isUnchangedActionPropertyGroup(
  action: TimelineAction,
  extensionKey: string,
  baselineAction: TimelineAction | undefined
): boolean {
  const baselineGroup = (
    baselineAction?.properties as Record<string, unknown> | undefined
  )?.[extensionKey];
  return (
    baselineGroup !== undefined &&
    structurallyEqualJson(
      (action.properties as Record<string, unknown> | undefined)?.[
        extensionKey
      ],
      baselineGroup
    )
  );
}

/** Look up a baseline timeline by id. */
function findTimelineById(
  timelines: readonly Timeline[] | undefined,
  timelineId: string
): Timeline | undefined {
  return timelines?.find((timeline) => timeline.id === timelineId);
}

/**
 * Look up a baseline action by timeline id + action id.
 * Action ids are timeline-scoped for grandfathering: a shared id across two
 * timelines must not borrow another timeline's Class/Display legacy payload.
 */
function findTimelineActionById(
  timelines: readonly Timeline[] | undefined,
  timelineId: string,
  actionId: string
): TimelineAction | undefined {
  if (!timelines) return undefined;
  for (const timeline of timelines) {
    if (timeline.id !== timelineId) continue;
    for (const action of timeline.actions) {
      if (action.id === actionId) return action;
    }
  }
  return undefined;
}

/**
 * Same timeline id + action id, same `tt` (including both omitted), and
 * structurally equal properties. Name / timing edits still count as
 * unchanged for tt inference and tween-slot grandfathering.
 *
 * A duplicated action id never qualifies: one stored row must not authorize
 * two submitted rows, or both keep an omitted `tt` and the copy animates
 * nothing (runtime defaults the omit to To, which has no From-only slot).
 */
function isUnchangedStoredAction(
  action: TimelineAction,
  timeline: Timeline,
  baselineTimelines: readonly Timeline[] | undefined
): boolean {
  if (baselineTimelines === undefined) return false;
  if (actionIdIsDuplicated(timeline.actions, action.id)) return false;
  const baselineAction = findTimelineActionById(
    baselineTimelines,
    timeline.id,
    action.id
  );
  return (
    baselineAction !== undefined &&
    baselineAction.tt === action.tt &&
    structurallyEqualJson(action.properties, baselineAction.properties)
  );
}

/**
 * Properties that may appear on non-Set (tt ≠ 3) actions only when they are
 * an unchanged echo of pre-Set Designer state (#95644). New invents still
 * require tt: 3. Used by validateActionPropertyShape and MCP softens.
 */
export function isLegacyNonAnimatableGrandfatherEligible(
  extensionKey: string,
  propertyName: string
): boolean {
  return (
    (extensionKey === IX3_WF_EXTENSION_KEYS.CLASS &&
      propertyName === 'class') ||
    (extensionKey === IX3_WF_EXTENSION_KEYS.TRANSFORM &&
      propertyName === 'display')
  );
}

/**
 * True when a non-animatable property on a non-Set action is an unchanged
 * echo of a stored legacy mixed action (Designer still returns these from
 * get / #95644). Covers `wf:class.class` and `wf:transform.display`.
 * Used by DE host RMW and the interactions filesystem lane.
 */
export function shouldAllowLegacyNonAnimatableOnUpdate(
  action: TimelineAction,
  extensionKey: string,
  propertyName: string,
  value: unknown,
  baselineAction: TimelineAction | undefined
): boolean {
  return isUnchangedLegacyNonAnimatable(
    action,
    extensionKey,
    propertyName,
    value,
    baselineAction
  );
}

/**
 * True when a pre-From/To Spline bag field can echo stored site data.
 * #95959 persisted `stateNames` at the `wf:spline` root and scalar channels
 * under `spline`. #96983 moved channels to `{from, to}` without migrating
 * those bags. Nested scalar channels are compared individually so a later
 * `{from, to}` edit of another channel does not reject the leftover scalar.
 * New invents still reject.
 */
export function shouldAllowLegacySplineFlatOnUpdate(
  extensionKey: string,
  propertyName: string,
  value: unknown,
  baselineAction: TimelineAction | undefined
): boolean {
  return isUnchangedLegacySplineFlatChannel(
    extensionKey,
    propertyName,
    value,
    baselineAction
  );
}

/**
 * True when Class or Display on a non-Set action is an unchanged echo of a
 * stored legacy mixed action (Designer still returns these from get).
 */
function isUnchangedLegacyNonAnimatable(
  action: TimelineAction,
  extensionKey: string,
  propertyName: string,
  value: unknown,
  baselineAction: TimelineAction | undefined
): boolean {
  if (
    baselineAction == null ||
    !isLegacyNonAnimatableGrandfatherEligible(extensionKey, propertyName)
  ) {
    return false;
  }
  const nextTt = action.tt ?? 0;
  const baselineTt = baselineAction.tt ?? 0;
  if (nextTt === 3 || nextTt !== baselineTt) return false;

  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.CLASS &&
    propertyName === 'class'
  ) {
    const baselineClass =
      baselineAction.properties?.[IX3_WF_EXTENSION_KEYS.CLASS]?.class;
    // Order-insensitive: Designer store may keep `{selectors, operation}` while
    // the filesystem lane materializes `{operation, selectors}`.
    return structurallyEqualJson(baselineClass, value);
  }

  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.TRANSFORM &&
    propertyName === 'display'
  ) {
    const baselineDisplay =
      baselineAction.properties?.[IX3_WF_EXTENSION_KEYS.TRANSFORM]?.display;
    return structurallyEqualJson(baselineDisplay, value);
  }

  return false;
}

function isSplineLegacyScalar(value: unknown): boolean {
  return typeof value === 'number' || typeof value === 'string';
}

/**
 * Channel values the #95959 writer stored (scalars) and the values #96983
 * writes for an edited channel (`{from?, to?}`). Untouched scalars stay in
 * place, so stored bags are often mixed.
 */
function isLegacyCompatibleSplineChannel(value: unknown): boolean {
  if (value === undefined) return true;
  if (isSplineLegacyScalar(value)) return true;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.keys(value as Record<string, unknown>).every(
    (field) => field === 'from' || field === 'to'
  );
}

/**
 * Nested `spline` bags that may appear on an unchanged echo: all-scalar
 * (#95959), all `{from, to}` (#96983), or mixed after a later panel edit
 * (untouched scalars + edited `{from, to}` channels). Unknown channel names
 * are not echo-eligible.
 */
function isLegacyCompatibleSplineNestedBag(value: unknown): boolean {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  for (const [key, channel] of Object.entries(
    value as Record<string, unknown>
  )) {
    if (!SPLINE_PROPERTY_KEYS.has(key)) return false;
    if (!isLegacyCompatibleSplineChannel(channel)) return false;
  }
  return true;
}

function isLegacySplineGrandfatherKey(
  propertyName: string,
  value: unknown
): boolean {
  if (propertyName === 'stateNames') return true;
  if (propertyName === 'spline') {
    return isLegacyCompatibleSplineNestedBag(value);
  }
  return SPLINE_PROPERTY_KEYS.has(propertyName);
}

/**
 * MCP/Page Automation input-boundary soften: let a stored Spline bag reach
 * the host so baseline echo can grandfather it. Invents still reject there.
 */
export function isLegacySplineGrandfatherEligible(
  extensionKey: string,
  propertyName: string,
  value: unknown
): boolean {
  return (
    extensionKey === IX3_WF_EXTENSION_KEYS.SPLINE &&
    isLegacySplineGrandfatherKey(propertyName, value)
  );
}

/**
 * Pre-From/To Spline bags (#95959) stored `stateNames` at the `wf:spline` root
 * and scalar channels inside `spline`. A leftover top-level channel key is
 * also treated as echo-eligible. Nested scalars must match the stored value
 * for that channel; `{from, to}` channels are not compared here.
 */
function isUnchangedLegacySplineFlatChannel(
  extensionKey: string,
  propertyName: string,
  value: unknown,
  baselineAction: TimelineAction | undefined
): boolean {
  if (
    baselineAction == null ||
    extensionKey !== IX3_WF_EXTENSION_KEYS.SPLINE ||
    !isLegacySplineGrandfatherKey(propertyName, value)
  ) {
    return false;
  }
  const baselineValue =
    baselineAction.properties?.[IX3_WF_EXTENSION_KEYS.SPLINE]?.[propertyName];
  if (baselineValue === undefined) return false;
  if (propertyName === 'spline') {
    return isUnchangedLegacySplineNestedChannels(value, baselineValue);
  }
  return structurallyEqualJson(baselineValue, value);
}

/**
 * Every leftover scalar in `next` must equal the stored scalar for that
 * channel. `{from, to}` channels may change; new or mutated scalars reject.
 */
function isUnchangedLegacySplineNestedChannels(
  next: unknown,
  baseline: unknown
): boolean {
  if (!isLegacyCompatibleSplineNestedBag(next)) return false;
  if (
    typeof baseline !== 'object' ||
    baseline === null ||
    Array.isArray(baseline)
  ) {
    return false;
  }
  const baselineBag = baseline as Record<string, unknown>;
  for (const [key, channel] of Object.entries(
    next as Record<string, unknown>
  )) {
    if (!isSplineLegacyScalar(channel)) continue;
    if (!isSplineLegacyScalar(baselineBag[key])) return false;
    if (!structurallyEqualJson(baselineBag[key], channel)) return false;
  }
  return true;
}

/**
 * Deep JSON equality that ignores object key insertion order (and nested
 * object order). Arrays stay order-sensitive. WFS materialize sorts keys
 * while the IX3 store keeps authored order, so grandfather compares must
 * use this instead of `JSON.stringify`.
 */
export function structurallyEqualJson(a: unknown, b: unknown): boolean {
  try {
    return stableJsonStringify(a) === stableJsonStringify(b);
  } catch {
    return false;
  }
}

function stableJsonStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableJsonStringify).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys
    .map((key) => `${JSON.stringify(key)}:${stableJsonStringify(obj[key])}`)
    .join(',')}}`;
}

// ---------------------------------------------------------------------------
// New guards: conditions capability, disallowed keys, property shapes, stamps
// ---------------------------------------------------------------------------

/**
 * Rejects `conditionalLogic` when conditions are not yet default-on for all
 * users. Static `CONDITIONS_DEFAULT_ON = false` until the feature GAs —
 * same W4 hand-maintained pattern as DEFAULT_ON_TRIGGER_KEYS (see plan R1).
 * Runs FIRST in the trigger invariant chain so "not offered" wins over deeper
 * outcome-shape messages (wired in findTriggerInvariantError above).
 */
export function findConditionsCapabilityError(
  triggers: Triggers
): string | null {
  if (CONDITIONS_DEFAULT_ON) return null;
  for (const [key, config] of triggers) {
    if (config.conditionalLogic !== undefined) {
      return (
        `Trigger "${key}" uses conditionalLogic, but the conditions ` +
        `capability is not yet default-on for all users. Conditions cannot ` +
        `be created through the API until the feature is generally available.`
      );
    }
  }
  return null;
}

/**
 * Targets the Filter-by picker never offers, though each is legal elsewhere:
 * `wf:body` and `wf:viewport` need `targetContext: 'trigger'`,
 * `wf:any-element` returns `shouldShow: false` outright once
 * `filteringContext === 'filter'`, and `wf:popover-element` /
 * `wf:popover-backdrop` both require `filteringContext: 'base'`
 * (targets/index.ts `getTargetUiConfig`).
 * Kept separate from the always-hidden `wf:id` because these are context
 * rejections, not blanket ones.
 */
export const FILTER_BY_DISALLOWED_KEYS: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.BODY,
  IX3_WF_EXTENSION_KEYS.VIEWPORT,
  IX3_WF_EXTENSION_KEYS.ANY_ELEMENT,
  // Every entry, as in `LEAF_NO_FILTER_TARGET_KEYS`.
  ...COMPONENT_MANIFEST.flatMap(componentPartKeys),
]);

/**
 * Rejects target extensionKeys the Designer never offers as authoring choices.
 * `wf:id` has `shouldShow: false` in every target context (targets/index.ts);
 * the runtime resolver exists but the panel never writes it. Applied on both
 * trigger targets (findTriggerValueFlagError) and action targets
 * (findTimelineValueFlagError). On update: only runs when triggers/timelines
 * are replaced (field-gated at host), so legacy wf:id in untouched data is
 * safe (plan R3).
 *
 * `asFilterBy` additionally applies FILTER_BY_DISALLOWED_KEYS — the picker for
 * a nested filterBy offers a strictly narrower set than the base Target one.
 */
export function findDisallowedTargetKeyError(
  target: TargetTuple,
  where: string,
  options?: {asFilterBy?: boolean}
): string | null {
  if (target[0] === IX3_WF_EXTENSION_KEYS.ID) {
    return (
      `${where}: target type "${IX3_WF_EXTENSION_KEYS.ID}" is not offered ` +
      `by the Designer (shouldShow: false in all contexts). Use ` +
      `wf:selector, wf:class, or wf:inst instead.`
    );
  }
  if (
    options?.asFilterBy === true &&
    FILTER_BY_DISALLOWED_KEYS.has(target[0])
  ) {
    return (
      `${where}: target type "${target[0]}" is not offered by the ` +
      `Designer's Filter-by picker. Filter by a class, selector, attribute, ` +
      `element, or the trigger element instead.`
    );
  }
  return null;
}

/**
 * Action namespaces whose property values are plugin bags / custom editors —
 * not GSAP tween leaves. Skip transform/style leaf rules; use dedicated checks.
 */
const PRESET_ACTION_PROPERTY_NAMESPACES: ReadonlySet<string> = new Set([
  IX3_WF_EXTENSION_KEYS.CLASS,
  IX3_WF_EXTENSION_KEYS.LOTTIE,
  IX3_WF_EXTENSION_KEYS.SPLINE,
  IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW,
  IX3_WF_EXTENSION_KEYS.VARIABLE,
  IX3_WF_EXTENSION_KEYS.RIVE,
  IX3_WF_EXTENSION_KEYS.ANIMATE_RIVE,
]);

const WF_CLASS_OPERATIONS: ReadonlySet<string> = new Set([
  'addClass',
  'removeClass',
  'toggleClass',
]);

const SPLINE_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'positionX',
  'positionY',
  'positionZ',
  'rotationX',
  'rotationY',
  'rotationZ',
  'scaleX',
  'scaleY',
  'scaleZ',
  'intensity',
  'opacity',
  'zoom',
  'color',
  'stateName',
]);

type VariableValueType = NonNullable<VariableAction['value']>['type'];
const VARIABLE_VALUE_TYPE_KEYS: Record<VariableValueType, true> = {
  number: true,
  length: true,
  percentage: true,
  color: true,
  ref: true,
};
export const VARIABLE_VALUE_TYPES: ReadonlySet<string> = new Set(
  Object.keys(VARIABLE_VALUE_TYPE_KEYS)
);

const RIVE_PROPERTY_TYPE_KEYS: Record<RiveAddedProperty['propertyType'], true> =
  {
    string: true,
    number: true,
    boolean: true,
    color: true,
    enum: true,
    trigger: true,
    artboard: true,
  };
export const RIVE_PROPERTY_TYPES: ReadonlySet<string> = new Set(
  Object.keys(RIVE_PROPERTY_TYPE_KEYS)
);

const ANIMATE_RIVE_PROPERTY_TYPE_KEYS: Record<AnimateRivePropertyType, true> = {
  number: true,
  color: true,
};
export const ANIMATE_RIVE_PROPERTY_TYPES: ReadonlySet<string> = new Set(
  Object.keys(ANIMATE_RIVE_PROPERTY_TYPE_KEYS)
);

const MOUSE_FOLLOW_AXIS: ReadonlySet<string> = new Set(['x', 'y']);
const MOUSE_FOLLOW_LEAVE: ReadonlySet<string> = new Set(['return', 'stay']);
const MOUSE_FOLLOW_ON_ENTER: ReadonlySet<string> = new Set(['animate', 'snap']);
const MOUSE_FOLLOW_MODE: ReadonlySet<string> = new Set([
  'full',
  'x-only',
  'y-only',
]);

/** True for numbers or opaque variable-reference objects (grammar deferred). */
const isNumberOrVariableRef = (value: unknown): boolean =>
  typeof value === 'number' ||
  (typeof value === 'object' && value !== null && !Array.isArray(value));

/**
 * A structured origin axis is a CSS string or a real style-variable reference.
 * Mirrors `isVariableReference` in `@packages/systems/core/utils/StyleVariables`
 * — kept local because that barrel is a React module and this one also runs on
 * the multiplayer server. Any other object survives the publish transform's
 * `resolveOrigin` untouched and interpolates as "[object Object]".
 */
const isStringOrVariableRef = (value: unknown): boolean => {
  if (typeof value === 'string') return true;
  if (typeof value !== 'object' || value === null) return false;
  const ref = value as {type?: unknown; value?: unknown};
  if (ref.type !== 'ref') return false;
  return (
    typeof ref.value === 'object' &&
    ref.value !== null &&
    typeof (ref.value as {variableId?: unknown}).variableId === 'string'
  );
};

/**
 * Designer ClassOperationInput value: `null` (unset / reset) or
 * `{operation, selectors}`.
 */
export function validateWfClassPropertyValue(value: unknown): string | null {
  // Add-property / Reset / Set preset can persist unset Class as null; runtime
  // treats that as a no-op.
  if (value === null) {
    return null;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return (
      '"class" (wf:class) value must be null or an object {operation, selectors}; ' +
      'the Designer Class operation control always writes that shape.'
    );
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== 'operation' && key !== 'selectors') {
      return (
        `"class" (wf:class) does not support property "${key}"; supported ` +
        `keys: operation, selectors.`
      );
    }
  }
  if (!('operation' in record) || !('selectors' in record)) {
    return '"class" (wf:class) value must include both "operation" and "selectors".';
  }
  if (
    typeof record.operation !== 'string' ||
    !WF_CLASS_OPERATIONS.has(record.operation)
  ) {
    return (
      '"class" (wf:class) operation must be one of addClass, removeClass, ' +
      'toggleClass.'
    );
  }
  if (!Array.isArray(record.selectors)) {
    return '"class" (wf:class) selectors must be an array of style-block id strings.';
  }
  if (
    !record.selectors.every(
      (entry) => typeof entry === 'string' && entry.length > 0
    )
  ) {
    return '"class" (wf:class) selectors must be an array of non-empty style-block id strings.';
  }
  return null;
}

function validateLottieNested(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return '"lottie" (wf:lottie) value must be an object with from/to.';
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== 'from' && key !== 'to' && key !== 'manualDuration') {
      return (
        `"lottie" (wf:lottie) does not support property "${key}"; supported ` +
        `keys: from, to, manualDuration.`
      );
    }
  }
  if (!('from' in record) || !('to' in record)) {
    return '"lottie" (wf:lottie) value must include both "from" and "to".';
  }
  if (
    !isNumberOrVariableRef(record.from) ||
    !isNumberOrVariableRef(record.to)
  ) {
    return '"lottie" (wf:lottie) from/to must be numbers or variable references.';
  }
  // Frame picker persists manualDuration inside the nested bag (also accepted
  // as a top-level wf:lottie property).
  if (
    'manualDuration' in record &&
    typeof record.manualDuration !== 'boolean'
  ) {
    return '"manualDuration" (wf:lottie) must be a boolean.';
  }
  return null;
}

function validateSplineNested(
  value: unknown,
  options?: {allowLegacyScalars?: boolean}
): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return '"spline" (wf:spline) value must be an object of animatable channels.';
  }
  const record = value as Record<string, unknown>;
  for (const [key, channel] of Object.entries(record)) {
    if (!SPLINE_PROPERTY_KEYS.has(key)) {
      return (
        `"spline" (wf:spline) does not support channel "${key}"; supported ` +
        `channels: ${[...SPLINE_PROPERTY_KEYS].join(', ')}.`
      );
    }
    if (
      typeof channel !== 'object' ||
      channel === null ||
      Array.isArray(channel)
    ) {
      if (options?.allowLegacyScalars && isSplineLegacyScalar(channel)) {
        continue;
      }
      return `"spline.${key}" must be a {from?, to?} object.`;
    }
    const ch = channel as Record<string, unknown>;
    for (const field of Object.keys(ch)) {
      if (field !== 'from' && field !== 'to') {
        return `"spline.${key}" only supports from/to fields.`;
      }
    }
  }
  return null;
}

/**
 * Local mirror of `isVariableReference` for object leaves (no string branch).
 * Same shape as the object arm of `isStringOrVariableRef`.
 */
const isVariableRefObject = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return isStringOrVariableRef(value);
};

/**
 * Designer VariableAction value leaf (non-null). Mirrors StyleVariables
 * primitives + VariableReference that VariableActionEditor writes and that
 * `formatVariableValue` reads on publish.
 */
function validateVariableValueObject(
  valueRecord: Record<string, unknown>,
  where: string
): string | null {
  for (const key of Object.keys(valueRecord)) {
    if (key !== 'type' && key !== 'value') {
      return (
        `${where}.value does not support property "${key}"; supported keys: ` +
        `type, value.`
      );
    }
  }
  if (typeof valueRecord.type !== 'string') {
    return `${where}.value must include a string "type".`;
  }
  if (!VARIABLE_VALUE_TYPES.has(valueRecord.type)) {
    return (
      `${where}.value.type must be one of ` +
      `${[...VARIABLE_VALUE_TYPES].join(', ')}.`
    );
  }
  if (!('value' in valueRecord)) {
    return `${where}.value must include "value" for type "${valueRecord.type}".`;
  }

  const inner = valueRecord.value;
  switch (valueRecord.type) {
    case 'color': {
      if (typeof inner !== 'string') {
        return `${where}.value.value must be a color string.`;
      }
      return null;
    }
    case 'number': {
      if (typeof inner !== 'object' || inner === null || Array.isArray(inner)) {
        return `${where}.value.value must be {value: number, unit: "-"}.`;
      }
      const body = inner as Record<string, unknown>;
      for (const key of Object.keys(body)) {
        if (key !== 'value' && key !== 'unit') {
          return (
            `${where}.value.value does not support property "${key}"; ` +
            `supported keys: value, unit.`
          );
        }
      }
      if (typeof body.value !== 'number' || !Number.isFinite(body.value)) {
        return `${where}.value.value.value must be a finite number.`;
      }
      if (!('unit' in body) || body.unit !== '-') {
        return `${where}.value.value.unit must be "-".`;
      }
      return null;
    }
    case 'length': {
      if (typeof inner !== 'object' || inner === null || Array.isArray(inner)) {
        return `${where}.value.value must be {value: number, unit: string}.`;
      }
      const body = inner as Record<string, unknown>;
      for (const key of Object.keys(body)) {
        if (key !== 'value' && key !== 'unit') {
          return (
            `${where}.value.value does not support property "${key}"; ` +
            `supported keys: value, unit.`
          );
        }
      }
      if (typeof body.value !== 'number' || !Number.isFinite(body.value)) {
        return `${where}.value.value.value must be a finite number.`;
      }
      if (typeof body.unit !== 'string' || body.unit.length === 0) {
        return `${where}.value.value.unit must be a non-empty string.`;
      }
      return null;
    }
    case 'percentage': {
      if (typeof inner !== 'object' || inner === null || Array.isArray(inner)) {
        return `${where}.value.value must be {value: number, unit: "%"}.`;
      }
      const body = inner as Record<string, unknown>;
      for (const key of Object.keys(body)) {
        if (key !== 'value' && key !== 'unit') {
          return (
            `${where}.value.value does not support property "${key}"; ` +
            `supported keys: value, unit.`
          );
        }
      }
      if (typeof body.value !== 'number' || !Number.isFinite(body.value)) {
        return `${where}.value.value.value must be a finite number.`;
      }
      if (!('unit' in body) || body.unit !== '%') {
        return `${where}.value.value.unit must be "%".`;
      }
      return null;
    }
    case 'ref': {
      // Reuse the VariableReference object arm of isStringOrVariableRef, then
      // reject extra keys on the inner body the same way variables[] items do.
      if (!isVariableRefObject(valueRecord)) {
        return `${where}.value must be {type: "ref", value: {variableId: string}}.`;
      }
      const body = inner as Record<string, unknown>;
      for (const key of Object.keys(body)) {
        if (key !== 'variableId') {
          return (
            `${where}.value.value does not support property "${key}"; ` +
            `supported keys: variableId.`
          );
        }
      }
      return null;
    }
    default:
      return null;
  }
}

/**
 * Designer VariableActionEditor bag: `{variables: [{variableId, value}]}`.
 * `value` is null (unset placeholder) or a style primitive / variable ref.
 * Source: ix3-wf-plugins/shared VariableActionConfig + DES-7572 get_interaction.
 */
function validateVariableNested(value: unknown): string | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return '"variable" (wf:variable) value must be an object with variables.';
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== 'variables') {
      return (
        `"variable" (wf:variable) does not support property "${key}"; ` +
        `supported keys: variables.`
      );
    }
  }
  if (!('variables' in record)) {
    return '"variable" (wf:variable) value must include "variables".';
  }
  if (!Array.isArray(record.variables)) {
    return '"variable" (wf:variable) variables must be an array.';
  }
  const seenVariableIds = new Set<string>();
  for (let i = 0; i < record.variables.length; i++) {
    const entry = record.variables[i];
    const where = `"variable.variables[${i}]"`;
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
      return `${where} must be an object {variableId, value}.`;
    }
    const item = entry as Record<string, unknown>;
    for (const key of Object.keys(item)) {
      if (key !== 'variableId' && key !== 'value') {
        return (
          `${where} does not support property "${key}"; supported keys: ` +
          `variableId, value.`
        );
      }
    }
    if (typeof item.variableId !== 'string' || item.variableId.length === 0) {
      return `${where}.variableId must be a non-empty string.`;
    }
    if (seenVariableIds.has(item.variableId)) {
      return `${where}.variableId is already present in variables.`;
    }
    seenVariableIds.add(item.variableId);
    if (!('value' in item)) {
      return `${where} must include "value" (null when unset).`;
    }
    if (item.value === null) continue;
    if (
      typeof item.value !== 'object' ||
      item.value === null ||
      Array.isArray(item.value)
    ) {
      return (
        `${where}.value must be null or a style primitive / variable ` +
        `reference object.`
      );
    }
    const valueErr = validateVariableValueObject(
      item.value as Record<string, unknown>,
      where
    );
    if (valueErr) return valueErr;
  }
  return null;
}

/**
 * Shared rive bag checks for Set Rive (`wf:rive`) and Animate Rive
 * (`wf:animate-rive`). Both store under nested key `rive` with
 * `animationSource` + `addedProperties`. Property-type allowlists differ.
 * Source: ix3-wf-plugins/shared RiveActionConfig / AnimateRiveActionConfig.
 */
function validateRiveNested(
  value: unknown,
  options: {
    extensionKey: string;
    propertyTypes: ReadonlySet<string>;
    valueKinds: 'set' | 'animate';
  }
): string | null {
  const label = `"rive" (${options.extensionKey})`;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return `${label} value must be an object.`;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== 'animationSource' && key !== 'addedProperties') {
      return (
        `${label} does not support property "${key}"; supported keys: ` +
        `animationSource, addedProperties.`
      );
    }
  }

  if ('animationSource' in record) {
    const source = record.animationSource;
    if (source !== null && source !== undefined) {
      if (typeof source !== 'object' || Array.isArray(source)) {
        return `${label}.animationSource must be null or {name, instanceName?}.`;
      }
      const src = source as Record<string, unknown>;
      for (const key of Object.keys(src)) {
        if (key !== 'name' && key !== 'instanceName') {
          return (
            `${label}.animationSource does not support property "${key}"; ` +
            `supported keys: name, instanceName.`
          );
        }
      }
      if (typeof src.name !== 'string' || src.name.length === 0) {
        return `${label}.animationSource.name must be a non-empty string.`;
      }
      if (
        'instanceName' in src &&
        src.instanceName !== undefined &&
        typeof src.instanceName !== 'string'
      ) {
        return `${label}.animationSource.instanceName must be a string.`;
      }
    }
  }

  if ('addedProperties' in record && record.addedProperties !== undefined) {
    const added = record.addedProperties;
    if (typeof added !== 'object' || added === null || Array.isArray(added)) {
      return `${label}.addedProperties must be an object map.`;
    }
    if (Object.keys(added).length > 0) {
      const source = record.animationSource;
      if (
        source === null ||
        source === undefined ||
        typeof source !== 'object' ||
        Array.isArray(source)
      ) {
        return `${label} must include animationSource when addedProperties is not empty.`;
      }
    }
    for (const [propKey, prop] of Object.entries(added)) {
      const where = `${label}.addedProperties["${propKey}"]`;
      if (typeof prop !== 'object' || prop === null || Array.isArray(prop)) {
        return `${where} must be an object {propertyName, propertyType, value?}.`;
      }
      const item = prop as Record<string, unknown>;
      for (const key of Object.keys(item)) {
        if (
          key !== 'propertyName' &&
          key !== 'propertyType' &&
          key !== 'value'
        ) {
          return (
            `${where} does not support property "${key}"; supported keys: ` +
            `propertyName, propertyType, value.`
          );
        }
      }
      if (
        typeof item.propertyName !== 'string' ||
        item.propertyName.length === 0
      ) {
        return `${where}.propertyName must be a non-empty string.`;
      }
      if (!propKey.endsWith(`:${item.propertyName}`)) {
        return `${where} key must end with ":${item.propertyName}".`;
      }
      if (
        typeof item.propertyType !== 'string' ||
        !options.propertyTypes.has(item.propertyType)
      ) {
        return (
          `${where}.propertyType must be one of ` +
          `${[...options.propertyTypes].join(', ')}.`
        );
      }
      if (
        !('value' in item) ||
        item.value === null ||
        item.value === undefined
      ) {
        continue;
      }
      if (options.valueKinds === 'set') {
        const ok =
          typeof item.value === 'string' ||
          typeof item.value === 'number' ||
          typeof item.value === 'boolean' ||
          isVariableRefObject(item.value);
        if (!ok) {
          return (
            `${where}.value must be a string, number, boolean, null, or ` +
            `variable reference.`
          );
        }
      } else {
        const ok =
          typeof item.value === 'number' ||
          typeof item.value === 'string' ||
          isVariableRefObject(item.value);
        if (!ok) {
          return (
            `${where}.value must be a number, string, null, or variable ` +
            `reference.`
          );
        }
      }
    }
  }
  return null;
}

/**
 * `transformOrigin` and mouse-follow `anchor` share the Designer's
 * PositionInput: one value per property (a 9-box preset or a single Left/Top
 * pair), so the panel only ever writes a CSS "X% Y%" string or the structured
 * per-axis form. Both editors parse with `(value || '50% 50%').split(' ')`, so
 * every other shape — array, number, boolean, untyped object — renders as an
 * empty "Set value" row and throws when opened, taking the Designer down.
 */
function validatePositionOriginValue(
  value: unknown,
  propertyName: string,
  extensionKey: string
): string | null {
  const at = `"${propertyName}" (${extensionKey})`;
  if (typeof value === 'string') return null;
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return (
      `${at} must be a CSS "X% Y%" string or an ` +
      '{type: "ix3-origin", x, y} object.'
    );
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (key !== 'type' && key !== 'x' && key !== 'y') {
      return (
        `${at} does not support property "${key}"; ` +
        `supported keys: type, x, y.`
      );
    }
  }
  if (record.type !== 'ix3-origin') {
    return `${at} object form must use type "ix3-origin".`;
  }
  if (!isStringOrVariableRef(record.x) || !isStringOrVariableRef(record.y)) {
    return `${at} ix3-origin x/y must be strings or variable references.`;
  }
  return null;
}

/**
 * Nested / typed checks for preset + class bag property values after the
 * top-level allowlist passes.
 */
export function validatePresetPropertyValue(
  extensionKey: string,
  propertyName: string,
  value: unknown
): string | null {
  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.CLASS &&
    propertyName === 'class'
  ) {
    return validateWfClassPropertyValue(value);
  }

  if (extensionKey === IX3_WF_EXTENSION_KEYS.LOTTIE) {
    if (propertyName === 'manualDuration') {
      return typeof value === 'boolean'
        ? null
        : '"manualDuration" (wf:lottie) must be a boolean.';
    }
    if (propertyName === 'lottie') {
      return validateLottieNested(value);
    }
  }

  if (extensionKey === IX3_WF_EXTENSION_KEYS.SPLINE) {
    if (propertyName === 'objectId') {
      // SplineActionConfig.objectId is `string | undefined` (editor assigns
      // through). animatingState is coerced with Boolean(...) on every write.
      return value === undefined || typeof value === 'string'
        ? null
        : '"objectId" (wf:spline) must be a string.';
    }
    if (propertyName === 'animatingState') {
      return typeof value === 'boolean'
        ? null
        : '"animatingState" (wf:spline) must be a boolean.';
    }
    if (propertyName === 'spline') {
      return validateSplineNested(value);
    }
  }

  if (extensionKey === IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW) {
    if (propertyName === 'axis') {
      return typeof value === 'string' && MOUSE_FOLLOW_AXIS.has(value)
        ? null
        : '"axis" (wf:mouse-follow) must be "x" or "y".';
    }
    if (propertyName === 'leaveBehavior') {
      return typeof value === 'string' && MOUSE_FOLLOW_LEAVE.has(value)
        ? null
        : '"leaveBehavior" (wf:mouse-follow) must be "return" or "stay".';
    }
    if (propertyName === 'onEnter') {
      return typeof value === 'string' && MOUSE_FOLLOW_ON_ENTER.has(value)
        ? null
        : '"onEnter" (wf:mouse-follow) must be "animate" or "snap".';
    }
    if (propertyName === 'followMode') {
      return typeof value === 'string' && MOUSE_FOLLOW_MODE.has(value)
        ? null
        : '"followMode" (wf:mouse-follow) must be "full", "x-only", or "y-only".';
    }
    if (propertyName === 'groupId' || propertyName === 'syncedActionId') {
      return typeof value === 'string'
        ? null
        : `"${propertyName}" (wf:mouse-follow) must be a string.`;
    }
    if (propertyName === 'anchor') {
      return validatePositionOriginValue(
        value,
        propertyName,
        IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW
      );
    }
  }

  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.VARIABLE &&
    propertyName === 'variable'
  ) {
    return validateVariableNested(value);
  }

  if (extensionKey === IX3_WF_EXTENSION_KEYS.RIVE && propertyName === 'rive') {
    return validateRiveNested(value, {
      extensionKey,
      propertyTypes: RIVE_PROPERTY_TYPES,
      valueKinds: 'set',
    });
  }

  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.ANIMATE_RIVE &&
    propertyName === 'rive'
  ) {
    return validateRiveNested(value, {
      extensionKey,
      propertyTypes: ANIMATE_RIVE_PROPERTY_TYPES,
      valueKinds: 'animate',
    });
  }

  return null;
}

/**
 * Designer authoring constraints for one action property (allowlist, Set-only,
 * no plain {from,to}, random/additive). Checks each `[from, to]` leaf.
 */
export function validateActionPropertyShape(
  extensionKey: string,
  propertyName: string,
  value: unknown,
  tt?: number,
  options?: {
    allowLegacyNonAnimatable?: boolean;
    /** Pre-From/To Spline scalars (#95959) echoed from stored baseline. */
    allowLegacySplineFlatChannel?: boolean;
  }
): string | null {
  // wf:custom-property carries author-typed names, so it has no fixed
  // allowlist to check against. It still needs a POSITIVE gate: the skip below
  // would otherwise accept anything, including the discrete properties that
  // snap instead of animating. See customProperties.ts for the tier policy.
  if (extensionKey === IX3_WF_EXTENSION_KEYS.CUSTOM_PROPERTY) {
    const rejection = describeCustomPropertyRejection(propertyName);
    if (rejection) return rejection;
    for (const leaf of authoredPropertyLeaves(value)) {
      const leafError = validateActionPropertyLeaf(
        extensionKey,
        propertyName,
        leaf
      );
      if (leafError) return leafError;
    }
    return null;
  }

  const allowedForKey = SUPPORTED_ACTION_PROPERTIES[extensionKey];
  if (!allowedForKey) {
    // Unknown namespaces should already be rejected by findFlagGatedActionError.
    return null;
  }
  if (!allowedForKey.includes(propertyName)) {
    if (
      options?.allowLegacySplineFlatChannel &&
      extensionKey === IX3_WF_EXTENSION_KEYS.SPLINE &&
      isLegacySplineGrandfatherKey(propertyName, value)
    ) {
      return null;
    }
    // Suggest the correct namespace if the caller put an opacity/x/etc. under
    // wf:style when it belongs under wf:transform.
    const transformProps =
      SUPPORTED_ACTION_PROPERTIES[IX3_WF_EXTENSION_KEYS.TRANSFORM];
    const suggestion =
      extensionKey === IX3_WF_EXTENSION_KEYS.STYLE &&
      transformProps?.includes(propertyName)
        ? ` (it is a ${IX3_WF_EXTENSION_KEYS.TRANSFORM} property)`
        : '';
    return (
      `"${propertyName}" is not a supported ${extensionKey} property` +
      `${suggestion}. Supported properties: ${allowedForKey.join(', ')}.`
    );
  }

  const nonAnimatableSet = NON_ANIMATABLE_ACTION_PROPERTIES[extensionKey] as
    | ReadonlySet<string>
    | undefined;
  if (nonAnimatableSet?.has(propertyName)) {
    // Non-animatable properties are only valid inside a Set action (tt: 3);
    // the Designer always emits tt=3 for them (utils.ts convertActionToSet).
    // Fall through to leaf checks so Set-only props still reject wrappers /
    // plain {from,to} objects the panel never writes.
    // Legacy Class/Display-on-tt:0 mixed actions are grandfathered via
    // allowLegacyNonAnimatable, but only for the Class/Display pair — the
    // schema owns that eligibility so a caller boolean cannot exempt zIndex etc.
    const allowLegacy =
      options?.allowLegacyNonAnimatable === true &&
      isLegacyNonAnimatableGrandfatherEligible(extensionKey, propertyName);
    if (tt !== 3 && !allowLegacy) {
      return (
        `"${propertyName}" (${extensionKey}) is non-animatable and must only ` +
        `be used in a Set action (tt: 3); the Designer always emits tt=3 for ` +
        `this property.`
      );
    }
  }

  // Class / lottie / spline / mouse-follow bags are not GSAP tween leaves.
  if (PRESET_ACTION_PROPERTY_NAMESPACES.has(extensionKey)) {
    if (
      options?.allowLegacySplineFlatChannel &&
      extensionKey === IX3_WF_EXTENSION_KEYS.SPLINE &&
      propertyName === 'spline' &&
      isLegacyCompatibleSplineNestedBag(value)
    ) {
      return validateSplineNested(value, {allowLegacyScalars: true});
    }
    return validatePresetPropertyValue(extensionKey, propertyName, value);
  }

  for (const leaf of authoredPropertyLeaves(value)) {
    const leafError = validateActionPropertyLeaf(
      extensionKey,
      propertyName,
      leaf
    );
    if (leafError) return leafError;
  }

  // After the leaf checks, so a random/additive wrapper still gets its own
  // message. `authoredPropertyLeaves` drops empty slots, so a one-slot
  // `["50% 50%", null]` origin would otherwise validate its single string leaf
  // and pass — then crash the panel's Origin editor.
  if (
    extensionKey === IX3_WF_EXTENSION_KEYS.TRANSFORM &&
    propertyName === 'transformOrigin'
  ) {
    return validatePositionOriginValue(value, propertyName, extensionKey);
  }

  return null;
}

/** Leaves the Designer authors: each slot of `[from]`, `[from, to]`, or a bare value. */
function authoredPropertyLeaves(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value.filter((entry) => entry !== null && entry !== undefined);
  }
  return [value];
}

const IX3_VALUE_MODE_TYPES = new Set([
  'ix3-random',
  'ix3-additive',
  'ix3-random-array',
]);

/**
 * Whether a property accepts a random/additive wrapper. wf:custom-property
 * names are author-typed, so its policy is computed rather than listed.
 */
function supportsValueMode(
  extensionKey: string,
  propertyName: string,
  mode: 'random-array' | 'random-range' | 'additive',
  supportingByKey: Readonly<Record<string, ReadonlySet<string>>>
): boolean {
  if (extensionKey === IX3_WF_EXTENSION_KEYS.CUSTOM_PROPERTY) {
    return customPropertySupportsMode(mode, propertyName);
  }
  return (
    (supportingByKey[extensionKey] as ReadonlySet<string> | undefined)?.has(
      propertyName
    ) ?? false
  );
}

function validateActionPropertyLeaf(
  extensionKey: string,
  propertyName: string,
  leaf: unknown
): string | null {
  // Known discriminator that fails its structural guard — reject rather than
  // treating it as an opaque object (or letting it slip past {from,to} checks).
  if (
    typeof leaf === 'object' &&
    leaf !== null &&
    !Array.isArray(leaf) &&
    typeof (leaf as {type?: unknown}).type === 'string' &&
    IX3_VALUE_MODE_TYPES.has((leaf as {type: string}).type)
  ) {
    const type = (leaf as {type: string}).type;
    const wellFormed =
      (type === 'ix3-random' && isRandomValue(leaf)) ||
      (type === 'ix3-additive' && isAdditiveValue(leaf)) ||
      (type === 'ix3-random-array' && isRandomArrayValue(leaf));
    if (!wellFormed) {
      return `"${propertyName}" (${extensionKey}) has a malformed ${type} wrapper.`;
    }
  }

  // Reject plain {from, to} objects — these are not a valid animation value
  // shape (DES-7371). The Designer never stores plain objects with from/to keys;
  // callers must use arrays ([from], [from, to]) or random/additive wrappers.
  if (
    typeof leaf === 'object' &&
    leaf !== null &&
    !Array.isArray(leaf) &&
    !isRandomValue(leaf) &&
    !isAdditiveValue(leaf) &&
    !isRandomArrayValue(leaf) &&
    ('from' in leaf || 'to' in leaf)
  ) {
    return (
      `"${propertyName}" (${extensionKey}) value must not be a plain ` +
      `{from, to} object. Use an array: a scalar "to" value, [from], ` +
      `[from, to], or a random/additive wrapper.`
    );
  }

  // Random-array wrapper: check support and array length bounds.
  if (isRandomArrayValue(leaf)) {
    const supportsRandom = supportsValueMode(
      extensionKey,
      propertyName,
      'random-array',
      ACTION_PROPERTIES_SUPPORTING_RANDOM
    );
    if (!supportsRandom) {
      return `"${propertyName}" (${extensionKey}) does not support random values.`;
    }
    const len = leaf.values.length;
    if (len < MIN_RANDOM_SET_VALUES || len > MAX_RANDOM_SET_VALUES) {
      return (
        `"${propertyName}" (${extensionKey}) random-array must have between ` +
        `${MIN_RANDOM_SET_VALUES} and ${MAX_RANDOM_SET_VALUES} values; got ${len}.`
      );
    }
    return null;
  }

  // Random min/max wrapper: range mode only (colors are value-set only).
  if (isRandomValue(leaf)) {
    const supportsRange = supportsValueMode(
      extensionKey,
      propertyName,
      'random-range',
      ACTION_PROPERTIES_SUPPORTING_RANDOM_RANGE
    );
    if (!supportsRange) {
      return (
        `"${propertyName}" (${extensionKey}) does not support random min/max ` +
        `range values.`
      );
    }
    return null;
  }

  // Additive wrapper: check support.
  if (isAdditiveValue(leaf)) {
    const supportsAdditive = supportsValueMode(
      extensionKey,
      propertyName,
      'additive',
      ACTION_PROPERTIES_SUPPORTING_ADDITIVE
    );
    if (!supportsAdditive) {
      return `"${propertyName}" (${extensionKey}) does not support additive values.`;
    }
    return null;
  }

  return null;
}

/**
 * Scroll scrub animates the timeline by scroll position; the runtime ignores
 * `timing.repeat` and `timing.yoyo` in scrub mode, and the Designer clears
 * them when the scrub toggle is enabled (convertActionToSet clears them on Set
 * conversion). Reuses `hasScrollScrubTrigger` (same predicate as
 * findPercentTimelineError) per plan R7.
 */
export function findScrollScrubActionTimingError(
  triggers: Triggers,
  timelines: readonly Timeline[]
): string | null {
  if (!hasScrollScrubTrigger(triggers)) return null;
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      if (action.timing?.repeat !== undefined) {
        return (
          `timeline "${timeline.id}" action "${action.id}": timing.repeat ` +
          `cannot be set on a scroll-scrub interaction; the Designer clears ` +
          `it when scrub is enabled.`
        );
      }
      if (action.timing?.yoyo !== undefined) {
        return (
          `timeline "${timeline.id}" action "${action.id}": timing.yoyo ` +
          `cannot be set on a scroll-scrub interaction; the Designer clears ` +
          `it when scrub is enabled.`
        );
      }
    }
  }
  return null;
}

/** The milliseconds form the Start input writes when its unit toggle is "ms". */
const ABSOLUTE_MS_POSITION = /^\d+(\.\d+)?ms$/;

/**
 * `timing.position` is an absolute start time in the panel. IX3StartTimeInput
 * writes a seconds number or a `"Nms"` string depending on its unit toggle, the
 * timeline drag commits the same two forms via `formatDuration`, and Reset
 * clears it — there is no operator UI.
 *
 * The schema additionally accepts GSAP's alignment and relative operators
 * (`<`, `>`, `+=0.5`, `<+=1`, …), which the panel cannot author *or display*:
 * `getTimelineEditorInputValue('+=0.5')` returns a null value, so the Start
 * field renders empty and the next edit silently overwrites the operator with
 * an absolute time.
 */
export function findActionTimingPositionError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const position = action.timing?.position;
      // Zod accepts Infinity/NaN as numbers; the Start field only writes finite
      // seconds. Reject here so sync cannot serialize them as null for the backend.
      if (typeof position === 'number' && !Number.isFinite(position)) {
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `timing.position "${position}" is not a start time the Designer ` +
          `can author; use a number of seconds or a "500ms" string.`
        );
      }
      if (typeof position !== 'string') continue;
      if (!ABSOLUTE_MS_POSITION.test(position)) {
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `timing.position "${position}" is not a start time the Designer ` +
          `can author; use a number of seconds or a "500ms" string.`
        );
      }
    }
  }
  return null;
}

/**
 * The Mask dropdown is filtered to "None" plus the option matching the split
 * type (IX3SplitText.tsx), and choosing None omits `mask` rather than storing
 * a value — so the only object form the panel writes is `{type}` or
 * `{type, mask}` with the two equal. Changing the type drops `mask` entirely,
 * so a mismatched pair cannot survive an edit either.
 *
 * The legacy string form (`splitText: 'chars'`) is still read for migration,
 * but the panel no longer writes it, so DE/MCP writes must use the object form.
 *
 * `baselineTimelines` (update path only): the action editor mutates an immer
 * draft of the stored action, so an unrelated edit (duration, target) carries a
 * legacy string through untouched. Rejecting that would force a migration the
 * panel never asks for, so an unchanged baseline string is grandfathered; only
 * a new or altered string form rejects. A duplicate action id in the next
 * timeline is rejected before that exception, so one stored string cannot
 * authorize a second row with the same id.
 */
function actionIdIsDuplicated(
  actions: readonly TimelineAction[],
  actionId: string
): boolean {
  let seen = false;
  for (const action of actions) {
    if (action.id !== actionId) continue;
    if (seen) return true;
    seen = true;
  }
  return false;
}

export function findActionSplitTextError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const splitText = action.splitText;
      if (splitText === undefined) continue;
      if (typeof splitText === 'string') {
        // One stored string must not authorize a second row with the same
        // id. Check uniqueness before the grandfather so a duplicate cannot
        // look up the single baseline action and bypass the object-form guard.
        if (actionIdIsDuplicated(timeline.actions, action.id)) {
          return (
            `timeline "${timeline.id}" action "${action.id}": ` +
            `duplicate action id; a stored legacy string splitText cannot ` +
            `authorize a second row with the same id.`
          );
        }
        const baselineAction = findTimelineActionById(
          baselineTimelines,
          timeline.id,
          action.id
        );
        if (baselineAction?.splitText === splitText) continue;
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `splitText "${splitText}" must be an object {type} or {type, mask}; ` +
          `the Designer no longer writes the legacy string form.`
        );
      }
      if (typeof splitText !== 'object' || splitText === null) continue;
      const {type, mask} = splitText;
      if (mask !== undefined && mask !== type) {
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `splitText.mask "${mask}" must match splitText.type "${type}"; ` +
          `the Designer's Mask dropdown only offers None or "${type}".`
        );
      }
    }
  }
  return null;
}

/**
 * Slot vs `tt` contradictions the runtime drops silently. Exclusive
 * from-only predicates so mixed From + To-only with an explicit `tt: 0`
 * (no real `[from, to]`) is not rejected — to-only properties still
 * animate. From (`tt: 1`) still rejects any To-only slot because the
 * runtime only reads `config.from` and drops the To-only properties.
 * Matches WFS `validateTimelineSemantics` reject rules. WFS does not
 * copy the stored-row skip except omitted-tt mixed Display (#95644).
 */
function tweenSlotMismatchMessage(
  tt: TimelineAction['tt'],
  slots: TweenValueSlots
): string | null {
  // The Designer's Origin control writes one value per action (a 9-box preset
  // or a single Left/Top pair spanning both the From and To columns), and its
  // editor only reads a string / structured origin — an array renders as an
  // empty "Set value" and throws when opened. So a two-endpoint origin is
  // unauthorable regardless of tt; the runtime mirrors the single endpoint.
  if (slots.hasOriginPair) {
    return (
      `transformOrigin has two endpoints, which the Designer cannot author ` +
      `— its Origin control writes one value per action. Use a single ` +
      `origin (e.g. "50% 50%"), which the runtime applies to both ends of ` +
      `the tween.`
    );
  }
  if (slots.hasFromTo && slots.hasFromOnly) {
    return (
      `properties mix [from, to] and [from] / [from, null] shapes — ` +
      `From-only properties have no to-value for the runtime to animate.`
    );
  }
  if ((tt === 0 || tt === 1 || tt === 3) && slots.hasFromTo) {
    const dropped = tt === 1 ? 'to-values' : 'from-values';
    return (
      `properties use [from, to] arrays but "tt" is ${tt} — the ${dropped} ` +
      `would be ignored. Use "tt": 2 (fromTo), or omit tt to have it inferred ` +
      `from the value shapes.`
    );
  }
  if (tt === 1 && slots.hasToOnly) {
    return (
      `properties use [null, to] (To) arrays or scalar (To) values but ` +
      `"tt" is 1 (From) — there is no from-value for the runtime to animate. ` +
      `Use "tt": 0 for To, or provide [from] values.`
    );
  }
  // fromTo emits a From-only property into `config.from` with no matching
  // `to`, so GSAP sets the start value and holds it — frozen, not skipped.
  // Rejected even beside To-only siblings, which do still animate.
  if (tt === 2 && slots.hasFromOnly) {
    return (
      `properties use [from] / [from, null] (From) arrays but "tt" is 2 ` +
      `(fromTo) — those properties have no to-value, so the runtime sets the ` +
      `from-value and never animates them. Provide [from, to] values, or use ` +
      `"tt": 1 for From.`
    );
  }
  if (
    (tt === 0 || tt === 3) &&
    slots.hasFromOnly &&
    !slots.hasToOnly &&
    !slots.hasFromTo
  ) {
    return (
      `properties use [from] / [from, null] (From) arrays but "tt" is ${tt} ` +
      `— there is no to-value for the runtime to animate. Use "tt": 1 for From, ` +
      `or provide [null, to] / [from, to] values.`
    );
  }
  if (tt === undefined && slots.hasFromOnly && slots.hasToOnly) {
    return (
      `properties mix [from] and [null, to] shapes without an explicit "tt" ` +
      `— pick one tween form (From, To, or fromTo with [from, to]).`
    );
  }
  return null;
}

/**
 * An action's `tt` must agree with the From/To slots in transform/style
 * values. Runtime `buildTweensForAction` only reads the half `tt` names, so
 * `[from, to]` with To/From/Set drops one endpoint, and a From/To/Set with
 * no matching slot is skipped entirely.
 *
 * `baselineTimelines` (update path only): the Animation Type control writes
 * `draft.tt` without rewriting slots, so a stored panel-trap action can
 * round-trip on an unrelated edit. An unchanged `tt` + properties pair
 * (including both omitted) on the same timeline id + action id is
 * grandfathered; a new or altered pair still rejects. Duplicate action ids
 * cannot borrow. Origin pairs are not grandfathered: they crash the panel
 * and `validatePositionOriginValue` already rejects arrays with no skip.
 */
export function findActionTweenSlotError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const slots = analyzeTweenValueSlots(action.properties);
      if (
        !slots.hasFromOnly &&
        !slots.hasToOnly &&
        !slots.hasFromTo &&
        !slots.hasOriginPair
      ) {
        continue;
      }
      const mismatch = tweenSlotMismatchMessage(action.tt, slots);
      if (mismatch === null) {
        continue;
      }
      if (actionIdIsDuplicated(timeline.actions, action.id)) {
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `duplicate action id; a stored tween-slot mismatch cannot ` +
          `authorize a second row with the same id.`
        );
      }
      // Origin pairs crash the panel and are already rejected by
      // validatePositionOriginValue with no grandfather. Don't skip them
      // even when the stored payload is unchanged.
      if (
        !slots.hasOriginPair &&
        isUnchangedStoredAction(action, timeline, baselineTimelines)
      ) {
        continue;
      }
      return `timeline "${timeline.id}" action "${action.id}": ${mismatch}`;
    }
  }
  return null;
}

/**
 * `action.timing.delay` parses (timing schema merge) but is inert —
 * `buildTweensForAction` never passes it to GSAP. Delay belongs on the
 * trigger or on `timeline.settings`.
 *
 * Update path: grandfather an unchanged stored value (same as
 * findActionSplitTextError, including the duplicate-id check). Compare
 * after `secondsOrMsSchema` so `"400ms"` / `null` still match `0.4` / `0.25`.
 */
export function findActionDelayError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      const delay = action.timing?.delay;
      if (delay === undefined) continue;
      if (actionIdIsDuplicated(timeline.actions, action.id)) {
        return (
          `timeline "${timeline.id}" action "${action.id}": ` +
          `duplicate action id; a stored timing.delay cannot authorize a ` +
          `second row with the same id.`
        );
      }
      const baselineAction = findTimelineActionById(
        baselineTimelines,
        timeline.id,
        action.id
      );
      if (normalizedActionDelaysEqual(baselineAction?.timing?.delay, delay)) {
        continue;
      }
      return (
        `timeline "${timeline.id}" action "${action.id}": ` +
        `timing.delay is not applied to an action; the runtime ignores it. ` +
        `Use timing.position to sequence actions within a timeline, or set ` +
        `delay on the trigger or on the timeline's settings.`
      );
    }
  }
  return null;
}

function normalizedActionDelay(delay: unknown): unknown {
  const parsed = secondsOrMsSchema.safeParse(delay);
  return parsed.success ? parsed.data : delay;
}

function normalizedActionDelaysEqual(left: unknown, right: unknown): boolean {
  return normalizedActionDelay(left) === normalizedActionDelay(right);
}

function intervalFieldsEqual(
  a: Timeline['triggerMetadata'],
  b: Timeline['triggerMetadata']
): boolean {
  if (!a || !b) return false;
  return structurallyEqualJson(
    {role: a.role, distance: a.distance, axes: a.axes},
    {role: b.role, distance: b.distance, axes: b.axes}
  );
}

/**
 * `triggerMetadata.distance` and `axes` are interval mouse-move fields
 * (IntervalController). The panel only writes them on the interval timeline.
 *
 * Timeline-local, so the host runs it only when the timeline set is replaced —
 * no trigger change can turn a stored `mouseX` role into `interval`, and a
 * trigger-only update must leave legacy off-role metadata alone.
 *
 * `baselineTimelines` (update path only): a duration / target / similar RMW
 * echoes stored `triggerMetadata` the same way the splitText string form
 * does. An unchanged off-role `distance` / `axes` pair on the same timeline
 * id is grandfathered; a new or altered pair still rejects.
 */
export function findOffIntervalMetadataError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    const meta = timeline.triggerMetadata;
    if (!meta) continue;
    if (meta.distance === undefined && meta.axes === undefined) continue;
    if (isMouseMoveIntervalRole(meta.role)) continue;
    const baselineMeta = findTimelineById(
      baselineTimelines,
      timeline.id
    )?.triggerMetadata;
    if (
      baselineMeta &&
      !isMouseMoveIntervalRole(baselineMeta.role) &&
      intervalFieldsEqual(meta, baselineMeta)
    ) {
      continue;
    }
    return (
      `timeline "${timeline.id}": triggerMetadata.distance and axes are ` +
      `only authored on interval mouse-move timelines; the Designer does ` +
      `not write them on role "${meta.role ?? '(none)'}".`
    );
  }
  return null;
}

/**
 * The interval editor only exists on a mouse-move interaction, so interval
 * metadata without a `wf:mouse-move` trigger is unauthorable. Cross-field, so
 * the host runs it whenever triggers or timelines change — a trigger swap can
 * strand metadata the timeline still carries. Off-role timelines are left to
 * `findOffIntervalMetadataError`.
 *
 * Update path: already-stranded stored data (click + interval metadata)
 * must not fail — the write is not introducing the mismatch.
 * `IX3TimelinePlaybackSettingsPopover` gates the interval editor on
 * `isMouseMoveIntervalRole(role)` only, so a user can change distance /
 * axes on that stranded pair. Skip when the baseline triggers also lacked
 * mouse-move and the same timeline already carried an interval role with
 * `distance` or `axes`. A write that removes mouse-move, or that newly
 * attaches those fields to a non-mouse-move interaction, still rejects.
 */
export function findIntervalMetadataTriggerError(
  triggers: Triggers,
  timelines: readonly Timeline[],
  baselineTriggers?: Triggers,
  baselineTimelines?: readonly Timeline[]
): string | null {
  if (hasMouseMoveTrigger(triggers)) return null;
  const baselineLackedMouseMove =
    baselineTriggers !== undefined && !hasMouseMoveTrigger(baselineTriggers);
  for (const timeline of timelines) {
    const meta = timeline.triggerMetadata;
    if (!meta) continue;
    if (meta.distance === undefined && meta.axes === undefined) continue;
    if (!isMouseMoveIntervalRole(meta.role)) continue;
    if (baselineLackedMouseMove) {
      const baselineMeta = findTimelineById(
        baselineTimelines,
        timeline.id
      )?.triggerMetadata;
      if (
        baselineMeta &&
        isMouseMoveIntervalRole(baselineMeta.role) &&
        (baselineMeta.distance !== undefined || baselineMeta.axes !== undefined)
      ) {
        continue;
      }
    }
    return (
      `timeline "${timeline.id}": triggerMetadata.distance and axes are ` +
      `only authored on interval mouse-move timelines; the Designer does ` +
      `not write them without a "${IX3_WF_EXTENSION_KEYS.MOUSE_MOVE}" trigger.`
    );
  }
  return null;
}

/**
 * The Designer never dispatches `ix3InteractionTimelineDefaultsUpdated`.
 * Runtime AnimationCoordinator treats `timelineDefaults` as deprecated and
 * unimplemented. Create rejects any non-null bag. Set still allows `null` to
 * clear a stored value; omit leaves legacy bags.
 */
export function findTimelineDefaultsError(
  timelineDefaults: unknown
): string | null {
  if (timelineDefaults == null) return null;
  return (
    `timelineDefaults is not authored by the Designer; omit the field, ` +
    `or pass null on set to clear a stored value.`
  );
}

/**
 * Neither the action timing editor nor timeline settings UI writes
 * `autoReverse`. Runtime reverse uses `playInReverse`, not this field.
 *
 * `playInReverse` is an internal runtime field, not an authoring escape hatch:
 * it is in `DEAD_TIMELINE_SCALARS`, so `findDeadTimelineScalarError` rejects it
 * too. Do not name it as the fix here, or the caller lands in a second
 * rejection. The panel's actual back-and-forth control writes `repeat`/`yoyo`
 * (`IX3RepeatInput`'s "Back & Forth" toggle, and the playback settings gear).
 *
 * That `repeat`/`yoyo` advice fits the common time-based timeline and is
 * deliberately not mode-aware — this guard never receives triggers, so it
 * cannot tell a click from a gesture. Two later guards refuse the advice on
 * gesture-driven modes: `findScrollScrubActionTimingError` (both keys, on
 * scroll-scrub actions) and `findPercentCanvasSettingsError` (`settings.repeat`
 * only; `yoyo` is deliberately left when `canvasDuration != null`). Both were
 * considered and knowingly left alone — `autoReverse` has nothing to reverse on
 * a gesture-driven timeline, the "author the reverse as its own tween" clause
 * still applies there, and each of those guards explains itself. A mode-aware
 * message would need a signature change.
 *
 * `baselineTimelines` (update path only): the panel has no control that clears
 * `autoReverse` either, and a single-key edit like the Duration input mutates
 * an immer draft of the stored action, so the value rides along on an unrelated
 * save. `get()` returns it too, so rejecting it outright would strand any
 * interaction that already has one on its next read-modify-write. An unchanged
 * stored value is therefore allowed through; new or altered ones still reject.
 */
export function findTimingAutoReverseError(
  timelines: readonly Timeline[],
  baselineTimelines?: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    const autoReverse = timeline.settings?.autoReverse;
    if (
      autoReverse !== undefined &&
      findTimelineById(baselineTimelines, timeline.id)?.settings
        ?.autoReverse !== autoReverse
    ) {
      return (
        `timeline "${timeline.id}": settings.autoReverse is not authored ` +
        `by the Designer; omit the field. playInReverse is not a ` +
        `replacement; it is rejected too. For back-and-forth playback set ` +
        `settings.repeat and settings.yoyo.`
      );
    }
    for (const action of timeline.actions) {
      const actionAutoReverse = action.timing?.autoReverse;
      if (actionAutoReverse === undefined) continue;
      const baselineAction = findTimelineActionById(
        baselineTimelines,
        timeline.id,
        action.id
      );
      if (baselineAction?.timing?.autoReverse === actionAutoReverse) continue;
      return (
        `timeline "${timeline.id}" action "${action.id}": ` +
        `timing.autoReverse is not authored by the Designer; omit the ` +
        `field. playInReverse is not a replacement; it is rejected too. ` +
        `For back-and-forth playback set timing.repeat and timing.yoyo ` +
        `(the panel's "Back & Forth" control), or author the reverse as ` +
        `its own tween.`
      );
    }
  }
  return null;
}

/**
 * Designer playback slider bounds (`IX3TimelinePlaybackSettings` `{min: 0,
 * max: 10}`). Same range the trigger `speed` invariant uses. Timeline
 * settings Zod is `.nonnegative()`, so parse and this bound both accept `0`.
 */
const TIMELINE_SPEED_RANGE = {min: 0, max: 10} as const;

const UNAUTHORED_TIMELINE_SETTINGS_KEYS = [
  'duration',
  'ease',
  'repeatDelay',
  'stagger',
  'position',
] as const;

/**
 * Mirrors the native strip list in `clearTimelinePlaybackDefaults`. Only
 * `delay` and `repeat` can actually surface this message:
 * `findUnauthoredTimelineSettingsError` runs earlier in the
 * `findUnauthoredTimelineFieldError` chain and already rejects `repeatDelay`
 * and `stagger` unconditionally. They stay listed so the two lists read as
 * the same set the store strips — do not treat this branch as the only thing
 * catching them if that unconditional list is ever loosened.
 */
const PERCENT_CANVAS_STRIPPED_SETTINGS_KEYS = [
  'delay',
  'repeat',
  'repeatDelay',
  'stagger',
] as const;

const DEAD_TIMELINE_SCALARS = [
  'playInReverse',
  'previewSpeed',
  'immediate',
  'actionPresetIds',
] as const;

function presentSettingsValue(
  settings: Timeline['settings'] | undefined,
  key: string
): unknown {
  if (settings == null) return undefined;
  return (settings as Record<string, unknown>)[key];
}

function hasAnyPresentSettings(
  settings: Timeline['settings'] | undefined
): boolean {
  if (settings == null) return false;
  return Object.values(settings as Record<string, unknown>).some(
    (value) => value !== undefined
  );
}

/**
 * `IX3_TIMELINE_EDITED` can write `playInReverse` / `previewSpeed` /
 * `immediate`, but no Designer caller dispatches it (GSAP-86; playbar
 * speed is ephemeral GSAP). `actionPresetIds` has no non-test writer.
 */
function findDeadTimelineScalarError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    for (const field of DEAD_TIMELINE_SCALARS) {
      if (timeline[field] !== undefined) {
        return (
          `timeline "${timeline.id}": ${field} is not authored by the ` +
          `Designer; omit the field.`
        );
      }
    }
  }
  return null;
}

/**
 * Gear (`IX3TimelinePlaybackSettingsPopover`) writes `control` / `delay` /
 * `jump` / `speed` / `repeat` / `yoyo`. It does not write `duration` /
 * `ease` / `repeatDelay` / `stagger` / `position`. Speed is 0–10; Repeat
 * is `-1` or `>= 0`.
 */
function findUnauthoredTimelineSettingsError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    const settings = timeline.settings;
    for (const key of UNAUTHORED_TIMELINE_SETTINGS_KEYS) {
      if (presentSettingsValue(settings, key) !== undefined) {
        return (
          `timeline "${timeline.id}": settings.${key} is not authored ` +
          `by the Designer; omit the field.`
        );
      }
    }
    const speed = presentSettingsValue(settings, 'speed');
    if (speed !== undefined) {
      if (
        typeof speed !== 'number' ||
        !Number.isFinite(speed) ||
        speed < TIMELINE_SPEED_RANGE.min ||
        speed > TIMELINE_SPEED_RANGE.max
      ) {
        return (
          `timeline "${timeline.id}": settings.speed must be a finite ` +
          `number between ${TIMELINE_SPEED_RANGE.min} and ` +
          `${TIMELINE_SPEED_RANGE.max}.`
        );
      }
    }
    const repeat = presentSettingsValue(settings, 'repeat');
    if (repeat !== undefined) {
      if (
        typeof repeat !== 'number' ||
        !Number.isInteger(repeat) ||
        (repeat !== -1 && repeat < 0)
      ) {
        return (
          `timeline "${timeline.id}": settings.repeat must be -1 or an ` +
          `integer >= 0.`
        );
      }
    }
  }
  return null;
}

/**
 * Continuous Mouse X/Y and interval roles hide the playback gear
 * (`IX3TimelinePlaybackSettingsPopover` returns null). Role seed does not
 * write `settings`. Interval UI writes `axes` / `distance` only.
 */
function findContinuousTimelineSettingsError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    const role = timeline.triggerMetadata?.role;
    if (!isMouseMoveTimelineRole(role) && !isMouseMoveIntervalRole(role)) {
      continue;
    }
    if (hasAnyPresentSettings(timeline.settings)) {
      return (
        `timeline "${timeline.id}": settings are not authored on the ` +
        `"${role}" timeline role; the Designer playback gear is hidden ` +
        `for Mouse X/Y and interval roles.`
      );
    }
  }
  return null;
}

/**
 * `clearTimelinePlaybackDefaults` / `sanitizeTimelineData` strip `delay` /
 * `repeat` / `repeatDelay` / `stagger` when `canvasDuration != null`. They
 * leave `yoyo` (Repeat is visible on the percent canvas). Both shipped with
 * `canvasDuration` (#112861), and native save still strips, so leftover
 * keys are not a stored Designer corpus. No baseline skip.
 */
function findPercentCanvasSettingsError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    if (timeline.canvasDuration == null) continue;
    for (const key of PERCENT_CANVAS_STRIPPED_SETTINGS_KEYS) {
      if (presentSettingsValue(timeline.settings, key) !== undefined) {
        return (
          `timeline "${timeline.id}": settings.${key} is stripped on a ` +
          `percent-canvas timeline; omit delay, repeat, repeatDelay, ` +
          `and stagger when canvasDuration is set.`
        );
      }
    }
  }
  return null;
}

/**
 * The store writes `canvasDuration` as `PERCENT_CANVAS_DURATION_S`, and grows it
 * to the pre-edit canvas when an edit shrinks the action that defined it (the
 * `canvasDurationAnchor` on a bulk action edit). That anchor is capped at
 * `MAX_CANVAS_DURATION_S`, so every value the panel can store sits in
 * `[PERCENT_CANVAS_DURATION_S, MAX_CANVAS_DURATION_S]` and a DE/code-view round
 * trip of a Designer-produced timeline must keep passing. Anything outside that
 * range is a crafted payload.
 */
function findCanvasDurationValueError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    if (
      timeline.canvasDuration !== undefined &&
      !(
        timeline.canvasDuration >= PERCENT_CANVAS_DURATION_S &&
        timeline.canvasDuration <= MAX_CANVAS_DURATION_S
      )
    ) {
      return (
        `timeline "${timeline.id}": canvasDuration must be between ` +
        `${PERCENT_CANVAS_DURATION_S} (the Designer percent-canvas marker) ` +
        `and ${MAX_CANVAS_DURATION_S}.`
      );
    }
  }
  return null;
}

/**
 * Interval / mouse-move UI never writes `triggerMetadata.pluginConfig`.
 * Axes and distance are top-level `triggerMetadata` fields.
 */
function findTriggerMetadataPluginConfigError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    if (timeline.triggerMetadata?.pluginConfig !== undefined) {
      return (
        `timeline "${timeline.id}": triggerMetadata.pluginConfig is not ` +
        `authored by the Designer; omit the field.`
      );
    }
  }
  return null;
}

/**
 * `IX3EditableText` blocks empty names. Action ids are generated non-empty,
 * so a present id must be a non-empty string; an omitted id stays legal
 * (create assigns one).
 */
function findEmptyTimelineActionNameError(
  timelines: readonly Timeline[]
): string | null {
  for (const timeline of timelines) {
    if (
      timeline.name !== undefined &&
      (typeof timeline.name !== 'string' || timeline.name.trim() === '')
    ) {
      return `timeline "${timeline.id}": name must be a non-empty string.`;
    }
    for (const [index, action] of timeline.actions.entries()) {
      if (
        action.id !== undefined &&
        (typeof action.id !== 'string' || action.id.trim() === '')
      ) {
        return (
          `timeline "${timeline.id}" actions[${index}]: id must be ` +
          `a non-empty string.`
        );
      }
      if (typeof action.name !== 'string' || action.name.trim() === '') {
        return (
          `timeline "${timeline.id}" actions[${index}]: name must ` +
          `be a non-empty string.`
        );
      }
    }
  }
  return null;
}

/**
 * Timeline fields the Interactions panel cannot persist. First error wins.
 * No baseline compare — rejected values are not Designer-stored.
 */
export function findUnauthoredTimelineFieldError(
  timelines: readonly Timeline[]
): string | null {
  return (
    findDeadTimelineScalarError(timelines) ??
    findUnauthoredTimelineSettingsError(timelines) ??
    findContinuousTimelineSettingsError(timelines) ??
    findPercentCanvasSettingsError(timelines) ??
    findCanvasDurationValueError(timelines) ??
    findTriggerMetadataPluginConfigError(timelines) ??
    findEmptyTimelineActionNameError(timelines)
  );
}

/**
 * `IX3EditableText` blocks empty interaction names. MCP already `.min(1)`;
 * this closes the DE create / name-replacing update path.
 */
export function findEmptyInteractionNameError(
  interaction: Pick<Interaction, 'id' | 'name'>
): string | null {
  if (typeof interaction.name !== 'string' || interaction.name.trim() === '') {
    return `interaction "${interaction.id}": name must be a non-empty string.`;
  }
  return null;
}

/**
 * `libraryProfileId` is written by library import only; mongoose read
 * drops it. Panel "All" variants writes `undefined`, never `[]`.
 *
 * `baselineScope` (update / lane path): `importIx3FromElementPreset` stamps
 * the field on a native install, and `get` / Code View materialize return
 * the live scope. An unchanged echo must stay legal so a same-session
 * get-to-set or rename does not fail. A newly introduced or changed id
 * still rejects.
 */
export function findScopeAuthoringError(
  scope: Interaction['scope'] | undefined,
  baselineScope?: Interaction['scope']
): string | null {
  if (scope?.type !== 'component') return null;
  if (scope.libraryProfileId !== undefined) {
    const baselineId =
      baselineScope?.type === 'component'
        ? baselineScope.libraryProfileId
        : undefined;
    if (scope.libraryProfileId !== baselineId) {
      return (
        `scope.libraryProfileId is not authored by the Designer; omit ` +
        `the field.`
      );
    }
  }
  if (scope.variants !== undefined && scope.variants.length === 0) {
    return (
      `scope.variants must be omitted or a non-empty array; the ` +
      `Designer writes undefined for All variants.`
    );
  }
  return null;
}

export function findUnauthoredInteractionFieldError(
  interaction: Interaction,
  baseline?: Interaction
): string | null {
  return (
    findEmptyInteractionNameError(interaction) ??
    findScopeAuthoringError(interaction.scope, baseline?.scope)
  );
}

/**
 * Appends DEFAULT_FILTER_CONTEXT to a 2-tuple target so Scope resolves
 * correctly. Leaves 3-tuples unchanged (caller-provided filterContext is
 * preserved, plan R2). Other lengths are left for validators to reject.
 * Applied in host/ix3.ts after objectToTuple for authored payload targets.
 */
export function stampDefaultFilterContext(target: TargetTuple): TargetTuple {
  if ((target as unknown[]).length === 2) {
    return [...target, DEFAULT_FILTER_CONTEXT] as unknown as TargetTuple;
  }
  return target;
}

/** Stamps DEFAULT_FILTER_CONTEXT on every 2-tuple in a target list. */
export function stampTargetsFilterContext(
  targets: readonly TargetTuple[]
): TargetTuple[] {
  return targets.map(stampDefaultFilterContext);
}

/** Target + path label for error messages. */
export type LocatedTarget = {target: TargetTuple; where: string};

// Mirrors findAuthoredTargetError: primary target, then filterBy when related.
function collectTarget(
  out: LocatedTarget[],
  target: TargetTuple,
  where: string
): void {
  out.push({target, where});
  const filterContext = target[2];
  if (
    filterContext?.filterBy &&
    filterContext.relationship !== RELATIONSHIP_TYPES.NONE
  ) {
    collectTarget(out, filterContext.filterBy, `${where} filter`);
  }
}

/** Trigger targets + scroll config pin/endTrigger/scroller (incl. filterBy). */
export function collectTriggerTargets(triggers: Triggers): LocatedTarget[] {
  const out: LocatedTarget[] = [];
  for (const [key, config, target] of triggers) {
    if (target) collectTarget(out, target, `trigger "${key}" target`);
    const scroll = config.scrollTriggerConfig;
    if (!scroll) continue;
    // pin is boolean | TargetTuple; only the tuple is an element target.
    if (Array.isArray(scroll.pin)) {
      collectTarget(out, scroll.pin, `trigger "${key}" scroll pin target`);
    }
    if (scroll.endTrigger) {
      collectTarget(
        out,
        scroll.endTrigger,
        `trigger "${key}" scroll endTrigger target`
      );
    }
    if (scroll.scroller) {
      collectTarget(
        out,
        scroll.scroller,
        `trigger "${key}" scroll scroller target`
      );
    }
  }
  return out;
}

/** Action targets (incl. filterBy) with path labels. */
export function collectActionTargets(
  timelines: readonly Timeline[]
): LocatedTarget[] {
  const out: LocatedTarget[] = [];
  for (const timeline of timelines) {
    for (const action of timeline.actions) {
      for (const target of action.targets) {
        collectTarget(
          out,
          target,
          `timeline "${timeline.id}" action "${action.id}" target`
        );
      }
    }
  }
  return out;
}

/**
 * [componentId, elementId] for wf:inst, else null. Null on unreadable shapes —
 * shape is validated upstream, so existence only rejects positively resolved ids.
 * Scoped form [[componentId, elementId], scope] unwraps to the base pair.
 */
export function asInstIdPath(
  target: TargetTuple
): {componentId: string; elementId: string} | null {
  if (target[0] !== IX3_WF_EXTENSION_KEYS.INST) return null;
  const value = target[1];
  const base =
    Array.isArray(value) && value.length === 2 && Array.isArray(value[0])
      ? value[0]
      : value;
  if (
    Array.isArray(base) &&
    base.length === 2 &&
    typeof base[0] === 'string' &&
    base[0].length > 0 &&
    typeof base[1] === 'string' &&
    base[1].length > 0
  ) {
    return {componentId: base[0], elementId: base[1]};
  }
  return null;
}
